import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api';

// ── PasswordStrength (embutido) ───────────────────────────────────────────────
const criteriosSenha = [
  { label: 'Mínimo 8 caracteres', test: (s) => s.length >= 8 },
  { label: 'Letra maiúscula (A-Z)', test: (s) => /[A-Z]/.test(s) },
  { label: 'Letra minúscula (a-z)', test: (s) => /[a-z]/.test(s) },
  { label: 'Número (0-9)', test: (s) => /\d/.test(s) },
  { label: 'Caractere especial (!@#$...)', test: (s) => /[^a-zA-Z\d\s]/.test(s) },
];
const niveisSenha = [
  { cor: '#d4dde8' },
  { cor: '#e74c3c' },
  { cor: '#e67e22' },
  { cor: '#f1c40f' },
  { cor: '#2ecc71' },
  { cor: '#27ae60' },
];
function PasswordStrength({ senha = '' }) {
  const aprovados = useMemo(() => criteriosSenha.filter((c) => c.test(senha)).length, [senha]);
  const nivel = senha.length === 0 ? 0 : Math.max(1, aprovados);
  const cor = niveisSenha[nivel].cor;
  if (!senha) return null;
  return (
    <div style={{ marginTop: 8, marginBottom: 4 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {[1,2,3,4,5].map((i) => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= nivel ? cor : '#d4dde8', transition: 'background 0.3s' }} />
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {criteriosSenha.map((c) => {
          const ok = c.test(senha);
          return (
            <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: ok ? '#27ae60' : '#6b7a8d' }}>
              <span>{ok ? '✓' : '○'}</span>{c.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}


const cores = { navy: '#1A2233', navyD: '#253047', navyL: 'rgba(26,34,51,0.08)', gold: '#e8a020', green: '#27ae60', red: '#e74c3c', border: '#d4dde8', muted: '#6b7a8d', orange: '#D85A30' };

const todosPerfis = [
  { key: 'func', label: 'Funcionario', modulo: null },
  { key: 'fin',  label: 'Financeiro',  modulo: null },
  { key: 'ges',  label: 'Gestor',      modulo: null },
  { key: 'est',  label: 'Estoque',     modulo: null },
  { key: 'fec',  label: 'Fechamento',  modulo: null },
  { key: 'orc',  label: 'Orcamentos',  modulo: null },
  { key: 'age',  label: 'Agenda',      modulo: null },
  { key: 'rel',  label: 'Relatórios',  modulo: null },
  { key: 'tkt',  label: 'Tickets',     modulo: 'tkt' },
  { key: 'cht',  label: 'Chat',        modulo: 'cht' },
  { key: 'cof',  label: 'Cofre de Senhas', modulo: null },
  { key: 'ctr',  label: 'Contratos',       modulo: 'ctr'  },
  { key: 'mon',  label: 'Monitoramento',   modulo: 'mon'  }, // NOVO v42
];

const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: '24px 20px', width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 20 }}>{title}</div>
        {children}
      </div>
    </div>
  );
};

function SeletorPerfis({ perfis, onChange, modulosAtivos = [] }) {
  const toggle = (key) => {
    if (perfis.includes(key)) {
      if (perfis.length === 1) return;
      onChange(perfis.filter(p => p !== key));
    } else {
      onChange([...perfis, key]);
    }
  };
  // Só mostra perfis cujo módulo está ativo (ou não tem módulo associado)
  const perfisVisiveis = todosPerfis.filter(p => !p.modulo || modulosAtivos.includes(p.modulo));
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {perfisVisiveis.map(p => (
        <button key={p.key} onClick={() => toggle(p.key)} type="button" style={{
          padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          border: `2px solid ${perfis.includes(p.key) ? cores.orange : cores.border}`,
          background: perfis.includes(p.key) ? '#fff3eb' : 'white',
          color: perfis.includes(p.key) ? cores.orange : cores.muted,
        }}>
          {p.label}
          {p.modulo && <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.7 }}>✦</span>}
        </button>
      ))}
      {perfisVisiveis.some(p => p.modulo) && (
        <div style={{ width: '100%', fontSize: 11, color: '#94a3b8', marginTop: 2 }}>✦ Módulo adicional contratado</div>
      )}
    </div>
  );
}

function iconeEmoji(tipo) {
  const map = { bus: '🚌', car: '🚗', moto: '🏍️', food: '🍽️', box: '📦', other: '📎', carro: '🚗' };
  return map[tipo] || '📎';
}


// ── CONFIGURAÇÕES DO COFRE DE SENHAS ─────────────────────────────────────────
function ConfiguracaoCofre({ toast }) {
  const cores2 = { navy: '#1A2233', orange: '#D85A30', border: '#d4dde8', muted: '#6b7a8d', green: '#27ae60', red: '#e74c3c' };
  const inputStyle2 = { width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${cores2.border}`, fontSize: 14, boxSizing: 'border-box', outline: 'none' };
  const labelStyle2 = { fontSize: 12, fontWeight: 700, color: cores2.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase' };
  const btnPrimary2 = { padding: '10px 20px', background: cores2.orange, color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' };
  const cardStyle2 = { background: 'white', borderRadius: 16, border: '1px solid #d4dde8', padding: '20px 16px', marginBottom: 16 };

  const [abaLocal, setAbaLocal] = React.useState('geral');
  const [config, setConfig] = React.useState(null);
  const [loadingConfig, setLoadingConfig] = React.useState(true);
  const [formGeral, setFormGeral] = React.useState({ mfa_timeout_horas: '10' });
  const [formMaster, setFormMaster] = React.useState({ senha_atual: '', senha_nova: '', confirmar: '' });
  const [salvando, setSalvando] = React.useState(false);
  const [salvandoMaster, setSalvandoMaster] = React.useState(false);
  const [logs, setLogs] = React.useState([]);
  const [totalLogs, setTotalLogs] = React.useState(0);
  const [paginaLogs, setPaginaLogs] = React.useState(1);
  const [loadingLogs, setLoadingLogs] = React.useState(false);

  React.useEffect(() => { carregarConfig(); }, []);
  React.useEffect(() => { if (abaLocal === 'auditoria') carregarLogs(); }, [abaLocal, paginaLogs]);

  const carregarConfig = async () => {
    setLoadingConfig(true);
    try {
      const r = await api.get('/cofre/config');
      setConfig(r.data);
      setFormGeral({ mfa_timeout_horas: r.data.mfa_timeout_horas || '10' });
    } catch { toast('Erro ao carregar configurações do cofre.', 'error'); }
    setLoadingConfig(false);
  };

  const carregarLogs = async () => {
    setLoadingLogs(true);
    try {
      const r = await api.get(`/cofre/auditoria?pagina=${paginaLogs}&limite=15`);
      setLogs(r.data.logs);
      setTotalLogs(r.data.total);
    } catch {}
    setLoadingLogs(false);
  };

  const salvarGeral = async () => {
    setSalvando(true);
    try {
      await api.post('/cofre/config', formGeral);
      toast('✅ Configurações do cofre salvas!', 'success');
    } catch { toast('Erro ao salvar.', 'error'); }
    setSalvando(false);
  };

  const salvarSenhaMaster = async () => {
    if (formMaster.senha_nova !== formMaster.confirmar) { toast('As senhas não conferem.', 'error'); return; }
    if (formMaster.senha_nova.length < 8) { toast('Senha master deve ter pelo menos 8 caracteres.', 'error'); return; }
    setSalvandoMaster(true);
    try {
      await api.post('/cofre/config/senha-master', { senha_atual: formMaster.senha_atual, senha_nova: formMaster.senha_nova });
      toast('✅ Senha master salva!', 'success');
      setFormMaster({ senha_atual: '', senha_nova: '', confirmar: '' });
      carregarConfig();
    } catch (e) { toast(e.response?.data?.erro || 'Erro ao salvar senha master.', 'error'); }
    setSalvandoMaster(false);
  };

  const acoesLabels = {
    'visualizou': '👁 Visualizou', 'copiou': '📋 Copiou', 'exportou': '📤 Exportou',
    'criou': '✅ Criou', 'editou': '✏️ Editou', 'deletou': '🗑 Deletou',
    'mfa_autenticou': '🔐 MFA', 'exportacao_negada': '⛔ Export. negada',
    'alterou_senha_master': '🔑 Alterou master',
  };

  const totalPaginasLogs = Math.ceil(totalLogs / 15);
  const abasLocais = [
    { key: 'geral', label: '⚙️ Geral' },
    { key: 'senha_master', label: '🔑 Senha Master' },
    { key: 'auditoria', label: '📋 Auditoria LGPD' },
  ];

  return (
    <div>
      <div style={cardStyle2}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🔐 Cofre de Senhas</div>
        <div style={{ fontSize: 13, color: cores2.muted, marginBottom: 20 }}>Gerencie acesso, segurança e conformidade LGPD do Cofre de Senhas.</div>

        {/* Sub-abas */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `2px solid ${cores2.border}` }}>
          {abasLocais.map(a => (
            <button key={a.key} onClick={() => setAbaLocal(a.key)} style={{
              background: 'none', border: 'none', padding: '10px 16px', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
              color: abaLocal === a.key ? cores2.orange : cores2.muted,
              borderBottom: abaLocal === a.key ? `2px solid ${cores2.orange}` : '2px solid transparent',
              marginBottom: -2,
            }}>{a.label}</button>
          ))}
        </div>

        {loadingConfig ? <p style={{ color: cores2.muted }}>Carregando...</p> : (
          <>
            {/* ── Geral ── */}
            {abaLocal === 'geral' && (
              <div style={{ maxWidth: 460 }}>
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '12px 14px', marginBottom: 20, fontSize: 13, color: '#0c4a6e' }}>
                  📋 <strong>LGPD:</strong> Toda visualização, cópia e exportação é registrada automaticamente no log de auditoria (Art. 37 LGPD).
                </div>
                <label style={labelStyle2}>⏱ Duração da sessão MFA do Cofre</label>
                <p style={{ color: cores2.muted, fontSize: 12, margin: '-2px 0 10px' }}>Após autenticar, o usuário não precisa repetir pelo tempo definido.</p>
                <select value={formGeral.mfa_timeout_horas} onChange={e => setFormGeral(p => ({ ...p, mfa_timeout_horas: e.target.value }))} style={inputStyle2}>
                  <option value="1">1 hora</option>
                  <option value="4">4 horas</option>
                  <option value="8">8 horas</option>
                  <option value="10">10 horas (padrão)</option>
                  <option value="12">12 horas</option>
                  <option value="24">24 horas</option>
                </select>
                <button onClick={salvarGeral} disabled={salvando} style={{ ...btnPrimary2, marginTop: 20 }}>
                  {salvando ? 'Salvando...' : '💾 Salvar configurações'}
                </button>
              </div>
            )}

            {/* ── Senha Master ── */}
            {abaLocal === 'senha_master' && (
              <div style={{ maxWidth: 460 }}>
                <div style={{ background: '#fef3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#856404' }}>
                  🔑 A senha master é necessária para exportar senhas do cofre. Ela é armazenada em hash bcrypt — ninguém consegue ver o valor original.
                </div>
                {config?.senha_master_configurada
                  ? <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#166534' }}>✅ Senha master já configurada.</div>
                  : <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991b1b' }}>⚠️ Sem senha master, exportações não são possíveis.</div>
                }
                {config?.senha_master_configurada && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle2}>Senha master atual</label>
                    <input type="password" value={formMaster.senha_atual} onChange={e => setFormMaster(p => ({ ...p, senha_atual: e.target.value }))} style={inputStyle2} placeholder="Digite a senha atual" />
                  </div>
                )}
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle2}>Nova senha master *</label>
                  <input type="password" value={formMaster.senha_nova} onChange={e => setFormMaster(p => ({ ...p, senha_nova: e.target.value }))} style={inputStyle2} placeholder="Mínimo 8 caracteres" />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle2}>Confirmar nova senha master *</label>
                  <input type="password" value={formMaster.confirmar} onChange={e => setFormMaster(p => ({ ...p, confirmar: e.target.value }))}
                    style={{ ...inputStyle2, borderColor: formMaster.confirmar && formMaster.senha_nova !== formMaster.confirmar ? cores2.red : cores2.border }}
                    placeholder="Repita a nova senha master" />
                </div>
                <button onClick={salvarSenhaMaster} disabled={salvandoMaster} style={btnPrimary2}>
                  {salvandoMaster ? 'Salvando...' : '🔑 Salvar senha master'}
                </button>
              </div>
            )}

            {/* ── Auditoria LGPD ── */}
            {abaLocal === 'auditoria' && (
              <div>
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#0c4a6e' }}>
                  📋 <strong>Log de Auditoria LGPD</strong> — Registro imutável de todas as ações no cofre. Retenção recomendada: mínimo 2 anos.
                </div>
                {loadingLogs ? <p style={{ color: cores2.muted }}>Carregando logs...</p> : (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#f8f9fb', borderBottom: `2px solid ${cores2.border}` }}>
                            {['Data/Hora', 'Usuário', 'Ação', 'Descrição', 'IP'].map(h => (
                              <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: cores2.navy, fontWeight: 600 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {logs.length === 0
                            ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: cores2.muted }}>Nenhum registro encontrado.</td></tr>
                            : logs.map(l => (
                              <tr key={l.id} style={{ borderBottom: `1px solid #f0f0f0` }}>
                                <td style={{ padding: '10px 14px', fontSize: 13 }}>{new Date(l.criado_em).toLocaleString('pt-BR')}</td>
                                <td style={{ padding: '10px 14px', fontSize: 13 }}>{l.usuario_nome}</td>
                                <td style={{ padding: '10px 14px' }}>
                                  <span style={{
                                    background: (l.acao.includes('deletou') || l.acao.includes('negada')) ? '#fdecea' : '#f0fdf4',
                                    color: (l.acao.includes('deletou') || l.acao.includes('negada')) ? cores2.red : '#166534',
                                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                                  }}>{acoesLabels[l.acao] || l.acao}</span>
                                </td>
                                <td style={{ padding: '10px 14px', fontSize: 13 }}>{l.descricao_senha || '—'}</td>
                                <td style={{ padding: '10px 14px', fontSize: 11, fontFamily: 'monospace', color: cores2.muted }}>{l.ip || '—'}</td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    </div>
                    {totalPaginasLogs > 1 && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button onClick={() => setPaginaLogs(p => p - 1)} disabled={paginaLogs === 1}
                          style={{ padding: '6px 14px', border: `1px solid ${cores2.border}`, borderRadius: 8, background: 'white', cursor: 'pointer' }}>‹ Anterior</button>
                        <span style={{ fontSize: 13, color: cores2.muted }}>{paginaLogs} / {totalPaginasLogs}</span>
                        <button onClick={() => setPaginaLogs(p => p + 1)} disabled={paginaLogs === totalPaginasLogs}
                          style={{ padding: '6px 14px', border: `1px solid ${cores2.border}`, borderRadius: 8, background: 'white', cursor: 'pointer' }}>Próxima ›</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── ABA CONFIGURAÇÕES DE TICKETS ──────────────────────────────────────────────
function AbaTicketConfig({ toast, usuarios }) {
  const cardStyle = { background: 'white', borderRadius: 16, border: '1px solid #d4dde8', padding: '20px 16px', marginBottom: 16 };
  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #d4dde8', fontSize: 14, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: '#6b7a8d', display: 'block', marginBottom: 6, textTransform: 'uppercase' };
  const btnPrimary = { padding: '8px 18px', background: '#D85A30', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const btnSm = { padding: '5px 12px', background: '#D85A30', color: 'white', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' };
  const btnGhost = { padding: '8px 14px', background: 'white', color: '#6b7a8d', border: '1px solid #d4dde8', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
  const btnDanger = { padding: '4px 10px', background: '#fde8e8', color: '#e74c3c', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };
  const btnEdit = { padding: '4px 10px', background: '#eef2ff', color: '#2563eb', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };

  // ── Sub-aba ativa ──
  const [subAba, setSubAba] = React.useState('config');
  // config | email | checklist | dominios

  // ── Tipos ──
  const [tipos, setTipos] = React.useState([]);
  const [novoTipo, setNovoTipo] = React.useState('');
  const [editandoTipo, setEditandoTipo] = React.useState(null);

  // ── Categorias / Subcategorias ──
  const [categorias, setCategorias] = React.useState([]);
  const [novaCategoria, setNovaCategoria] = React.useState('');
  const [editandoCategoria, setEditandoCategoria] = React.useState(null);
  const [catExpandida, setCatExpandida] = React.useState(null);
  const [novaSubcat, setNovaSubcat] = React.useState('');
  const [editandoSubcat, setEditandoSubcat] = React.useState(null);

  // ── Mesas ──
  const [mesas, setMesas] = React.useState([]);
  const [modalMesa, setModalMesa] = React.useState(false);
  const [mesaForm, setMesaForm] = React.useState({ nome: '', membros_ids: [] });
  const [editandoMesa, setEditandoMesa] = React.useState(null);

  // ── Checklists ──
  const [checklists, setChecklists]               = React.useState([]);
  const [checklistAba, setChecklistAba]           = React.useState('lista'); // 'lista' | 'form'
  const [checklistEditando, setChecklistEditando] = React.useState(null);
  const [checklistForm, setChecklistForm]         = React.useState({ nome: '', descricao: '', itens: [{ descricao: '', tipo: 'checkbox', opcoes: [''] }], subcategorias: [] });
  const [salvandoChecklist, setSalvandoChecklist] = React.useState(false);

  // ── Número inicial de ticket ──
  const [tktNumeroInicial, setTktNumeroInicial] = React.useState('');
  const [tktNumeroCurrent, setTktNumeroCurrent] = React.useState('');
  const [salvandoNumeroTkt, setSalvandoNumeroTkt] = React.useState(false);

  // ── Email de entrada (IMAP) ──
  const [tktEmailEntrada, setTktEmailEntrada]   = React.useState('');
  const [tktEmailSenha, setTktEmailSenha]       = React.useState('');
  const [tktEmailSenhaVis, setTktEmailSenhaVis] = React.useState(false);
  const [tktEmailHost, setTktEmailHost]         = React.useState('');
  const [tktEmailPorta, setTktEmailPorta]       = React.useState('993');
  const [tktEmailAtivo, setTktEmailAtivo]       = React.useState(false);
  const [tktEmailFila, setTktEmailFila]         = React.useState('suporte');
  const [salvandoEmailTkt, setSalvandoEmailTkt] = React.useState(false);
  const [testandoImap, setTestando]             = React.useState(false);
  const [resultadoImap, setResultadoImap]       = React.useState(null);

  // ── Domínios ──
  const [tktDominios, setTktDominios]           = React.useState([]);
  const [tktNovoDominio, setTktNovoDominio]     = React.useState('');
  const [salvandoDominios, setSalvandoDominios] = React.useState(false);

  // ── Personalização: rótulos customizados ──
  const [labelsCustom, setLabelsCustom]         = React.useState({}); // { chave: label } vindo do backend
  const [labelsForm, setLabelsForm]             = React.useState({}); // valores em edição no formulário
  const [salvandoLabels, setSalvandoLabels]     = React.useState(false);

  // ── Personalização: campos customizados ──
  const [camposCustom, setCamposCustom]         = React.useState([]);
  const [campoForm, setCampoForm]               = React.useState({ nome_interno: '', label: '', tipo: 'texto', opcoes: [''], obrigatorio: false });
  const [editandoCampo, setEditandoCampo]       = React.useState(null); // id do campo em edição, ou null = criando
  const [salvandoCampo, setSalvandoCampo]       = React.useState(false);

  // ── Personalização: assinatura digital ──
  const [assinaturaAtiva, setAssinaturaAtiva]         = React.useState(false);
  const [salvandoAssinaturaToggle, setSalvandoAssinaturaToggle] = React.useState(false);

  const CAMPOS_PADRAO_OS = [
    { chave: 'cliente_nome',     padrao: 'Nome (Solicitante)' },
    { chave: 'cliente_telefone', padrao: 'Telefone' },
    { chave: 'cliente_email',    padrao: 'E-mail' },
    { chave: 'titulo',           padrao: 'Assunto' },
    { chave: 'prioridade',       padrao: 'Prioridade' },
    { chave: 'fila',             padrao: 'Fila' },
    { chave: 'origem',           padrao: 'Origem' },
    { chave: 'atendente_nome',   padrao: 'Técnico' },
  ];

  React.useEffect(() => {
    carregarTipos(); carregarCategorias(); carregarMesas();
    carregarNumeroInicialTkt(); carregarEmailTkt(); carregarChecklists();
    carregarLabels(); carregarCamposCustom(); carregarAssinaturaAtiva();
  }, []);

  // ── Número inicial ──
  const carregarNumeroInicialTkt = async () => {
    try {
      const r = await api.get('/configuracoes');
      const conf = r.data.find(c => c.chave === 'tkt_numero_inicial');
      if (conf) { setTktNumeroInicial(conf.valor); setTktNumeroCurrent(conf.valor); }
    } catch {}
  };
  const salvarNumeroInicialTkt = async () => {
    const num = parseInt(tktNumeroInicial);
    if (!num || num < 1) return toast('Informe um número válido (mínimo 1)', 'error');
    setSalvandoNumeroTkt(true);
    try {
      await api.patch('/configuracoes/tkt_numero_inicial', { valor: String(num) });
      setTktNumeroCurrent(String(num));
      toast('Número inicial salvo! Válido para novos tickets.', 'success');
    } catch { toast('Erro ao salvar número inicial', 'error'); }
    finally { setSalvandoNumeroTkt(false); }
  };

  // ── Email IMAP ──
  const carregarEmailTkt = async () => {
    try {
      const r = await api.get('/configuracoes');
      const conf = {};
      (r.data || []).forEach(c => { conf[c.chave] = c.valor; });
      if (conf.tkt_imap_email)  setTktEmailEntrada(conf.tkt_imap_email);
      if (conf.tkt_imap_senha)  setTktEmailSenha(conf.tkt_imap_senha);
      if (conf.tkt_imap_host)   setTktEmailHost(conf.tkt_imap_host);
      if (conf.tkt_imap_porta)  setTktEmailPorta(conf.tkt_imap_porta);
      if (conf.tkt_imap_fila)   setTktEmailFila(conf.tkt_imap_fila);
      if (conf.tkt_imap_ativo)  setTktEmailAtivo(conf.tkt_imap_ativo === 'true');
      try {
        const dominios = JSON.parse(conf.tkt_imap_dominios || '[]');
        if (Array.isArray(dominios)) setTktDominios(dominios);
      } catch {}
    } catch {}
  };
  const salvarEmailTkt = async () => {
    if (!tktEmailEntrada || !tktEmailSenha || !tktEmailHost) {
      toast('Preencha ao menos email, senha e host IMAP.', 'error'); return;
    }
    setSalvandoEmailTkt(true);
    try {
      await Promise.all([
        api.patch('/configuracoes/tkt_imap_email', { valor: tktEmailEntrada }),
        api.patch('/configuracoes/tkt_imap_senha', { valor: tktEmailSenha }),
        api.patch('/configuracoes/tkt_imap_host',  { valor: tktEmailHost }),
        api.patch('/configuracoes/tkt_imap_porta', { valor: tktEmailPorta }),
        api.patch('/configuracoes/tkt_imap_fila',  { valor: tktEmailFila }),
        api.patch('/configuracoes/tkt_imap_ativo', { valor: String(tktEmailAtivo) }),
      ]);
      toast('Configuração de email salva!', 'success');
    } catch { toast('Erro ao salvar configuração de email.', 'error'); }
    finally { setSalvandoEmailTkt(false); }
  };

  // ── Domínios ──
  const adicionarDominio = () => {
    const d = tktNovoDominio.trim().toLowerCase().replace(/^@/, '').replace(/^https?:\/\//,'');
    if (!d || tktDominios.includes(d)) { setTktNovoDominio(''); return; }
    setTktDominios(prev => [...prev, d]);
    setTktNovoDominio('');
  };
  const removerDominio = (dominio) => { setTktDominios(prev => prev.filter(d => d !== dominio)); };
  const salvarDominios = async () => {
    setSalvandoDominios(true);
    try {
      await api.patch('/configuracoes/tkt_imap_dominios', { valor: JSON.stringify(tktDominios) });
      toast(tktDominios.length === 0 ? 'Filtro removido — qualquer email pode abrir ticket.' : `${tktDominios.length} domínio(s) salvo(s)!`, 'success');
    } catch { toast('Erro ao salvar domínios.', 'error'); }
    finally { setSalvandoDominios(false); }
  };

  // ── Personalização: rótulos customizados ──
  const carregarLabels = async () => {
    try {
      const r = await api.get('/tickets/labels');
      setLabelsCustom(r.data || {});
      setLabelsForm(r.data || {});
    } catch {}
  };
  const salvarLabels = async () => {
    setSalvandoLabels(true);
    try {
      await api.put('/tickets/labels', { labels: labelsForm });
      setLabelsCustom(labelsForm);
      toast('Rótulos salvos! Já valem para o formulário e para a impressão da OS.', 'success');
    } catch { toast('Erro ao salvar rótulos.', 'error'); }
    finally { setSalvandoLabels(false); }
  };

  // ── Personalização: campos customizados ──
  const carregarCamposCustom = async () => {
    try { const r = await api.get('/tickets/campos-customizados'); setCamposCustom(r.data || []); } catch {}
  };
  const novoCampoForm = () => setCampoForm({ nome_interno: '', label: '', tipo: 'texto', opcoes: [''], obrigatorio: false });
  const editarCampoCustom = (c) => {
    setCampoForm({
      nome_interno: c.nome_interno, label: c.label, tipo: c.tipo || 'texto',
      opcoes: c.opcoes?.length ? c.opcoes : [''], obrigatorio: !!c.obrigatorio,
    });
    setEditandoCampo(c.id);
  };
  const salvarCampoCustom = async () => {
    if (!campoForm.label.trim()) { toast('Informe o rótulo do campo.', 'error'); return; }
    if (!editandoCampo && !campoForm.nome_interno.trim()) { toast('Informe o identificador interno do campo.', 'error'); return; }
    setSalvandoCampo(true);
    try {
      const payload = {
        label: campoForm.label.trim(),
        tipo: campoForm.tipo,
        opcoes: campoForm.tipo === 'select' ? campoForm.opcoes.filter(o => o?.trim()) : undefined,
        obrigatorio: campoForm.obrigatorio,
      };
      if (editandoCampo) {
        await api.patch(`/tickets/campos-customizados/${editandoCampo}`, payload);
        toast('Campo atualizado!', 'success');
      } else {
        await api.post('/tickets/campos-customizados', { ...payload, nome_interno: campoForm.nome_interno.trim() });
        toast('Campo criado!', 'success');
      }
      novoCampoForm();
      setEditandoCampo(null);
      carregarCamposCustom();
    } catch (e) { toast(e.response?.data?.erro || 'Erro ao salvar campo.', 'error'); }
    finally { setSalvandoCampo(false); }
  };
  const removerCampoCustom = async (id) => {
    if (!window.confirm('Remover este campo? Valores já preenchidos em OS antigas não são apagados, só deixam de aparecer.')) return;
    try { await api.delete(`/tickets/campos-customizados/${id}`); carregarCamposCustom(); toast('Removido.', 'success'); }
    catch { toast('Erro ao remover.', 'error'); }
  };
  const campoAddOpcao = () => setCampoForm(p => ({ ...p, opcoes: [...p.opcoes, ''] }));
  const campoSetOpcao = (idx, val) => setCampoForm(p => { const n = [...p.opcoes]; n[idx] = val; return { ...p, opcoes: n }; });
  const campoRemOpcao = (idx) => setCampoForm(p => ({ ...p, opcoes: p.opcoes.filter((_, i) => i !== idx) }));

  // ── Personalização: assinatura digital ──
  const carregarAssinaturaAtiva = async () => {
    try {
      const r = await api.get('/configuracoes');
      const conf = r.data.find(c => c.chave === 'tkt_assinatura_ativa');
      setAssinaturaAtiva(conf?.valor === 'true');
    } catch {}
  };
  const toggleAssinaturaAtiva = async () => {
    const novoValor = !assinaturaAtiva;
    setSalvandoAssinaturaToggle(true);
    try {
      await api.patch('/configuracoes/tkt_assinatura_ativa', { valor: String(novoValor) });
      setAssinaturaAtiva(novoValor);
      toast(novoValor ? 'Assinatura digital ativada!' : 'Assinatura digital desativada.', 'success');
    } catch { toast('Erro ao atualizar configuração.', 'error'); }
    finally { setSalvandoAssinaturaToggle(false); }
  };

  // ── Tipos ──
  const carregarTipos = async () => {
    try { const r = await api.get('/tickets/tipos'); setTipos(r.data); } catch {}
  };
  const salvarTipo = async () => {
    if (!novoTipo.trim()) return;
    try { await api.post('/tickets/tipos', { nome: novoTipo.trim() }); setNovoTipo(''); carregarTipos(); toast('Tipo criado!', 'success'); }
    catch (e) { toast(e.response?.data?.erro || 'Erro ao criar tipo', 'error'); }
  };
  const editarTipo = async (id) => {
    if (!editandoTipo?.nome?.trim()) return;
    try { await api.patch(`/tickets/tipos/${id}`, { nome: editandoTipo.nome }); setEditandoTipo(null); carregarTipos(); toast('Tipo atualizado!', 'success'); }
    catch { toast('Erro ao atualizar', 'error'); }
  };
  const removerTipo = async (id) => {
    if (!window.confirm('Remover este tipo?')) return;
    try { await api.delete(`/tickets/tipos/${id}`); carregarTipos(); toast('Removido', 'success'); }
    catch { toast('Erro ao remover', 'error'); }
  };

  // ── Categorias ──
  const carregarCategorias = async () => {
    try { const r = await api.get('/tickets/categorias'); setCategorias(r.data); } catch {}
  };
  const salvarCategoria = async () => {
    if (!novaCategoria.trim()) return;
    try { await api.post('/tickets/categorias', { nome: novaCategoria.trim() }); setNovaCategoria(''); carregarCategorias(); toast('Categoria criada!', 'success'); }
    catch (e) { toast(e.response?.data?.erro || 'Erro ao criar categoria', 'error'); }
  };
  const editarCategoria = async (id) => {
    if (!editandoCategoria?.nome?.trim()) return;
    try { await api.patch(`/tickets/categorias/${id}`, { nome: editandoCategoria.nome }); setEditandoCategoria(null); carregarCategorias(); toast('Categoria atualizada!', 'success'); }
    catch { toast('Erro ao atualizar', 'error'); }
  };
  const removerCategoria = async (id) => {
    if (!window.confirm('Remover esta categoria e suas subcategorias?')) return;
    try { await api.delete(`/tickets/categorias/${id}`); if (catExpandida === id) setCatExpandida(null); carregarCategorias(); toast('Removida', 'success'); }
    catch { toast('Erro ao remover', 'error'); }
  };

  // ── Subcategorias ──
  const salvarSubcat = async (categoriaId) => {
    if (!novaSubcat.trim()) return;
    try {
      await api.post(`/tickets/categorias/${categoriaId}/subcategorias`, { nome: novaSubcat.trim() });
      setNovaSubcat(''); carregarCategorias(); toast('Subcategoria criada!', 'success');
    } catch (e) { toast(e.response?.data?.erro || 'Erro ao criar subcategoria', 'error'); }
  };
  const editarSubcat = async (id) => {
    if (!editandoSubcat?.nome?.trim()) return;
    try { await api.patch(`/tickets/subcategorias/${id}`, { nome: editandoSubcat.nome }); setEditandoSubcat(null); carregarCategorias(); toast('Subcategoria atualizada!', 'success'); }
    catch { toast('Erro ao atualizar', 'error'); }
  };
  const removerSubcat = async (id) => {
    if (!window.confirm('Remover esta subcategoria?')) return;
    try { await api.delete(`/tickets/subcategorias/${id}`); carregarCategorias(); toast('Removida', 'success'); }
    catch { toast('Erro ao remover', 'error'); }
  };

  // ── Checklists ──
  const carregarChecklists = async () => {
    try { const r = await api.get('/tickets/checklists'); setChecklists(r.data || []); } catch {}
  };
  const salvarChecklist = async () => {
    if (!checklistForm.nome.trim()) { toast('Nome do checklist é obrigatório.', 'error'); return; }
    const itensValidos = checklistForm.itens.filter(i => i.descricao?.trim()).map(i => ({
      ...i,
      tipo: i.tipo || 'checkbox',
      opcoes: (i.tipo === 'multipla') ? (i.opcoes || []).filter(o => o?.trim()) : undefined,
    }));
    if (itensValidos.length === 0) { toast('Adicione pelo menos um item ao checklist.', 'error'); return; }
    setSalvandoChecklist(true);
    try {
      const payload = { ...checklistForm, itens: itensValidos };
      if (checklistEditando) {
        await api.put(`/tickets/checklists/${checklistEditando}`, payload);
        toast('Checklist atualizado!', 'success');
      } else {
        await api.post('/tickets/checklists', payload);
        toast('Checklist criado!', 'success');
      }
      setChecklistAba('lista');
      setChecklistEditando(null);
      setChecklistForm({ nome: '', descricao: '', itens: [{ descricao: '' }], subcategorias: [] });
      carregarChecklists();
    } catch (e) { toast(e.response?.data?.erro || 'Erro ao salvar checklist.', 'error'); }
    finally { setSalvandoChecklist(false); }
  };
  const excluirChecklist = async (id) => {
    if (!window.confirm('Excluir este checklist?')) return;
    try { await api.delete(`/tickets/checklists/${id}`); carregarChecklists(); toast('Removido.', 'success'); }
    catch { toast('Erro ao excluir.', 'error'); }
  };
  const editarChecklist = (cl) => {
    setChecklistForm({
      nome: cl.nome,
      descricao: cl.descricao || '',
      itens: cl.itens?.length
        ? cl.itens.map(i => ({
            descricao: i.descricao,
            tipo: i.tipo || 'checkbox',
            opcoes: i.opcoes ? (typeof i.opcoes === 'string' ? JSON.parse(i.opcoes) : i.opcoes) : [''],
          }))
        : [{ descricao: '', tipo: 'checkbox', opcoes: [''] }],
      subcategorias: cl.subcategorias?.map(s => s.subcategoria_id) || [],
    });
    setChecklistEditando(cl.id);
    setChecklistAba('form');
  };
  const checklistSetItem     = (idx, field, val) => { const n = [...checklistForm.itens]; n[idx] = { ...n[idx], [field]: val }; setChecklistForm(p => ({ ...p, itens: n })); };
  const checklistSetTipo     = (idx, tipo) => { const n = [...checklistForm.itens]; n[idx] = { ...n[idx], tipo, opcoes: tipo === 'multipla' ? (n[idx].opcoes?.length ? n[idx].opcoes : ['','']) : n[idx].opcoes }; setChecklistForm(p => ({ ...p, itens: n })); };
  const checklistAddOpcao    = (idx) => { const n = [...checklistForm.itens]; n[idx] = { ...n[idx], opcoes: [...(n[idx].opcoes || []), ''] }; setChecklistForm(p => ({ ...p, itens: n })); };
  const checklistSetOpcao    = (idx, oidx, val) => { const n = [...checklistForm.itens]; const ops = [...(n[idx].opcoes || [])]; ops[oidx] = val; n[idx] = { ...n[idx], opcoes: ops }; setChecklistForm(p => ({ ...p, itens: n })); };
  const checklistRemOpcao    = (idx, oidx) => { const n = [...checklistForm.itens]; n[idx] = { ...n[idx], opcoes: (n[idx].opcoes || []).filter((_, i) => i !== oidx) }; setChecklistForm(p => ({ ...p, itens: n })); };
  const checklistAddItem     = () => setChecklistForm(p => ({ ...p, itens: [...p.itens, { descricao: '', tipo: 'checkbox', opcoes: [''] }] }));
  const checklistRemItem     = (idx) => setChecklistForm(p => ({ ...p, itens: p.itens.filter((_, i) => i !== idx) }));
  const checklistToggleSub = (subId) => {
    setChecklistForm(p => ({
      ...p,
      subcategorias: p.subcategorias.includes(subId)
        ? p.subcategorias.filter(id => id !== subId)
        : [...p.subcategorias, subId],
    }));
  };

  // ── Mesas ──
  const carregarMesas = async () => {
    try { const r = await api.get('/tickets/mesas'); setMesas(r.data); } catch {}
  };
  const abrirModalMesa = (mesa = null) => {
    setMesaForm(mesa ? { nome: mesa.nome, membros_ids: mesa.membros_ids || [] } : { nome: '', membros_ids: [] });
    setEditandoMesa(mesa);
    setModalMesa(true);
  };
  const salvarMesa = async () => {
    if (!mesaForm.nome.trim()) { toast('Nome obrigatório', 'error'); return; }
    try {
      if (editandoMesa) { await api.patch(`/tickets/mesas/${editandoMesa.id}`, mesaForm); toast('Mesa atualizada!', 'success'); }
      else { await api.post('/tickets/mesas', mesaForm); toast('Mesa criada!', 'success'); }
      setModalMesa(false);
      carregarMesas();
    } catch (e) { toast(e.response?.data?.erro || 'Erro ao salvar mesa', 'error'); }
  };
  const removerMesa = async (id) => {
    if (!window.confirm('Remover esta mesa?')) return;
    try { await api.delete(`/tickets/mesas/${id}`); carregarMesas(); toast('Removida', 'success'); }
    catch { toast('Erro ao remover', 'error'); }
  };
  const toggleMembro = (userId) => {
    setMesaForm(prev => ({
      ...prev,
      membros_ids: prev.membros_ids.includes(userId)
        ? prev.membros_ids.filter(id => id !== userId)
        : [...prev.membros_ids, userId],
    }));
  };

  const ItemSimples = ({ nome, onEdit, onDelete, editando, onSalvar, onCancelar, onChange }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
      {editando ? (
        <>
          <input value={editando.nome} onChange={e => onChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onSalvar()}
            style={{ ...inputStyle, flex: 1, padding: '6px 10px' }} autoFocus />
          <button onClick={onSalvar} style={btnSm}>✓</button>
          <button onClick={onCancelar} style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }}>✕</button>
        </>
      ) : (
        <>
          <span style={{ flex: 1, fontSize: 14, color: '#1A2233', fontWeight: 500 }}>{nome}</span>
          <button onClick={onEdit} style={btnEdit}>Editar</button>
          <button onClick={onDelete} style={btnDanger}>Remover</button>
        </>
      )}
    </div>
  );

  // ── Abas horizontais ──
  const ABAS = [
    { key: 'config',    label: '⚙️ Configurações' },
    { key: 'email',     label: '📨 Email de Entrada' },
    { key: 'checklist', label: '✅ Checklists' },
    { key: 'personalizacao', label: '🖊️ Personalização' },
    { key: 'dominios',  label: '🛡️ Domínios' },
  ];

  return (
    <div>
      {/* Seletor de sub-abas estilo Integrações */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f0f3f7', padding: 4, borderRadius: 10, width: 'fit-content', flexWrap: 'wrap' }}>
        {ABAS.map(a => (
          <button key={a.key} onClick={() => setSubAba(a.key)}
            style={{ padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: subAba === a.key ? 'white' : 'transparent',
              color: subAba === a.key ? '#D85A30' : '#6b7a8d',
              boxShadow: subAba === a.key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
            {a.label}
          </button>
        ))}
      </div>

      {/* ── ABA: Configurações ── */}
      {subAba === 'config' && (
        <>
          {/* Tipos */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, color: '#1A2233' }}>Tipos de Ticket</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input value={novoTipo} onChange={e => setNovoTipo(e.target.value)} onKeyDown={e => e.key === 'Enter' && salvarTipo()}
                placeholder="Ex: Oficina, Orçamento, Plantão..." style={{ ...inputStyle, flex: 1 }} />
              <button onClick={salvarTipo} style={btnPrimary}>+ Adicionar</button>
            </div>
            {tipos.length === 0
              ? <div style={{ color: '#6b7a8d', fontSize: 13, textAlign: 'center', padding: '10px 0' }}>Nenhum tipo cadastrado</div>
              : tipos.map(t => (
                <ItemSimples key={t.id} nome={t.nome}
                  onEdit={() => setEditandoTipo({ id: t.id, nome: t.nome })}
                  onDelete={() => removerTipo(t.id)}
                  editando={editandoTipo?.id === t.id ? editandoTipo : null}
                  onSalvar={() => editarTipo(t.id)}
                  onCancelar={() => setEditandoTipo(null)}
                  onChange={v => setEditandoTipo(p => ({ ...p, nome: v }))}
                />
              ))
            }
          </div>

          {/* Categorias + Subcategorias */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, color: '#1A2233' }}>Categorias e Subcategorias</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input value={novaCategoria} onChange={e => setNovaCategoria(e.target.value)} onKeyDown={e => e.key === 'Enter' && salvarCategoria()}
                placeholder="Ex: Câmeras (CFTV), Informática (T.I)..." style={{ ...inputStyle, flex: 1 }} />
              <button onClick={salvarCategoria} style={btnPrimary}>+ Adicionar</button>
            </div>
            {categorias.length === 0
              ? <div style={{ color: '#6b7a8d', fontSize: 13, textAlign: 'center', padding: '10px 0' }}>Nenhuma categoria cadastrada</div>
              : categorias.map(c => (
                <div key={c.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 4, marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                    {editandoCategoria?.id === c.id ? (
                      <>
                        <input value={editandoCategoria.nome} onChange={e => setEditandoCategoria(p => ({ ...p, nome: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && editarCategoria(c.id)}
                          style={{ ...inputStyle, flex: 1, padding: '6px 10px' }} autoFocus />
                        <button onClick={() => editarCategoria(c.id)} style={btnSm}>✓</button>
                        <button onClick={() => setEditandoCategoria(null)} style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }}>✕</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setCatExpandida(catExpandida === c.id ? null : c.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6b7a8d', padding: '0 4px', fontWeight: 700 }}>
                          {catExpandida === c.id ? '▾' : '▸'}
                        </button>
                        <span style={{ flex: 1, fontSize: 14, color: '#1A2233', fontWeight: 600 }}>
                          {c.nome}
                          <span style={{ fontSize: 11, color: '#6b7a8d', fontWeight: 400, marginLeft: 8 }}>
                            {(c.subcategorias || []).length} subcategoria{(c.subcategorias || []).length !== 1 ? 's' : ''}
                          </span>
                        </span>
                        <button onClick={() => setEditandoCategoria({ id: c.id, nome: c.nome })} style={btnEdit}>Editar</button>
                        <button onClick={() => removerCategoria(c.id)} style={btnDanger}>Remover</button>
                      </>
                    )}
                  </div>
                  {catExpandida === c.id && (
                    <div style={{ marginLeft: 28, background: '#fafafa', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        <input value={novaSubcat} onChange={e => setNovaSubcat(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && salvarSubcat(c.id)}
                          placeholder={`Adicionar subcategoria em "${c.nome}"...`}
                          style={{ ...inputStyle, flex: 1, padding: '6px 10px', fontSize: 13 }} />
                        <button onClick={() => salvarSubcat(c.id)} style={{ ...btnSm, padding: '6px 12px' }}>+</button>
                      </div>
                      {(c.subcategorias || []).length === 0
                        ? <div style={{ fontSize: 12, color: '#6b7a8d', padding: '4px 0' }}>Nenhuma subcategoria</div>
                        : (c.subcategorias || []).map(s => (
                          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', borderBottom: '1px solid #ebebeb' }}>
                            {editandoSubcat?.id === s.id ? (
                              <>
                                <input value={editandoSubcat.nome} onChange={e => setEditandoSubcat(p => ({ ...p, nome: e.target.value }))}
                                  onKeyDown={e => e.key === 'Enter' && editarSubcat(s.id)}
                                  style={{ ...inputStyle, flex: 1, padding: '5px 8px', fontSize: 13 }} autoFocus />
                                <button onClick={() => editarSubcat(s.id)} style={{ ...btnSm, padding: '4px 10px' }}>✓</button>
                                <button onClick={() => setEditandoSubcat(null)} style={{ ...btnGhost, padding: '4px 8px', fontSize: 12 }}>✕</button>
                              </>
                            ) : (
                              <>
                                <span style={{ fontSize: 12, color: '#6b7a8d', marginRight: 2 }}>└</span>
                                <span style={{ flex: 1, fontSize: 13, color: '#1A2233' }}>{s.nome}</span>
                                <button onClick={() => setEditandoSubcat({ id: s.id, nome: s.nome })} style={{ ...btnEdit, padding: '3px 8px', fontSize: 11 }}>Editar</button>
                                <button onClick={() => removerSubcat(s.id)} style={{ ...btnDanger, padding: '3px 8px', fontSize: 11 }}>Remover</button>
                              </>
                            )}
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
              ))
            }
          </div>

          {/* Mesas de Trabalho */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#1A2233' }}>Mesas de Trabalho</div>
              <button onClick={() => abrirModalMesa()} style={btnPrimary}>+ Nova Mesa</button>
            </div>
            {mesas.length === 0
              ? <div style={{ color: '#6b7a8d', fontSize: 13, textAlign: 'center', padding: '10px 0' }}>Nenhuma mesa cadastrada</div>
              : mesas.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1A2233' }}>{m.nome}</div>
                    <div style={{ fontSize: 12, color: '#6b7a8d', marginTop: 2 }}>
                      {m.membros_nomes?.length > 0 ? m.membros_nomes.join(', ') : 'Sem membros'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => abrirModalMesa(m)} style={btnEdit}>Editar</button>
                    <button onClick={() => removerMesa(m.id)} style={btnDanger}>Remover</button>
                  </div>
                </div>
              ))
            }
          </div>

          {/* Numeração */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, color: '#1A2233' }}>🔢 Numeração de Tickets</div>
            <div style={{ fontSize: 13, color: '#6b7a8d', marginBottom: 14 }}>
              Define o número inicial para novos tickets.
              {tktNumeroCurrent && (
                <span style={{ marginLeft: 8, background: '#f0f9ff', color: '#0369a1', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                  Configurado: #{tktNumeroCurrent}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="number" min="1" max="999999" value={tktNumeroInicial}
                onChange={e => setTktNumeroInicial(e.target.value)} placeholder="Ex: 1000"
                style={{ ...inputStyle, width: 160 }} />
              <button onClick={salvarNumeroInicialTkt} disabled={salvandoNumeroTkt} style={{ ...btnPrimary, opacity: salvandoNumeroTkt ? 0.7 : 1 }}>
                {salvandoNumeroTkt ? 'Salvando...' : '💾 Salvar'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#6b7a8d', marginTop: 8 }}>
              ⚠️ Afeta apenas tickets criados a partir de agora.
            </div>
          </div>
        </>
      )}

      {/* ── ABA: Email de Entrada ── */}
      {subAba === 'email' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#1A2233' }}>📨 Email para abertura de tickets</div>
              <div style={{ fontSize: 13, color: '#6b7a8d', marginTop: 2 }}>
                Quando um cliente enviar email para este endereço, um ticket será aberto automaticamente.
              </div>
            </div>
            <div onClick={() => setTktEmailAtivo(v => !v)} title={tktEmailAtivo ? 'Desativar' : 'Ativar'}
              style={{ width: 44, height: 24, borderRadius: 12, cursor: 'pointer', flexShrink: 0, background: tktEmailAtivo ? '#D85A30' : '#d4dde8', position: 'relative', transition: 'background .2s' }}>
              <div style={{ position: 'absolute', top: 3, left: tktEmailAtivo ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
            </div>
          </div>

          {!tktEmailAtivo && (
            <div style={{ fontSize: 12, color: '#6b7a8d', background: '#f8fafc', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
              ⚠️ Integração desativada. Ative a chave acima para habilitar.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={labelStyle}>Host IMAP *</label>
              <input value={tktEmailHost} onChange={e => setTktEmailHost(e.target.value)} placeholder="imap.gmail.com" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Porta</label>
              <input value={tktEmailPorta} onChange={e => setTktEmailPorta(e.target.value)} placeholder="993" style={inputStyle} />
              <div style={{ fontSize: 11, color: '#6b7a8d', marginTop: 3 }}>993 = SSL · 143 = TLS</div>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Email da caixa de entrada *</label>
            <input type="email" value={tktEmailEntrada} onChange={e => setTktEmailEntrada(e.target.value)}
              placeholder="tickets@suaempresa.com.br" style={inputStyle} />
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Senha / App Password *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type={tktEmailSenhaVis ? 'text' : 'password'} value={tktEmailSenha}
                onChange={e => setTktEmailSenha(e.target.value)} placeholder="Senha ou App Password"
                style={{ ...inputStyle, flex: 1 }} />
              <button onClick={() => setTktEmailSenhaVis(v => !v)}
                style={{ padding: '8px 12px', background: '#f0f3f7', border: '1px solid #d4dde8', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
                {tktEmailSenhaVis ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Fila padrão dos tickets criados por email</label>
            <input value={tktEmailFila} onChange={e => setTktEmailFila(e.target.value)}
              placeholder="suporte" style={{ ...inputStyle, maxWidth: 240 }} />
          </div>

          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e' }}>
            💡 <strong>Como funciona:</strong> o sistema verifica a caixa a cada 5 minutos. O assunto do email vira o título do ticket e o corpo vira a descrição.
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={salvarEmailTkt} disabled={salvandoEmailTkt}
              style={{ ...btnPrimary, flex: 1, opacity: salvandoEmailTkt ? 0.7 : 1 }}>
              {salvandoEmailTkt ? 'Salvando...' : '💾 Salvar configuração de email'}
            </button>
            <button onClick={async () => {
              setTestando(true); setResultadoImap(null);
              try {
                const r = await api.post('/tickets/testar-imap');
                setResultadoImap({ ok: true, msg: r.data.mensagem });
              } catch (e) {
                setResultadoImap({ ok: false, msg: e.response?.data?.erro || 'Erro ao testar conexão.' });
              }
              setTestando(false);
            }} disabled={testandoImap}
              style={{ padding: '9px 16px', background: '#1A2233', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: testandoImap ? 0.7 : 1 }}>
              {testandoImap ? '⏳ Testando...' : '🔌 Testar conexão'}
            </button>
          </div>
          {resultadoImap && (
            <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: resultadoImap.ok ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${resultadoImap.ok ? '#86efac' : '#fca5a5'}`,
              color: resultadoImap.ok ? '#15803d' : '#dc2626' }}>
              {resultadoImap.msg}
            </div>
          )}
        </div>
      )}

      {/* ── ABA: Checklists ── */}
      {subAba === 'checklist' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#1A2233' }}>✅ Checklists de Finalização</div>
              <div style={{ fontSize: 13, color: '#6b7a8d', marginTop: 2 }}>
                Checklists são exibidos obrigatoriamente ao técnico ao finalizar tickets da subcategoria vinculada.
              </div>
            </div>
            {checklistAba === 'lista' && (
              <button onClick={() => {
                setChecklistForm({ nome: '', descricao: '', itens: [{ descricao: '' }], subcategorias: [] });
                setChecklistEditando(null);
                setChecklistAba('form');
              }} style={btnPrimary}>+ Novo Checklist</button>
            )}
          </div>

          {checklistAba === 'lista' ? (
            <>
              {checklists.length === 0 ? (
                <div style={{ color: '#6b7a8d', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
                  Nenhum checklist criado ainda.
                </div>
              ) : checklists.map(cl => (
                <div key={cl.id} style={{ border: '1px solid #e8ecf0', borderRadius: 10, padding: '12px 14px', marginBottom: 10, background: '#fafafa' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1A2233' }}>{cl.nome}</div>
                      {cl.descricao && <div style={{ fontSize: 12, color: '#6b7a8d', marginTop: 2 }}>{cl.descricao}</div>}
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {(cl.subcategorias || []).map(s => (
                          <span key={s.subcategoria_id} style={{ background: '#e8f0fe', color: '#1a2233', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                            {s.categoria_nome} → {s.subcategoria_nome}
                          </span>
                        ))}
                        {(!cl.subcategorias || cl.subcategorias.length === 0) && (
                          <span style={{ color: '#f59e0b', fontSize: 11, fontWeight: 600 }}>⚠ Sem subcategoria vinculada</span>
                        )}
                      </div>
                      <div style={{ marginTop: 5, fontSize: 11, color: '#94a3b8' }}>{(cl.itens || []).length} item(ns)</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button onClick={() => editarChecklist(cl)} style={btnEdit}>Editar</button>
                      <button onClick={() => excluirChecklist(cl.id)} style={btnDanger}>Remover</button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                <button onClick={() => { setChecklistAba('lista'); setChecklistEditando(null); }} style={btnGhost}>← Voltar</button>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1A2233' }}>{checklistEditando ? 'Editar Checklist' : 'Novo Checklist'}</div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Nome do Checklist *</label>
                <input style={inputStyle} value={checklistForm.nome}
                  onChange={e => setChecklistForm(p => ({ ...p, nome: e.target.value }))}
                  placeholder="Ex: Checklist de Preventiva de CFTV" />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Descrição (opcional)</label>
                <input style={inputStyle} value={checklistForm.descricao}
                  onChange={e => setChecklistForm(p => ({ ...p, descricao: e.target.value }))}
                  placeholder="Descrição interna do checklist" />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Vincular a Subcategorias</label>
                <div style={{ fontSize: 11, color: '#6b7a8d', marginBottom: 8 }}>
                  O checklist será exigido ao finalizar tickets das subcategorias selecionadas.
                </div>
                <div style={{ maxHeight: 220, overflowY: 'auto', border: '1.5px solid #d4dde8', borderRadius: 8, padding: '8px 10px' }}>
                  {categorias.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#6b7a8d', textAlign: 'center', padding: 8 }}>Nenhuma categoria cadastrada</div>
                  ) : categorias.map(cat => (
                    <div key={cat.id} style={{ marginBottom: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 11, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4, marginTop: 4 }}>{cat.nome}</div>
                      {(cat.subcategorias || []).length === 0 ? (
                        <div style={{ fontSize: 11, color: '#aaa', paddingLeft: 8 }}>Sem subcategorias</div>
                      ) : (cat.subcategorias || []).map(sub => {
                        const sel = checklistForm.subcategorias.includes(sub.id);
                        return (
                          <label key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer', borderRadius: 6, marginBottom: 2,
                            background: sel ? '#fff3eb' : 'transparent', border: `1px solid ${sel ? '#D85A30' : 'transparent'}` }}>
                            <input type="checkbox" style={{ accentColor: '#D85A30' }} checked={sel} onChange={() => checklistToggleSub(sub.id)} />
                            <span style={{ fontSize: 13, color: sel ? '#D85A30' : '#374151', fontWeight: sel ? 600 : 400 }}>{sub.nome}</span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Itens do Checklist *</label>
                <div style={{ fontSize: 11, color: '#6b7a8d', marginBottom: 10 }}>
                  Cada item pode ser uma confirmação, múltipla escolha ou resposta dissertativa.
                </div>
                {checklistForm.itens.map((item, idx) => (
                  <div key={idx} style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 10, background: '#fafafa' }}>
                    {/* Linha topo: número + tipo + remover */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ color: '#94a3b8', fontSize: 12, minWidth: 20, textAlign: 'right', flexShrink: 0, fontWeight: 700 }}>{idx + 1}.</span>
                      {/* Seletor de tipo */}
                      <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                        {[
                          { key: 'checkbox',  label: '✅ Confirmação',     title: 'Técnico marca como feito' },
                          { key: 'multipla',  label: '🔘 Múltipla escolha', title: 'Técnico escolhe uma opção' },
                          { key: 'dissertativa', label: '📝 Dissertativa',  title: 'Técnico digita uma resposta' },
                        ].map(t => (
                          <button key={t.key} title={t.title} onClick={() => checklistSetTipo(idx, t.key)}
                            style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, border: '1.5px solid', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
                              borderColor: item.tipo === t.key ? '#D85A30' : '#d4dde8',
                              background: item.tipo === t.key ? '#fff3eb' : 'white',
                              color: item.tipo === t.key ? '#D85A30' : '#6b7a8d' }}>
                            {t.label}
                          </button>
                        ))}
                      </div>
                      <button onClick={() => checklistRemItem(idx)}
                        style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 20, padding: '0 4px', lineHeight: 1, flexShrink: 0 }}>×</button>
                    </div>

                    {/* Pergunta / enunciado */}
                    <input style={{ ...inputStyle, padding: '7px 10px', fontSize: 13 }}
                      value={item.descricao}
                      onChange={e => checklistSetItem(idx, 'descricao', e.target.value)}
                      placeholder={
                        item.tipo === 'checkbox'     ? 'Ex: Verificou os cabos de alimentação?' :
                        item.tipo === 'multipla'     ? 'Ex: Quantas câmeras estão online?' :
                                                       'Ex: Descreva os problemas encontrados'
                      } />

                    {/* Opções de múltipla escolha */}
                    {item.tipo === 'multipla' && (
                      <div style={{ marginTop: 10, paddingLeft: 8 }}>
                        <div style={{ fontSize: 11, color: '#6b7a8d', marginBottom: 6, fontWeight: 600 }}>OPÇÕES DE RESPOSTA</div>
                        {(item.opcoes || ['']).map((op, oidx) => (
                          <div key={oidx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                            <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 16 }}>○</span>
                            <input style={{ ...inputStyle, flex: 1, padding: '5px 9px', fontSize: 12 }}
                              value={op}
                              onChange={e => checklistSetOpcao(idx, oidx, e.target.value)}
                              placeholder={`Opção ${oidx + 1}...`} />
                            {(item.opcoes || []).length > 2 && (
                              <button onClick={() => checklistRemOpcao(idx, oidx)}
                                style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 16, padding: '0 2px', lineHeight: 1 }}>×</button>
                            )}
                          </div>
                        ))}
                        <button onClick={() => checklistAddOpcao(idx)}
                          style={{ fontSize: 11, color: '#D85A30', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '2px 0' }}>
                          + Adicionar opção
                        </button>
                      </div>
                    )}

                    {/* Hint dissertativa */}
                    {item.tipo === 'dissertativa' && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#6b7a8d', fontStyle: 'italic', paddingLeft: 8 }}>
                        📝 O técnico poderá digitar livremente a resposta.
                      </div>
                    )}
                  </div>
                ))}
                <button onClick={checklistAddItem} style={{ ...btnGhost, fontSize: 12, marginTop: 4 }}>+ Adicionar Item</button>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={salvarChecklist} disabled={salvandoChecklist} style={{ ...btnPrimary, opacity: salvandoChecklist ? .7 : 1 }}>
                  {salvandoChecklist ? 'Salvando...' : checklistEditando ? '💾 Salvar Alterações' : '✅ Criar Checklist'}
                </button>
                <button onClick={() => { setChecklistAba('lista'); setChecklistEditando(null); }} style={btnGhost}>Cancelar</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ABA: Personalização ── */}
      {subAba === 'personalizacao' && (
        <>
          {/* Rótulos customizados */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, color: '#1A2233' }}>Rótulos da Ordem de Serviço</div>
            <div style={{ fontSize: 13, color: '#6b7a8d', marginBottom: 14 }}>
              Renomeie os campos padrão para o vocabulário do seu negócio (ex: "Cliente" → "Paciente"). Vale para o formulário do ticket e para a impressão da OS.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              {CAMPOS_PADRAO_OS.map(c => (
                <div key={c.chave}>
                  <label style={labelStyle}>{c.padrao}</label>
                  <input
                    value={labelsForm[c.chave] ?? ''}
                    onChange={e => setLabelsForm(p => ({ ...p, [c.chave]: e.target.value }))}
                    placeholder={c.padrao}
                    style={inputStyle}
                  />
                </div>
              ))}
            </div>
            <button onClick={salvarLabels} style={btnPrimary} disabled={salvandoLabels}>
              {salvandoLabels ? 'Salvando...' : 'Salvar Rótulos'}
            </button>
          </div>

          {/* Campos customizados */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, color: '#1A2233' }}>Campos Extras</div>
            <div style={{ fontSize: 13, color: '#6b7a8d', marginBottom: 14 }}>
              Crie campos adicionais para o formulário do ticket. Eles aparecem na OS impressa apenas quando preenchidos.
            </div>

            {camposCustom.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {camposCustom.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                    <span style={{ flex: 1, fontSize: 14, color: '#1A2233', fontWeight: 500 }}>
                      {c.label} <span style={{ color: '#9aa5b1', fontWeight: 400 }}>({c.tipo}{c.obrigatorio ? ', obrigatório' : ''})</span>
                    </span>
                    <button onClick={() => editarCampoCustom(c)} style={btnEdit}>Editar</button>
                    <button onClick={() => removerCampoCustom(c.id)} style={btnDanger}>Remover</button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, border: '1px solid #eef1f5' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#1A2233' }}>
                {editandoCampo ? 'Editar campo' : 'Novo campo'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: editandoCampo ? '1fr 1fr' : '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                {!editandoCampo && (
                  <div>
                    <label style={labelStyle}>Identificador Interno</label>
                    <input value={campoForm.nome_interno} onChange={e => setCampoForm(p => ({ ...p, nome_interno: e.target.value }))}
                      placeholder="ex: numero_serie" style={inputStyle} />
                  </div>
                )}
                <div>
                  <label style={labelStyle}>Rótulo (visível)</label>
                  <input value={campoForm.label} onChange={e => setCampoForm(p => ({ ...p, label: e.target.value }))}
                    placeholder="ex: Número de Série" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Tipo</label>
                  <select value={campoForm.tipo} onChange={e => setCampoForm(p => ({ ...p, tipo: e.target.value }))} style={inputStyle}>
                    <option value="texto">Texto</option>
                    <option value="numero">Número</option>
                    <option value="data">Data</option>
                    <option value="select">Lista de opções</option>
                    <option value="textarea">Texto longo</option>
                  </select>
                </div>
              </div>

              {campoForm.tipo === 'select' && (
                <div style={{ marginBottom: 10 }}>
                  <label style={labelStyle}>Opções</label>
                  {campoForm.opcoes.map((op, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                      <input value={op} onChange={e => campoSetOpcao(i, e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                      <button onClick={() => campoRemOpcao(i)} style={{ ...btnGhost, padding: '5px 10px' }}>✕</button>
                    </div>
                  ))}
                  <button onClick={campoAddOpcao} style={{ ...btnGhost, fontSize: 12, padding: '5px 12px' }}>+ Opção</button>
                </div>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13, color: '#1A2233', cursor: 'pointer' }}>
                <input type="checkbox" checked={campoForm.obrigatorio} onChange={e => setCampoForm(p => ({ ...p, obrigatorio: e.target.checked }))} />
                Obrigatório para finalizar o ticket
              </label>

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={salvarCampoCustom} style={btnPrimary} disabled={salvandoCampo}>
                  {salvandoCampo ? 'Salvando...' : editandoCampo ? 'Salvar Alterações' : 'Criar Campo'}
                </button>
                {editandoCampo && (
                  <button onClick={() => { novoCampoForm(); setEditandoCampo(null); }} style={btnGhost}>Cancelar</button>
                )}
              </div>
            </div>
          </div>

          {/* Assinatura digital */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, color: '#1A2233' }}>Assinatura Digital</div>
            <div style={{ fontSize: 13, color: '#6b7a8d', marginBottom: 14 }}>
              Quando ativa, o técnico e o cliente podem assinar (desenho na tela) ao finalizar a OS. Nunca é obrigatório — a OS pode ser finalizada sem assinatura mesmo com a opção ativa.
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={assinaturaAtiva} onChange={toggleAssinaturaAtiva} disabled={salvandoAssinaturaToggle}
                style={{ width: 18, height: 18, cursor: 'pointer' }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1A2233' }}>
                Habilitar captura de assinatura na finalização de tickets
              </span>
            </label>
          </div>
        </>
      )}

      {/* ── ABA: Domínios ── */}
      {subAba === 'dominios' && (
        <div style={cardStyle}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1A2233' }}>🛡️ Domínios autorizados a abrir tickets</div>
            <div style={{ fontSize: 13, color: '#6b7a8d', marginTop: 4 }}>
              Somente emails vindos destes domínios criarão tickets automaticamente. Deixe vazio para aceitar qualquer remetente.
            </div>
          </div>

          {tktDominios.length === 0 ? (
            <div style={{ fontSize: 13, color: '#6b7a8d', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
              ⚠️ Nenhum domínio cadastrado — <strong>qualquer email</strong> pode abrir um ticket, incluindo spam.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              {tktDominios.map(d => (
                <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 20, padding: '4px 10px 4px 12px' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#15803d' }}>@{d}</span>
                  <button onClick={() => removerDominio(d)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#15803d', fontSize: 14, lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center' }}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7a8d', fontSize: 14, pointerEvents: 'none' }}>@</span>
              <input value={tktNovoDominio} onChange={e => setTktNovoDominio(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && adicionarDominio()}
                placeholder="suaempresa.com.br" style={{ ...inputStyle, paddingLeft: 28 }} />
            </div>
            <button onClick={adicionarDominio} style={{ ...btnSm, whiteSpace: 'nowrap', padding: '9px 16px' }}>+ Adicionar</button>
          </div>

          <div style={{ fontSize: 11, color: '#6b7a8d', marginBottom: 14 }}>
            💡 Subdomínios são aceitos automaticamente. Ex: cadastrar <strong>empresa.com.br</strong> também autoriza <strong>mail.empresa.com.br</strong>.
          </div>

          <button onClick={salvarDominios} disabled={salvandoDominios} style={{ ...btnPrimary, opacity: salvandoDominios ? 0.7 : 1 }}>
            {salvandoDominios ? 'Salvando...' : '💾 Salvar domínios'}
          </button>
        </div>
      )}

      {/* Modal Mesa */}
      {modalMesa && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: 480, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 20, color: '#1a2233' }}>
              {editandoMesa ? 'Editar Mesa de Trabalho' : 'Nova Mesa de Trabalho'}
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Nome da mesa *</label>
              <input value={mesaForm.nome} onChange={e => setMesaForm(p => ({ ...p, nome: e.target.value }))}
                placeholder="Ex: CFTV, T.I, Telefonia" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Operadores desta mesa</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto', padding: 4 }}>
                {(usuarios || []).filter(u => u.ativo !== false).map(u => {
                  const sel = mesaForm.membros_ids.includes(u.id);
                  return (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 10px', borderRadius: 8, border: `1px solid ${sel ? '#D85A30' : '#d4dde8'}`, background: sel ? '#fff8f0' : 'white' }}>
                      <input type="checkbox" checked={sel} onChange={() => toggleMembro(u.id)} style={{ display: 'none' }} />
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: u.cor || '#D85A30', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                        {u.iniciais || u.nome?.slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1a2233' }}>{u.nome}</div>
                        <div style={{ fontSize: 12, color: '#6b7a8d' }}>{u.email}</div>
                      </div>
                      {sel && <span style={{ color: '#D85A30', fontSize: 16 }}>✓</span>}
                    </label>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setModalMesa(false)} style={btnGhost}>Cancelar</button>
              <button onClick={salvarMesa} style={btnPrimary}>{editandoMesa ? 'Salvar' : 'Criar Mesa'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ABA BACKUP ────────────────────────────────────────────────────────────────

// ── AbaFinanceiroConfig — gerencia categorias de Contas a Pagar e Receber ───
function AbaFinanceiroConfig({ toast }) {
  const [subAba, setSubAba] = useState('pagar');
  const [categorias, setCategorias] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [novaCor, setNovaCor] = useState('#1A2233');
  const [editando, setEditando] = useState(null);

  // Estados da sub-aba Notas Fiscais
  const [nfeInicial, setNfeInicial] = useState('');
  const [nfseInicial, setNfseInicial] = useState('');
  const [salvandoNotas, setSalvandoNotas] = useState(false);
  const [infoNfe, setInfoNfe] = useState(null);
  const [infoNfse, setInfoNfse] = useState(null);

  const labelStyle = { fontSize: 11, fontWeight: 700, color: '#6b7a8d', display: 'block', marginBottom: 4, textTransform: 'uppercase' };
  const inputStyle = { width: '100%', padding: '9px 12px', border: '1.5px solid #d4dde8', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

  useEffect(() => {
    if (subAba === 'pagar' || subAba === 'receber') carregar();
    if (subAba === 'notas') carregarNotas();
  }, [subAba]);

  const carregar = async () => {
    setCarregando(true);
    try {
      const r = await api.get(`/financeiro/categorias?tipo=${subAba}`);
      setCategorias(r.data || []);
    } catch {}
    setCarregando(false);
  };

  const carregarNotas = async () => {
    try {
      const [rNfe, rNfse, cfgNfe, cfgNfse] = await Promise.all([
        api.get('/notas/proximo-numero/nfe').catch(() => ({ data: null })),
        api.get('/notas/proximo-numero/nfse').catch(() => ({ data: null })),
        api.get('/configuracoes').catch(() => ({ data: [] })),
        api.get('/configuracoes').catch(() => ({ data: [] })),
      ]);
      setInfoNfe(rNfe.data);
      setInfoNfse(rNfse.data);
      const cfgs = {};
      (cfgNfe.data || []).forEach(x => { cfgs[x.chave] = x.valor; });
      setNfeInicial(cfgs['numero_inicial_nfe'] || '1');
      setNfseInicial(cfgs['numero_inicial_nfse'] || '1');
    } catch {}
  };

  const salvarNumerosNotas = async () => {
    if (!nfeInicial || !nfseInicial) { toast('Preencha os dois campos.', 'error'); return; }
    setSalvandoNotas(true);
    try {
      await api.post('/notas/numeracao', {
        numero_inicial_nfe: parseInt(nfeInicial) || 1,
        numero_inicial_nfse: parseInt(nfseInicial) || 1,
      });
      toast('Numeração salva com sucesso!', 'success');
      carregarNotas();
    } catch { toast('Erro ao salvar numeração.', 'error'); }
    setSalvandoNotas(false);
  };

  const salvarNovo = async () => {
    if (!novoNome.trim()) { toast('Informe o nome da categoria.', 'error'); return; }
    setSalvando(true);
    try {
      await api.post('/financeiro/categorias', { nome: novoNome.trim(), cor: novaCor, tipo: subAba });
      toast('Categoria criada!', 'success');
      setNovoNome(''); setNovaCor('#1A2233');
      carregar();
    } catch { toast('Erro ao criar categoria.', 'error'); }
    setSalvando(false);
  };

  const salvarEdicao = async () => {
    if (!editando?.nome?.trim()) { toast('Informe o nome.', 'error'); return; }
    setSalvando(true);
    try {
      await api.patch(`/financeiro/categorias/${editando.id}`, { nome: editando.nome.trim(), cor: editando.cor });
      toast('Categoria atualizada!', 'success');
      setEditando(null);
      carregar();
    } catch { toast('Erro ao atualizar.', 'error'); }
    setSalvando(false);
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta categoria? Os lançamentos existentes não serão afetados.')) return;
    try {
      await api.delete(`/financeiro/categorias/${id}`);
      toast('Categoria excluída!', 'success');
      if (editando?.id === id) setEditando(null);
      carregar();
    } catch { toast('Erro ao excluir.', 'error'); }
  };

  const CORES_RAPIDAS = ['#1A2233','#D85A30','#27ae60','#e74c3c','#2563eb','#9333ea','#e8a020','#6b7a8d'];

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 16, color: '#1a2233', marginBottom: 4 }}>💰 Configurações Financeiras</div>
      <div style={{ fontSize: 13, color: '#6b7a8d', marginBottom: 20 }}>Gerencie as categorias de Contas a Pagar e Contas a Receber.</div>

      {/* Sub-abas */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#f0f3f7', borderRadius: 10, padding: 4 }}>
        {[['pagar', '💸 Contas a Pagar'], ['receber', '💰 Contas a Receber'], ['notas', '🧾 Notas Fiscais']].map(([k, l]) => (
          <button key={k} onClick={() => { setSubAba(k); setEditando(null); setNovoNome(''); }} style={{
            flex: 1, padding: '8px', border: 'none', borderRadius: 8, cursor: 'pointer',
            background: subAba === k ? 'white' : 'transparent',
            color: subAba === k ? '#D85A30' : '#6b7a8d',
            fontWeight: subAba === k ? 700 : 500, fontSize: 13,
            boxShadow: subAba === k ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
          }}>{l}</button>
        ))}
      </div>

      {/* ── Sub-aba Notas Fiscais ── */}
      {subAba === 'notas' && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a2233', marginBottom: 4 }}>🧾 Numeração das Notas Fiscais</div>
          <div style={{ fontSize: 13, color: '#6b7a8d', marginBottom: 20 }}>
            Define o número inicial de cada tipo de nota. Use para continuar a numeração de outro sistema.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            {/* NF-e Produto */}
            <div style={{ background: 'white', border: '1px solid #d4dde8', borderRadius: 12, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>📦 NF-e — Produto</div>
              <div style={{ fontSize: 12, color: '#6b7a8d', marginBottom: 16 }}>Nota Fiscal Eletrônica de produto</div>
              <label style={labelStyle}>Número inicial</label>
              <input
                type="number" min={1} value={nfeInicial}
                onChange={e => setNfeInicial(e.target.value)}
                style={{ ...inputStyle, marginBottom: 12 }}
                placeholder="Ex: 839"
              />
              {infoNfe && (
                <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
                  <div style={{ color: '#6b7a8d' }}>Notas emitidas: <strong style={{ color: '#1a2233' }}>{infoNfe.total_emitidas}</strong></div>
                  <div style={{ color: '#6b7a8d' }}>Próximo número: <strong style={{ color: '#D85A30', fontSize: 14 }}>#{infoNfe.proximo}</strong></div>
                </div>
              )}
            </div>

            {/* NFS-e Serviço */}
            <div style={{ background: 'white', border: '1px solid #d4dde8', borderRadius: 12, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>🛠️ NFS-e — Serviço</div>
              <div style={{ fontSize: 12, color: '#6b7a8d', marginBottom: 16 }}>Nota Fiscal de Serviço Eletrônica</div>
              <label style={labelStyle}>Número inicial</label>
              <input
                type="number" min={1} value={nfseInicial}
                onChange={e => setNfseInicial(e.target.value)}
                style={{ ...inputStyle, marginBottom: 12 }}
                placeholder="Ex: 3826"
              />
              {infoNfse && (
                <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
                  <div style={{ color: '#6b7a8d' }}>Notas emitidas: <strong style={{ color: '#1a2233' }}>{infoNfse.total_emitidas}</strong></div>
                  <div style={{ color: '#6b7a8d' }}>Próximo número: <strong style={{ color: '#D85A30', fontSize: 14 }}>#{infoNfse.proximo}</strong></div>
                </div>
              )}
            </div>
          </div>

          <div style={{ background: '#fff8f5', border: '1px solid #f5cbb0', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 12, color: '#7a4010' }}>
            ⚠️ <strong>Atenção:</strong> alterar o número inicial afeta apenas novas notas emitidas pelo Sobraa. Não altera notas já emitidas.
          </div>

          <button onClick={salvarNumerosNotas} disabled={salvandoNotas} style={{
            padding: '10px 24px', background: '#D85A30', color: 'white', border: 'none',
            borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer',
            opacity: salvandoNotas ? 0.7 : 1,
          }}>
            {salvandoNotas ? 'Salvando...' : '💾 Salvar numeração'}
          </button>
        </div>
      )}

      {/* Formulário nova categoria e lista — só para pagar/receber */}
      {subAba !== 'notas' && (<>
      <div style={{ background: 'white', border: '1px solid #d4dde8', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>➕ Nova Categoria</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Nome da categoria *</label>
            <input value={novoNome} onChange={e => setNovoNome(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && salvarNovo()}
              placeholder={subAba === 'pagar' ? 'Ex: Fornecedores, Aluguel, Energia...' : 'Ex: Vendas, Serviços, Mensalidades...'}
              style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Cor</label>
            <input type="color" value={novaCor} onChange={e => setNovaCor(e.target.value)}
              style={{ width: 46, height: 40, border: '1.5px solid #d4dde8', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {CORES_RAPIDAS.map(c => (
            <button key={c} onClick={() => setNovaCor(c)} style={{
              width: 24, height: 24, borderRadius: '50%', background: c, border: novaCor === c ? '3px solid #1A2233' : '2px solid white',
              cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          ))}
        </div>
        <button onClick={salvarNovo} disabled={salvando || !novoNome.trim()} style={{
          padding: '9px 20px', background: '#D85A30', color: 'white', border: 'none', borderRadius: 8,
          fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: !novoNome.trim() ? 0.5 : 1,
        }}>
          {salvando ? 'Salvando...' : '✔ Adicionar Categoria'}
        </button>
      </div>

      {/* Lista de categorias */}
      <div style={{ background: 'white', border: '1px solid #d4dde8', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #d4dde8', fontWeight: 700, fontSize: 14 }}>
          Categorias de {subAba === 'pagar' ? 'Contas a Pagar' : 'Contas a Receber'} ({categorias.length})
        </div>

        {carregando ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#6b7a8d' }}>Carregando...</div>
        ) : categorias.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#6b7a8d' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
            Nenhuma categoria cadastrada ainda.
          </div>
        ) : categorias.map((c, i) => (
          <div key={c.id}>
            {editando?.id === c.id ? (
              // Modo edição inline
              <div style={{ padding: '12px 20px', background: '#fffbeb', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input value={editando.nome} onChange={e => setEditando(p => ({...p, nome: e.target.value}))}
                  onKeyDown={e => e.key === 'Enter' && salvarEdicao()}
                  style={{ ...inputStyle, flex: 1, minWidth: 120 }} autoFocus />
                <input type="color" value={editando.cor || '#1A2233'} onChange={e => setEditando(p => ({...p, cor: e.target.value}))}
                  style={{ width: 40, height: 38, border: '1.5px solid #d4dde8', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                <div style={{ display: 'flex', gap: 6 }}>
                  {CORES_RAPIDAS.map(cor => (
                    <button key={cor} onClick={() => setEditando(p => ({...p, cor}))} style={{
                      width: 22, height: 22, borderRadius: '50%', background: cor,
                      border: editando.cor === cor ? '3px solid #1A2233' : '2px solid white',
                      cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  ))}
                </div>
                <button onClick={salvarEdicao} disabled={salvando} style={{ padding:'7px 14px', background:'#D85A30', color:'white', border:'none', borderRadius:7, fontWeight:700, fontSize:13, cursor:'pointer' }}>
                  {salvando ? '...' : 'Salvar'}
                </button>
                <button onClick={() => setEditando(null)} style={{ padding:'7px 12px', background:'#f0f3f7', color:'#6b7a8d', border:'none', borderRadius:7, fontSize:13, cursor:'pointer' }}>
                  Cancelar
                </button>
              </div>
            ) : (
              // Modo visualização
              <div style={{ padding: '12px 20px', display:'flex', alignItems:'center', gap:12, borderBottom: i < categorias.length-1 ? '1px solid #d4dde8' : 'none' }}>
                <div style={{ width:14, height:14, borderRadius:'50%', background: c.cor || '#1A2233', flexShrink:0 }} />
                <span style={{ flex:1, fontWeight:600, fontSize:14, color:'#1a2233' }}>{c.nome}</span>
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={() => setEditando({ id:c.id, nome:c.nome, cor:c.cor||'#1A2233' })}
                    style={{ padding:'5px 10px', background:'#eef2ff', color:'#2563eb', border:'none', borderRadius:6, cursor:'pointer', fontSize:12 }}>✏️</button>
                  <button onClick={() => excluir(c.id)}
                    style={{ padding:'5px 10px', background:'#fde8e8', color:'#e74c3c', border:'none', borderRadius:6, cursor:'pointer', fontSize:12 }}>🗑️</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      </>)} {/* fim subAba !== notas */}
    </div>
  );
}

function AbaBackup({ toast }) {
  const [exportando, setExportando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = React.useRef(null);

  const cardStyle = { background: 'white', borderRadius: 16, border: `1px solid #d4dde8`, padding: '24px 20px', marginBottom: 16 };

  const exportar = async () => {
    setExportando(true);
    try {
      const [
        usuarios, categorias, lancamentos, salarios,
        contratos, despesas, reservado, socios, bancos,
        fechamentos, orcamentos, configs, estoque,
        fin_categorias, fin_lancamentos,
        tickets, wpp_contatos,
      ] = await Promise.all([
        api.get('/usuarios'),
        api.get('/configuracoes/categorias').catch(() => ({ data: [] })),
        api.get('/lancamentos'),
        api.get('/salarios'),
        api.get('/fechamento/contratos'),
        api.get('/fechamento/despesas'),
        api.get('/fechamento/reservado'),
        api.get('/fechamento/socios'),
        api.get('/fechamento/bancos'),
        api.get('/fechamento/historico').catch(() => ({ data: [] })),
        api.get('/orcamentos').catch(() => ({ data: [] })),
        api.get('/configuracoes').catch(() => ({ data: [] })),
        api.get('/estoque').catch(() => ({ data: [] })),
        api.get('/financeiro/categorias?tipo=pagar').catch(() => ({ data: [] })),
        api.get('/financeiro/lancamentos?tipo=pagar&limite=99999').catch(() => ({ data: [] })),
        api.get('/tickets?limite=5000').catch(() => ({ data: [] })),
        api.get('/whatsapp/contatos').catch(() => ({ data: [] })),
      ]);

      // Contas a Receber separado para juntar tudo
      const [fin_cats_receber, fin_lanc_receber] = await Promise.all([
        api.get('/financeiro/categorias?tipo=receber').catch(() => ({ data: [] })),
        api.get('/financeiro/lancamentos?tipo=receber&limite=99999').catch(() => ({ data: [] })),
      ]);

      const backup = {
        versao: '1.2',
        exportadoEm: new Date().toISOString(),
        dados: {
          usuarios: usuarios.data,
          categorias: categorias.data,
          lancamentos: lancamentos.data,
          salarios: salarios.data,
          contratos: contratos.data,
          despesas: despesas.data,
          reservado: reservado.data,
          socios: socios.data,
          bancos: bancos.data,
          fechamentos: fechamentos.data,
          orcamentos: orcamentos.data,
          configs: configs.data,
          estoque: estoque.data,
          fin_categorias: [...(fin_categorias.data || []), ...(fin_cats_receber.data || [])],
          fin_lancamentos: [...(fin_lancamentos.data || []), ...(fin_lanc_receber.data || [])],
          tickets: Array.isArray(tickets.data) ? tickets.data : (tickets.data?.tickets || []),
          wpp_contatos: wpp_contatos.data,
        },
      };

      const json = JSON.stringify(backup, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const data = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `backup_sobraa_${data}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast('Backup exportado com sucesso!', 'success');
    } catch { toast('Erro ao exportar backup.', 'error'); }
    setExportando(false);
  };

  const selecionarArquivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const dados = JSON.parse(ev.target.result);
        if (!dados.versao || !dados.dados) {
          toast('Arquivo inválido. Selecione um backup gerado pelo sistema.', 'error');
          return;
        }
        setPreview(dados);
      } catch { toast('Erro ao ler o arquivo. Verifique se é um JSON válido.', 'error'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const confirmarImportacao = async () => {
    if (!preview) return;
    if (!window.confirm('⚠️ ATENÇÃO: A importação irá SUBSTITUIR todos os dados atuais da empresa. Tem certeza?')) return;
    setImportando(true);
    try {
      await api.post('/backup/importar', preview);
      toast('Backup importado com sucesso! Recarregue a página.', 'success');
      setPreview(null);
    } catch { toast('Erro ao importar backup. Verifique o arquivo.', 'error'); }
    setImportando(false);
  };

  const contarItens = (dados) => [
    { label: 'Usuários',          valor: dados.usuarios?.length || 0 },
    { label: 'Lançamentos',       valor: dados.lancamentos?.length || 0 },
    { label: 'Contratos',         valor: dados.contratos?.length || 0 },
    { label: 'Despesas',          valor: dados.despesas?.length || 0 },
    { label: 'Salários',          valor: dados.salarios?.length || 0 },
    { label: 'Fechamentos',       valor: dados.fechamentos?.length || 0 },
    { label: 'Orçamentos',        valor: dados.orcamentos?.length || 0 },
    { label: 'Sócios',            valor: dados.socios?.length || 0 },
    { label: 'Bancos',            valor: dados.bancos?.length || 0 },
    { label: 'Estoque',           valor: dados.estoque?.length || 0 },
    { label: 'Contas (Fin.)',     valor: dados.fin_lancamentos?.length || 0 },
    { label: 'Tickets',           valor: dados.tickets?.length || 0 },
    { label: 'Contatos WPP',      valor: dados.wpp_contatos?.length || 0 },
  ];

  return (
    <div>
      {/* Exportar */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📤</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1a2233' }}>Exportar Backup</div>
            <div style={{ fontSize: 13, color: '#6b7a8d', marginTop: 2 }}>Baixa um arquivo JSON com todos os dados da empresa</div>
          </div>
        </div>
        <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#155724' }}>
          ✅ Inclui: usuários, lançamentos, contratos, despesas, salários, fechamentos, orçamentos, sócios e bancos, estoque, agenda, clientes, catálogo, notas fiscais, cofre de senhas, contas a pagar/receber, tickets e contatos WhatsApp.
        </div>
        <button onClick={exportar} disabled={exportando} style={{
          width: '100%', padding: '12px', background: '#27ae60', color: 'white',
          border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer'
        }}>
          {exportando ? '⏳ Exportando...' : '📥 Exportar Backup'}
        </button>
      </div>

      {/* Importar */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#fff3eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📂</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1a2233' }}>Importar Backup</div>
            <div style={{ fontSize: 13, color: '#6b7a8d', marginTop: 2 }}>Restaura os dados a partir de um arquivo de backup</div>
          </div>
        </div>
        <div style={{ background: '#fffbeb', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#856404' }}>
          ⚠️ Atenção: a importação substitui todos os dados atuais. Faça um backup antes de importar.
        </div>

        <input ref={fileRef} type="file" accept=".json" onChange={selecionarArquivo} style={{ display: 'none' }} />
        <button onClick={() => fileRef.current?.click()} style={{
          width: '100%', padding: '12px', background: '#D85A30', color: 'white',
          border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 12
        }}>
          📁 Selecionar Arquivo de Backup
        </button>

        {/* Preview do arquivo selecionado */}
        {preview && (
          <div style={{ background: '#f7f8fa', borderRadius: 10, padding: '16px', border: '1px solid #e4e9f0' }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: '#1a2233' }}>📋 Arquivo selecionado</div>
            <div style={{ fontSize: 12, color: '#6b7a8d', marginBottom: 12 }}>
              Exportado em: {new Date(preview.exportadoEm).toLocaleString('pt-BR')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
              {contarItens(preview.dados).map(({ label, valor }) => (
                <div key={label} style={{ background: 'white', borderRadius: 8, padding: '8px 12px', border: '1px solid #e4e9f0', textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#D85A30' }}>{valor}</div>
                  <div style={{ fontSize: 11, color: '#6b7a8d' }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setPreview(null)} style={{
                flex: 1, padding: '10px', background: '#f0f3f7', color: '#6b7a8d',
                border: '1px solid #d4dde8', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer'
              }}>Cancelar</button>
              <button onClick={confirmarImportacao} disabled={importando} style={{
                flex: 2, padding: '10px', background: '#e74c3c', color: 'white',
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer'
              }}>
                {importando ? '⏳ Importando...' : '⚠️ Confirmar Importação'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-aba Relatório — Histórico de Envios Automáticos + Template ───────────
const MESES_ABREV_REL = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

function AbaRelatorioConfig({ toast }) {
  const [abaLocal, setAbaLocal]   = useState('historico');
  const [envios, setEnvios]       = useState([]);
  const [totalEnvios, setTotal]   = useState(0);
  const [pagina, setPagina]       = useState(1);
  const [loading, setLoading]     = useState(true);
  const [template, setTemplate]   = useState({ assunto: '', corpo: '' });
  const [salvando, setSalvando]   = useState(false);
  const LIMITE = 15;

  const carregarHistorico = React.useCallback((p = 1) => {
    setLoading(true);
    api.get(`/relatorios/envios/historico?pagina=${p}&limite=${LIMITE}`)
      .then(r => { setEnvios(r.data.envios || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    carregarHistorico(1);
    api.get('/relatorios/envios/template')
      .then(r => setTemplate(r.data))
      .catch(() => {});
  }, [carregarHistorico]);

  const handleSalvar = async () => {
    if (!template.assunto?.trim() || !template.corpo?.trim()) {
      toast('Preencha o assunto e o corpo do email.', 'error'); return;
    }
    setSalvando(true);
    try {
      await api.post('/relatorios/envios/template', template);
      toast('✅ Template salvo com sucesso!', 'success');
    } catch (e) {
      toast(e?.response?.data?.erro || 'Erro ao salvar template.', 'error');
    } finally { setSalvando(false); }
  };

  const totalPaginas = Math.ceil(totalEnvios / LIMITE);

  const variaveis = [
    { v: '{{mes}}',      desc: 'Mês de referência (ex: Maio / 2026)' },
    { v: '{{cliente}}',  desc: 'Nome do cliente' },
    { v: '{{total}}',    desc: 'Total de tickets' },
    { v: '{{fechados}}', desc: 'Tickets concluídos' },
    { v: '{{abertos}}',  desc: 'Tickets em aberto' },
    { v: '{{horas}}',    desc: 'Total de horas de atendimento' },
    { v: '{{empresa}}',  desc: 'Nome da empresa' },
  ];

  const inp = { width: '100%', padding: '10px 12px', border: `1px solid ${cores.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', color: cores.navy };

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ background: 'white', borderRadius: 16, border: `1px solid ${cores.border}`, padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: cores.navy, marginBottom: 4 }}>📬 Relatórios Automáticos de Tickets</div>
        <div style={{ fontSize: 13, color: cores.muted, lineHeight: 1.6 }}>
          No <strong>dia 1 de cada mês</strong>, o sistema envia automaticamente o relatório do mês anterior
          para todos os clientes com <em>"Notificar ao finalizar ticket"</em> ativado e email de tickets preenchido.
        </div>
      </div>

      {/* Sub-abas locais */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[['historico','📋 Histórico de Envios'],['template','✏️ Configurar Email']].map(([k,l]) => (
          <button key={k} onClick={() => setAbaLocal(k)} style={{
            padding: '8px 16px',
            border: `1.5px solid ${abaLocal===k ? cores.orange : cores.border}`,
            borderRadius: 8,
            background: abaLocal===k ? cores.orange : 'white',
            color: abaLocal===k ? 'white' : cores.muted,
            fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all .15s',
          }}>{l}</button>
        ))}
      </div>

      {/* ── Histórico ── */}
      {abaLocal === 'historico' && (
        <div style={{ background: 'white', borderRadius: 16, border: `1px solid ${cores.border}`, padding: '18px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: cores.muted, fontSize: 13 }}>Carregando...</div>
          ) : envios.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: cores.muted, fontSize: 13 }}>
              📭 Nenhum envio automático registrado ainda.
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f5f7fa' }}>
                      <th style={{ padding: '10px 12px', fontWeight: 700, fontSize: 11, color: cores.muted, textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'left' }}>Cliente</th>
                      <th style={{ padding: '10px 12px', fontWeight: 700, fontSize: 11, color: cores.muted, textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'left' }}>Email</th>
                      <th style={{ padding: '10px 12px', fontWeight: 700, fontSize: 11, color: cores.muted, textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'center' }}>Mês Ref.</th>
                      <th style={{ padding: '10px 12px', fontWeight: 700, fontSize: 11, color: cores.muted, textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'center' }}>Tickets</th>
                      <th style={{ padding: '10px 12px', fontWeight: 700, fontSize: 11, color: cores.muted, textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'left' }}>Enviado em</th>
                      <th style={{ padding: '10px 12px', fontWeight: 700, fontSize: 11, color: cores.muted, textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {envios.map((e, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${cores.border}` }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600, color: cores.navy }}>{e.cliente_nome}</td>
                        <td style={{ padding: '10px 12px', color: cores.muted, fontSize: 12 }}>{e.cliente_email}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap', color: cores.navy }}>
                          {MESES_ABREV_REL[e.mes - 1]}/{e.ano}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: cores.navy }}>{e.total_tickets}</td>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 12, color: cores.muted }}>
                          {e.enviado_em ? new Date(e.enviado_em).toLocaleString('pt-BR', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          }) : '—'}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          {e.status === 'enviado'
                            ? <span style={{ color: '#27ae60', fontWeight: 700, fontSize: 12 }}>✅ Enviado</span>
                            : <span style={{ color: '#e74c3c', fontWeight: 700, fontSize: 12 }} title={e.erro_msg || ''}>❌ Erro</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {totalPaginas > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 14, borderTop: `1px solid ${cores.border}` }}>
                  <span style={{ fontSize: 12, color: cores.muted }}>{totalEnvios} registro(s)</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => { const p = pagina-1; setPagina(p); carregarHistorico(p); }} disabled={pagina===1}
                      style={{ padding: '6px 14px', border: `1.5px solid ${cores.border}`, borderRadius: 6, background: 'white', color: pagina===1?cores.muted:cores.navy, fontSize: 12, fontWeight: 600, cursor: pagina===1?'not-allowed':'pointer' }}>
                      ← Anterior
                    </button>
                    <span style={{ padding: '6px 8px', fontSize: 12, color: cores.muted }}>{pagina}/{totalPaginas}</span>
                    <button onClick={() => { const p = pagina+1; setPagina(p); carregarHistorico(p); }} disabled={pagina===totalPaginas}
                      style={{ padding: '6px 14px', border: `1.5px solid ${cores.border}`, borderRadius: 6, background: 'white', color: pagina===totalPaginas?cores.muted:cores.navy, fontSize: 12, fontWeight: 600, cursor: pagina===totalPaginas?'not-allowed':'pointer' }}>
                      Próximo →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Configurar Email ── */}
      {abaLocal === 'template' && (
        <div style={{ background: 'white', borderRadius: 16, border: `1px solid ${cores.border}`, padding: '18px 20px' }}>
          <div style={{ fontSize: 13, color: cores.muted, marginBottom: 16, lineHeight: 1.6 }}>
            Personalize o email enviado automaticamente. Use as variáveis para incluir dados dinâmicos:
          </div>

          {/* Variáveis */}
          <div style={{ background: '#f5f7fa', borderRadius: 8, padding: '12px 14px', marginBottom: 18, display: 'flex', flexWrap: 'wrap', gap: '8px 20px' }}>
            {variaveis.map(v => (
              <div key={v.v} style={{ fontSize: 12 }}>
                <code style={{ background: '#e8edf5', padding: '2px 7px', borderRadius: 4, fontWeight: 700, color: cores.orange }}>{v.v}</code>
                <span style={{ color: cores.muted, marginLeft: 5 }}>{v.desc}</span>
              </div>
            ))}
          </div>

          {/* Assunto */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
              Assunto do Email
            </label>
            <input type="text" value={template.assunto || ''} onChange={e => setTemplate(p => ({ ...p, assunto: e.target.value }))}
              style={inp}
              onFocus={e => e.target.style.borderColor = cores.orange}
              onBlur={e => e.target.style.borderColor = cores.border} />
          </div>

          {/* Corpo */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
              Corpo do Email
            </label>
            <textarea value={template.corpo || ''} onChange={e => setTemplate(p => ({ ...p, corpo: e.target.value }))} rows={8}
              style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
              onFocus={e => e.target.style.borderColor = cores.orange}
              onBlur={e => e.target.style.borderColor = cores.border} />
            <div style={{ fontSize: 11, color: cores.muted, marginTop: 4 }}>
              Os cards de resumo (Total, Concluídos, Em aberto, Horas) e a tabela detalhada de tickets
              são adicionados automaticamente abaixo do corpo.
            </div>
          </div>

          <button onClick={handleSalvar} disabled={salvando} style={{
            padding: '10px 24px', background: salvando ? cores.border : cores.orange,
            color: salvando ? cores.muted : 'white', border: 'none', borderRadius: 8,
            fontSize: 13, fontWeight: 700, cursor: salvando ? 'not-allowed' : 'pointer',
            transition: 'background .2s',
          }}
            onMouseEnter={e => { if (!salvando) e.currentTarget.style.background = '#B84A24'; }}
            onMouseLeave={e => { if (!salvando) e.currentTarget.style.background = cores.orange; }}
          >{salvando ? '⏳ Salvando...' : '💾 Salvar Template'}</button>
        </div>
      )}
    </div>
  );
}

export default function Gestor({ aba, toast }) {
  const [usuarios, setUsuarios] = useState([]);
  const [configs, setConfigs] = useState({ km_carro: 0.65, km_moto: 0.35 });
  const [categorias, setCategorias] = useState([]);
  const [subAbaConfig, setSubAbaConfig] = useState('geral');
  const [subAbaInteg, setSubAbaInteg] = useState('asaas');
  // Módulos ativos do tenant — usa estado para poder atualizar via API
  const [modulosAtivos, setModulosAtivos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('usuario') || '{}').modulos || []; } catch { return []; }
  });

  // Clientes
  const [clientes, setClientes] = useState([]);
  const [clientesBusca, setClientesBusca] = useState('');
  const [clienteModal, setClienteModal] = useState(null); // null | 'novo' | objeto
  const [clienteForm, setClienteForm] = useState({});
  const [salvandoCliente, setSalvandoCliente] = useState(false);

  // Orçamentos config
  const [orcamentos, setOrcamentos] = useState([]);
  const [orcProximoNumero, setOrcProximoNumero] = useState('');
  const [orcNumeroInicial, setOrcNumeroInicial] = useState('');
  const [salvandoNumeroInicial, setSalvandoNumeroInicial] = useState(false);
  const [editandoOrc, setEditandoOrc] = useState(null);
  const [novoNumeroOrc, setNovoNumeroOrc] = useState('');

  // Identidade visual da empresa (PDF orçamento)
  const [empresaNome, setEmpresaNome] = useState('');
  const [empresaCNPJ, setEmpresaCNPJ] = useState('');
  const [empresaEndereco, setEmpresaEndereco] = useState('');
  const [empresaTelefone, setEmpresaTelefone] = useState('');
  const [empresaCor, setEmpresaCor] = useState('#D85A30');
  const [empresaLogo, setEmpresaLogo] = useState(''); // base64
  const [empresaLogoPreview, setEmpresaLogoPreview] = useState('');
  const [salvandoEmpresa, setSalvandoEmpresa] = useState(false);
  const [empresaLogoAltura, setEmpresaLogoAltura] = useState(70);

  // Assinatura de email
  const [emailOrcAssinaturaImg, setEmailOrcAssinaturaImg] = useState('');

  // Sócios
  const [socios, setSocios] = useState([]);
  const [novoSocio, setNovoSocio] = useState({ nome: '', percentual: '' });
  const [editandoSocio, setEditandoSocio] = useState(null);
  const [totalPercentual, setTotalPercentual] = useState(0);

  // Bancos
  const [bancos, setBancos] = useState([]);
  const [novoBanco, setNovoBanco] = useState('');
  const [novoBancoInicio, setNovoBancoInicio] = useState('');
  const [editandoBanco, setEditandoBanco] = useState(null);

  const [showModalUser, setShowModalUser] = useState(false);
  const [nuNome, setNuNome] = useState('');
  const [nuEmail, setNuEmail] = useState('');
  const [nuSenha, setNuSenha] = useState('');
  const [nuPerfis, setNuPerfis] = useState(['func']);
  const [nuDept, setNuDept] = useState('');

  const [showModalEditUser, setShowModalEditUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [euNome, setEuNome] = useState('');
  const [euEmail, setEuEmail] = useState('');
  const [euSenha, setEuSenha] = useState('');
  const [euPerfis, setEuPerfis] = useState(['func']);
  const [euDept, setEuDept] = useState('');
  const [euCor, setEuCor] = useState('#2563eb');
  const [nuCor, setNuCor] = useState('#2563eb');

  const [showModalKm, setShowModalKm] = useState(false);
  const [kmTipo, setKmTipo] = useState('');
  const [kmValor, setKmValor] = useState('');

  // MFA
  const [mfaAtivo, setMfaAtivo] = useState(false);
  const [mfaCarregado, setMfaCarregado] = useState(false);
  const [mfaSalvando, setMfaSalvando] = useState(false);

  // ASAAS (Integrações)
  const [asaasKey, setAsaasKey] = useState('');
  const [asaasAmbiente, setAsaasAmbiente] = useState('sandbox');
  const [asaasKeyVisivel, setAsaasKeyVisivel] = useState(false);
  const [asaasSalvando, setAsaasSalvando] = useState(false);
  const [asaasTeste, setAsaasTeste] = useState(null); // null | 'ok' | 'erro'
  const [asaasTestando, setAsaasTestando] = useState(false);

  // SMTP (Integrações)
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPorta, setSmtpPorta] = useState('587');
  const [smtpUsuario, setSmtpUsuario] = useState('');
  const [smtpSenha, setSmtpSenha] = useState('');
  const [smtpSenhaVisivel, setSmtpSenhaVisivel] = useState(false);
  const [smtpSeguranca, setSmtpSeguranca] = useState('tls');
  const [smtpNomeRemetente, setSmtpNomeRemetente] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('');
  const [smtpFromSenha, setSmtpFromSenha] = useState('');
  const [smtpFromSenhaVisivel, setSmtpFromSenhaVisivel] = useState(false);
  const [smtpFromNomeRemetente, setSmtpFromNomeRemetente] = useState('');
  const [smtpFromTeste, setSmtpFromTeste] = useState(null);
  const [smtpFromTestando, setSmtpFromTestando] = useState(false);
  const [smtpNota, setSmtpNota] = useState('');
  const [smtpNotaSenha, setSmtpNotaSenha] = useState('');
  const [smtpNotaSenhaVisivel, setSmtpNotaSenhaVisivel] = useState(false);
  const [smtpNotaNome, setSmtpNotaNome] = useState('');
  const [smtpNotaTeste, setSmtpNotaTeste] = useState(null);
  const [smtpNotaTestando, setSmtpNotaTestando] = useState(false);
  const [smtpSalvando, setSmtpSalvando] = useState(false);
  const [smtpTeste, setSmtpTeste] = useState(null);
  const [smtpTestando, setSmtpTestando] = useState(false);

  // Sobraa Mail
  const [sobraaDominio, setSobraaDominio] = useState('');
  const [sobraaStatus, setSobraaStatus] = useState(null); // null | objeto do banco
  const [sobraaCarregando, setSobraaCarregando] = useState(false);
  const [sobraaVerificando, setSobraaVerificando] = useState(false);
  const [sobraaAdicionando, setSobraaAdicionando] = useState(false);
  const [sobraaRemetentes, setSobraaRemetentes] = useState({ orcamento: {nome:'',email:''}, nota: {nome:'',email:''}, ticket: {nome:'',email:''} });
  const [sobraaSalvandoRem, setSobraaSalvandoRem] = useState(false);

  // WhatsApp (Integrações)
  const [wppInstancias, setWppInstancias] = useState([]);
  const [wppFilas, setWppFilas] = useState([]);
  const [wppQRModal, setWppQRModal] = useState(null);
  const [wppQRBase64, setWppQRBase64] = useState('');
  const [wppNovaInstanciaModal, setWppNovaInstanciaModal] = useState(false);
  const [wppNovaInstancia, setWppNovaInstancia] = useState({ nome: '', zapi_instance_id: '', zapi_token: '', zapi_client_token: '', fila: '' });
  const [wppNovaFilaModal, setWppNovaFilaModal] = useState(false);
  const [wppNovaFila, setWppNovaFila] = useState({ nome: '', chave: '', cor: '#2563eb', tecnicos: [] });
  const [wppEditFilaModal, setWppEditFilaModal] = useState(false);
  const [wppEditFila, setWppEditFila] = useState(null); // fila sendo editada
  const [wppLoading, setWppLoading] = useState(false);
  const wppQRIntervalRef = React.useRef(null);

  // WhatsApp — Configurações do bot e expediente
  const WPP_CONFIG_DIAS_SEMANA = [
    { v: 0, l: 'Dom' }, { v: 1, l: 'Seg' }, { v: 2, l: 'Ter' },
    { v: 3, l: 'Qua' }, { v: 4, l: 'Qui' }, { v: 5, l: 'Sex' }, { v: 6, l: 'Sáb' },
  ];
  const [wppConfig, setWppConfig] = useState({
    wpp_boas_vindas: '',
    wpp_msg_fila_selecionada: '✅ Você foi direcionado para a fila *{fila}*. Em breve um atendente irá te ajudar!',
    wpp_msg_encerramento: '',
    wpp_expediente_ativo: 'false',
    wpp_expediente_inicio: '08:00',
    wpp_expediente_fim: '18:00',
    wpp_expediente_dias: '[1,2,3,4,5]',
    wpp_msg_fora_expediente: '',
    wpp_abrir_chamado_ativo: 'false',
  });
  const [wppConfigSalvando, setWppConfigSalvando] = useState(false);
  const [wppMesasCount, setWppMesasCount] = useState(0); // contagem de mesas de trabalho, usada só para o aviso do toggle "Abrir chamado"
  const wppDiasAtivos = (() => { try { return JSON.parse(wppConfig.wpp_expediente_dias || '[1,2,3,4,5]'); } catch { return [1,2,3,4,5]; } })();
  const wppToggleDia = (dia) => {
    const atual = wppDiasAtivos;
    const novo = atual.includes(dia) ? atual.filter(d => d !== dia) : [...atual, dia].sort((a,b) => a-b);
    setWppConfig(p => ({ ...p, wpp_expediente_dias: JSON.stringify(novo) }));
  };
  const carregarWppConfig = async () => {
    try { const r = await api.get('/whatsapp/config'); setWppConfig(prev => ({ ...prev, ...r.data })); } catch {}
  };
  const salvarWppConfig = async () => {
    setWppConfigSalvando(true);
    try { await api.patch('/whatsapp/config', wppConfig); toast('Configurações do bot salvas!', 'success'); }
    catch { toast('Erro ao salvar configurações', 'error'); }
    setWppConfigSalvando(false);
  };

  // ── Adicionar ao Gestor.js após salvarWppConfig ──────────────────────────────

  const carregarFocusDados = async () => {
  try {
    const [rFiscal, rConf] = await Promise.all([
      api.get('/notas/fiscal'),
      api.get('/configuracoes'),
    ]);
    const d = rFiscal.data;
    const conf = {};
    (rConf.data || []).forEach(c => { conf[c.chave] = c.valor; });

    setFocusAmbiente(d.focus_ambiente || 'homologacao');
    setFocusCnpjCadastrado(!!d.focus_cnpj_cadastrado);
    setFocusInscricaoEstadual(d.inscricao_estadual || '');
    setFocusInscricaoMunicipal(d.inscricao_municipal || '');
    setFocusRegimeTributario(String(d.regime_tributario || '1'));
    setFocusCnae(d.cnae || '');
    setFocusCep(d.cep || '');

    // Carrega campos de endereço — prioridade: tenant (campos estruturados) > vazio
    setFocusLogradouro(d.logradouro || '');
    setFocusNumeroEndereco(d.numero_endereco || '');
    setFocusBairro(d.bairro || '');
    setFocusMunicipio(d.municipio || '');
    setFocusUF(d.uf || '');
    setFocusCodigoMunicipio(d.codigo_municipio || '');
    setFocusAliquotaSimples(d.aliquota_simples_nacional != null ? String(d.aliquota_simples_nacional) : '');
    setFocusNfseNacional(d.nfse_nacional === true || d.nfse_nacional === 't');
    setFocusRntrc(d.rntrc || '');

    // Carrega dados da empresa das configurações
    if (conf.empresa_nome)     setEmpresaNome(conf.empresa_nome);
    if (conf.empresa_cnpj)     setEmpresaCNPJ(conf.empresa_cnpj);
    if (conf.empresa_telefone) setEmpresaTelefone(conf.empresa_telefone);
    if (conf.smtp_from)        setSmtpFrom(conf.smtp_from);
    setFocusCodigoServicoPadrao(conf.nfse_codigo_servico_padrao || '');

    // Testa email de tickets automaticamente se estiver configurado
    if (conf.smtp_from) {
      api.post('/email-orcamento/testar-tickets')
        .then(() => setSmtpFromTeste('ok'))
        .catch(() => setSmtpFromTeste('erro'));
    }
  } catch {}
  };

  const salvarFocus = async () => {
  setFocusSalvando(true);
  try {
    // Garante o código IBGE mesmo quando o CEP já veio preenchido do banco
    // (carregado ao abrir a tela) e nunca foi redigitado — o autofill de
    // buscarCepFocus só dispara no onChange do campo, não ao carregar dados.
    let codigoMunicipioEfetivo = focusCodigoMunicipio;
    const cepLimpoFocus = (focusCep || '').replace(/\D/g, '');
    if (!codigoMunicipioEfetivo && cepLimpoFocus.length === 8) {
      try {
        const rCep = await api.get(`/utils/cep/${cepLimpoFocus}`);
        if (rCep.data?.ibge) {
          codigoMunicipioEfetivo = rCep.data.ibge;
          setFocusCodigoMunicipio(rCep.data.ibge);
        }
      } catch { /* segue sem IBGE — validação abaixo avisa o usuário */ }
    }
    if (!codigoMunicipioEfetivo) {
      toast('Atenção: não foi possível obter o Código IBGE automaticamente. Preencha manualmente se for emitir NFS-e.', 'error');
    }

    // 1. Salva todos os dados fiscais no banco (tenant)
    await api.put('/notas/fiscal', {
      inscricao_estadual: focusInscricaoEstadual,
      inscricao_municipal: focusInscricaoMunicipal,
      regime_tributario: focusRegimeTributario,
      cnae: focusCnae,
      logradouro: focusLogradouro,
      numero_endereco: focusNumeroEndereco,
      bairro: focusBairro,
      municipio: focusMunicipio,
      uf: focusUF,
      cep: focusCep,
      codigo_municipio: codigoMunicipioEfetivo,
      focus_ambiente: focusAmbiente,
      aliquota_simples_nacional: focusAliquotaSimples || null,
      nfse_nacional: focusNfseNacional,
      rntrc: focusRntrc || null,
    });

    // 2. Monta endereço completo para o PDF do orçamento
    const partes = [
      focusLogradouro,
      focusNumeroEndereco ? `nº ${focusNumeroEndereco}` : '',
      focusBairro,
      focusMunicipio && focusUF ? `${focusMunicipio} - ${focusUF}` : focusMunicipio || focusUF,
      focusCep ? `CEP: ${focusCep}` : '',
    ].filter(Boolean);
    const enderecoCompleto = partes.join(', ');

    // 3. Salva nas configuracoes (usadas pelo PDF de orçamento)
    await Promise.all([
      api.patch('/configuracoes/empresa_nome',     { valor: empresaNome }),
      api.patch('/configuracoes/empresa_cnpj',     { valor: empresaCNPJ }),
      api.patch('/configuracoes/empresa_telefone', { valor: empresaTelefone }),
      api.patch('/configuracoes/empresa_endereco', { valor: enderecoCompleto }),
      // Código de serviço padrão da NFS-e — só grava se já foi carregado do banco
      ...(focusCodigoServicoPadrao !== null
        ? [api.patch('/configuracoes/nfse_codigo_servico_padrao', { valor: focusCodigoServicoPadrao.trim() })]
        : []),
    ]);

    // 4. Se houver certificado selecionado, salva no banco antes de sincronizar
    if (focusCertArquivo && focusCertSenha) {
      try {
        const base64 = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = ev => res(ev.target.result.split(',')[1]);
          reader.onerror = () => rej(new Error('Falha ao ler arquivo'));
          reader.readAsDataURL(focusCertArquivo);
        });
        await api.post('/notas/certificado', {
          certificado_base64: base64,
          certificado_senha: focusCertSenha,
        });
        setFocusCertArquivo(null);
        setFocusCertSenha('');
      } catch {
        toast('Atenção: erro ao salvar certificado. Os demais dados foram salvos.', 'error');
      }
    }

    // 5. Sincroniza automaticamente com a Focus NFe (cadastra ou atualiza a empresa)
    try {
      const r = await api.post('/notas/fiscal/sincronizar');
      if (r.data?.sucesso) {
        setFocusCnpjCadastrado(true);
        carregarInfoCertificado();
        toast('Dados fiscais salvos e empresa sincronizada! ✅', 'success');
      } else {
        toast('Dados salvos, mas não foi possível sincronizar a NFe.', 'error');
      }
    } catch (syncErr) {
      const msgErro = syncErr.response?.data?.erro || 'Erro ao sincronizar NFe.';
      // Dados já foram salvos — avisa mas não falha o fluxo inteiro
      toast(`Dados salvos. Atenção: ${msgErro}`, 'error');
    }

  } catch { toast('Erro ao salvar dados fiscais', 'error'); }
  setFocusSalvando(false);
  };


  const buscarCepFocus = async (cepRaw) => {
  const cepLimpo = cepRaw.replace(/\D/g, '');
  setFocusCep(cepRaw);
  if (cepLimpo.length !== 8) return;
  setFocusBuscandoCep(true);
  try {
    const r = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
    const d = await r.json();
    if (!d.erro) {
      setFocusLogradouro(d.logradouro || '');
      setFocusBairro(d.bairro || '');
      setFocusMunicipio(d.localidade || '');
      setFocusUF(d.uf || '');
      if (d.ibge) setFocusCodigoMunicipio(d.ibge);
    }
  } catch { /* silencioso */ }
  setFocusBuscandoCep(false);
  };

  // Focus NFe
  const [focusAmbiente, setFocusAmbiente] = useState('homologacao');
  const [focusCnpjCadastrado, setFocusCnpjCadastrado] = useState(false);
  const [focusInscricaoEstadual, setFocusInscricaoEstadual] = useState('');
  const [focusInscricaoMunicipal, setFocusInscricaoMunicipal] = useState('');
  const [focusRegimeTributario, setFocusRegimeTributario] = useState('1');
  const [focusCnae, setFocusCnae] = useState('');
  const [focusLogradouro, setFocusLogradouro] = useState('');
  const [focusNumeroEndereco, setFocusNumeroEndereco] = useState('');
  const [focusBairro, setFocusBairro] = useState('');
  const [focusMunicipio, setFocusMunicipio] = useState('');
  const [focusUF, setFocusUF] = useState('');
  const [focusCodigoMunicipio, setFocusCodigoMunicipio] = useState('');
  const [focusAliquotaSimples, setFocusAliquotaSimples] = useState('');
  const [focusNfseNacional, setFocusNfseNacional] = useState(false);
  const [focusRntrc, setFocusRntrc] = useState(''); // RNTRC da transportadora — usado na emissão de CT-e
  // Código de serviço padrão (LC 116) da NFS-e — salvo em configuracoes (chave nfse_codigo_servico_padrao).
  // null = ainda não carregado: evita sobrescrever o valor salvo com vazio se o usuário salvar antes do load.
  const [focusCodigoServicoPadrao, setFocusCodigoServicoPadrao] = useState(null);
  const [focusCep, setFocusCep] = useState('');
  const [focusCertSenha, setFocusCertSenha] = useState('');
  const [focusCertArquivo, setFocusCertArquivo] = useState(null);
  const [focusCertValidoAte, setFocusCertValidoAte] = useState(null);
  const [focusCertValidoDe, setFocusCertValidoDe] = useState(null);
  const [focusCertCnpj, setFocusCertCnpj] = useState(null);
  const [focusCertCarregando, setFocusCertCarregando] = useState(false);
  const [focusSalvando, setFocusSalvando] = useState(false);
  const [focusBuscandoCep, setFocusBuscandoCep] = useState(false);

  // Email template orçamento
  const [emailOrcAssunto, setEmailOrcAssunto] = useState('Orçamento Nº {{numero}} — {{cliente}}');
  const [emailOrcCorpo, setEmailOrcCorpo] = useState('Olá, {{cliente}}!\n\nSegue em anexo o orçamento Nº {{numero}} no valor de {{valor}}.\n\nQualquer dúvida, estamos à disposição.\n\nAtenciosamente.');
  const [emailOrcSalvando, setEmailOrcSalvando] = useState(false);

  // Paginação
  const POR_PAGINA = 10;
  const [paginaOrc, setPaginaOrc] = useState(1);
  const [paginaCli, setPaginaCli] = useState(1);

  const [showModalCat, setShowModalCat] = useState(false);
  const [catNome, setCatNome] = useState('');
  const [catIcone, setCatIcone] = useState('');
  const [catTipo, setCatTipo] = useState('valor');

  useEffect(() => {
    carregarUsuarios();
    carregarConfigs();
    carregarCategorias();
    carregarOrcamentos();
    carregarClientes();
    carregarSocios();
    carregarBancos();
    carregarEmpresa();
    carregarMFA();
    carregarWpp();
    carregarFocus();
    carregarFocusDados();
    carregarInfoCertificado();
    // Atualiza módulos ativos buscando do backend (token pode estar desatualizado
    // se o admin ativou/desativou módulos sem novo login)
    api.get('/auth/me').then(r => {
      const modulos = r.data?.modulos || [];
      setModulosAtivos(modulos);
      try {
        const u = JSON.parse(localStorage.getItem('usuario') || '{}');
        localStorage.setItem('usuario', JSON.stringify({ ...u, modulos }));
      } catch {}
    }).catch(() => {
      // fallback: lê do token JWT se a rota falhar
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.modulos?.length) {
            setModulosAtivos(payload.modulos);
          }
        }
      } catch {}
    });
  }, []);

  useEffect(() => {
    const total = socios.reduce((s, sc) => s + Number(sc.percentual || 0), 0);
    setTotalPercentual(total);
  }, [socios]);

  const carregarUsuarios = async () => {
    try { const r = await api.get('/usuarios'); setUsuarios(r.data); } catch {}
  };

  const carregarConfigs = async () => {
    try {
      const r = await api.get('/configuracoes');
      const c = {};
      r.data.forEach(x => { c[x.chave] = parseFloat(x.valor); });
      setConfigs(c);
    } catch {}
  };

  const carregarCategorias = async () => {
    try { const r = await api.get('/configuracoes/categorias'); setCategorias(r.data); } catch {}
  };

  const carregarClientes = async () => {
    try {
      const res = await api.get('/orcamentos/clientes/todos');
      setClientes(res.data);
    } catch { toast('Erro ao carregar clientes.', 'error'); }
  };

  const salvarCliente = async () => {
    if (!clienteForm.nome?.trim()) { toast('Nome é obrigatório.', 'error'); return; }
    setSalvandoCliente(true);
    try {
      // Limpar campos internos do frontend antes de enviar
      const { _cnpjStatus, _cepStatus, id, ...dadosLimpos } = clienteForm;
      // Extrai email principal da lista emails_adicionais para o campo email da tabela
      try {
        const lista = JSON.parse(dadosLimpos.emails_adicionais || '[]');
        const principal = lista.find(e => e.principal && e.email);
        if (principal) dadosLimpos.email = principal.email;
      } catch {}
      if (clienteModal === 'novo') {
        await api.post('/orcamentos/clientes', dadosLimpos);
        toast('Cliente adicionado!', 'success');
      } else {
        const idCliente = clienteModal.id || clienteModal;
        await api.patch(`/orcamentos/clientes/${idCliente}`, dadosLimpos);
        toast('Cliente atualizado!', 'success');
      }
      setClienteModal(null);
      setClienteForm({});
      carregarClientes();
    } catch (e) {
      console.error('Erro ao salvar cliente:', e);
      toast('Erro ao salvar cliente.', 'error');
    }
    finally { setSalvandoCliente(false); }
  };

  const excluirCliente = async (id) => {
    if (!window.confirm('Excluir este cliente?')) return;
    try {
      await api.delete(`/orcamentos/clientes/${id}`);
      toast('Cliente excluído.', 'success');
      carregarClientes();
    } catch { toast('Erro ao excluir cliente.', 'error'); }
  };

  const carregarOrcamentos = async () => {
    try {
      const [listaRes, proximoRes] = await Promise.all([
        api.get('/orcamentos'),
        api.get('/orcamentos/util/proximo-numero'),
      ]);
      setOrcamentos(listaRes.data);
      setOrcProximoNumero(proximoRes.data.numero);
    } catch {}
  };

  const carregarSocios = async () => {
    try { const r = await api.get('/fechamento/socios'); setSocios(r.data); } catch {}
  };

  const carregarBancos = async () => {
    try { const r = await api.get('/fechamento/bancos'); setBancos(r.data); } catch {}
  };


  const carregarMFA = async () => {
    try {
      // Pega o tenant_id do token JWT armazenado
      const token = localStorage.getItem('token');
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const { data } = await api.get(`/auth/mfa/status?tenant_id=${payload.tenant_id}`);
      setMfaAtivo(data.mfa_ativo);
    } catch {}
    setMfaCarregado(true);
  };

  const toggleMFA = async () => {
    setMfaSalvando(true);
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const { data } = await api.patch('/auth/mfa/toggle', {
        tenant_id: payload.tenant_id,
        ativo: !mfaAtivo,
      });
      setMfaAtivo(data.mfa_ativo);
      toast(data.mfa_ativo ? 'MFA ativado com sucesso!' : 'MFA desativado.', 'success');
    } catch { toast('Erro ao alterar MFA.', 'error'); }
    setMfaSalvando(false);
  };

  const carregarEmpresa = async () => {
    try {
      const r = await api.get("/configuracoes");
      const c = {};
      r.data.forEach(x => { c[x.chave] = x.valor; });
      if (c.empresa_nome)     setEmpresaNome(c.empresa_nome);
      if (c.empresa_cnpj)     setEmpresaCNPJ(c.empresa_cnpj);
      if (c.empresa_endereco) setEmpresaEndereco(c.empresa_endereco);
      if (c.empresa_telefone) setEmpresaTelefone(c.empresa_telefone);
      if (c.empresa_cor)      setEmpresaCor(c.empresa_cor);
      if (c.empresa_logo)     { setEmpresaLogo(c.empresa_logo); setEmpresaLogoPreview(c.empresa_logo); }
      if (c.empresa_logo_altura) setEmpresaLogoAltura(Number(c.empresa_logo_altura) || 70);
      if (c.email_orc_assinatura_img !== undefined) setEmailOrcAssinaturaImg(c.email_orc_assinatura_img || '');
      if (c.asaas_api_key)    setAsaasKey(c.asaas_api_key);
      if (c.asaas_ambiente)   setAsaasAmbiente(c.asaas_ambiente);
      if (c.smtp_host)           setSmtpHost(c.smtp_host);
      if (c.smtp_porta)          setSmtpPorta(c.smtp_porta);
      if (c.smtp_usuario)        setSmtpUsuario(c.smtp_usuario);
      if (c.smtp_senha)          setSmtpSenha(c.smtp_senha);
      if (c.smtp_seguranca)      setSmtpSeguranca(c.smtp_seguranca);
      if (c.smtp_nome_remetente) setSmtpNomeRemetente(c.smtp_nome_remetente);
      if (c.smtp_from)           setSmtpFrom(c.smtp_from);
      if (c.smtp_from_senha)     setSmtpFromSenha(c.smtp_from_senha);
      if (c.smtp_from_nome)      setSmtpFromNomeRemetente(c.smtp_from_nome);
      if (c.smtp_nota_email)     setSmtpNota(c.smtp_nota_email);
      if (c.smtp_nota_senha)     setSmtpNotaSenha(c.smtp_nota_senha);
      if (c.smtp_nota_nome)      setSmtpNotaNome(c.smtp_nota_nome);
      if (c.email_orc_assunto)   setEmailOrcAssunto(c.email_orc_assunto);
      if (c.email_orc_corpo)     setEmailOrcCorpo(c.email_orc_corpo);

      // Testa conexões automaticamente em background se já estiverem configuradas
      if (c.asaas_api_key) {
        api.post('/cobranca/testar-conexao')
          .then(() => setAsaasTeste('ok'))
          .catch(() => setAsaasTeste('erro'));
      }
      if (c.smtp_host && c.smtp_usuario && c.smtp_senha) {
        api.post('/email-orcamento/testar')
          .then(() => setSmtpTeste('ok'))
          .catch(() => setSmtpTeste('erro'));
        // Testa email de tickets se estiver configurado
        if (c.smtp_from) {
          api.post('/email-orcamento/testar-tickets')
            .then(() => setSmtpFromTeste('ok'))
            .catch(() => setSmtpFromTeste('erro'));
        }
        // Testa email de notas se estiver configurado
        // Carrega Sobraa Mail
      try {
        const smR = await api.get('/email-dominios').catch(() => ({ data: null }));
        if (smR.data) {
          setSobraaStatus(smR.data);
          setSobraaDominio(smR.data.dominio || '');
        }
        const remR = await api.get('/email-remetentes').catch(() => ({ data: [] }));
        const remMap = { orcamento:{nome:'',email:''}, nota:{nome:'',email:''}, ticket:{nome:'',email:''} };
        (remR.data || []).forEach(r => {
          if (remMap[r.tipo] !== undefined) remMap[r.tipo] = { nome: r.nome, email: r.email };
        });
        setSobraaRemetentes(remMap);
      } catch {}

      if (c.smtp_nota_email) {
          api.post('/email-orcamento/testar-notas')
            .then(() => setSmtpNotaTeste('ok'))
            .catch(() => setSmtpNotaTeste('erro'));
        }
      }
    } catch {}
  };

  const salvarEmpresa = async () => {
    setSalvandoEmpresa(true);
    try {
      const campos = [
        { chave: "empresa_nome",     valor: empresaNome },
        { chave: "empresa_cnpj",     valor: empresaCNPJ },
        { chave: "empresa_endereco", valor: empresaEndereco },
        { chave: "empresa_telefone", valor: empresaTelefone },
        { chave: "empresa_cor",      valor: empresaCor },
        { chave: "empresa_logo",     valor: empresaLogo },
        { chave: "empresa_logo_altura",  valor: String(empresaLogoAltura) },
      ];
      await Promise.all(campos.map(({ chave, valor }) =>
        api.patch(`/configuracoes/${chave}`, { valor })
      ));
      toast("Identidade visual salva!", "success");
    } catch { toast("Erro ao salvar.", "error"); }
    setSalvandoEmpresa(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500000) { toast("Logo muito grande. Use uma imagem ate 500KB.", "error"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setEmpresaLogo(ev.target.result);
      setEmpresaLogoPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const criarUsuario = async () => {
    if (!nuNome || !nuEmail || !nuSenha) { toast('Preencha todos os campos', 'error'); return; }
    try {
      await api.post('/usuarios', { nome: nuNome, email: nuEmail, senha: nuSenha, perfil: nuPerfis[0], perfis: nuPerfis, departamento: nuDept, cor: nuCor });
      toast('Usuario criado!', 'success');
      setShowModalUser(false);
      setNuNome(''); setNuEmail(''); setNuSenha(''); setNuDept(''); setNuPerfis(['func']);
      carregarUsuarios();
    } catch { toast('Erro ao criar usuario.', 'error'); }
  };

  const removerUsuario = async (id) => {
    if (!window.confirm('Remover este usuario?')) return;
    try {
      await api.delete(`/usuarios/${id}`);
      toast('Usuario removido', 'success');
      carregarUsuarios();
    } catch { toast('Erro ao remover', 'error'); }
  };

  const abrirEdicao = (u) => {
    setEditUser(u);
    setEuNome(u.nome);
    setEuEmail(u.email);
    setEuPerfis(u.perfis || [u.perfil]);
    setEuDept(u.departamento || '');
    setEuSenha('');
    setEuCor(u.cor || '#2563eb');
    setShowModalEditUser(true);
  };

  const salvarEdicao = async () => {
    if (!euNome || !euEmail) { toast('Preencha todos os campos', 'error'); return; }
    try {
      const body = { nome: euNome, email: euEmail, perfil: euPerfis[0], perfis: euPerfis, departamento: euDept, cor: euCor };
      if (euSenha) body.senha = euSenha;
      await api.patch(`/usuarios/${editUser.id}`, body);
      toast('Usuario atualizado!', 'success');
      setShowModalEditUser(false);
      carregarUsuarios();
    } catch { toast('Erro ao atualizar.', 'error'); }
  };

  const editarKm = (tipo) => { setKmTipo(tipo); setKmValor(configs[`km_${tipo}`] || ''); setShowModalKm(true); };

  const salvarKm = async () => {
    try {
      await api.patch(`/configuracoes/km_${kmTipo}`, { valor: kmValor });
      toast('KM atualizado!', 'success');
      setShowModalKm(false);
      carregarConfigs();
    } catch { toast('Erro ao salvar.', 'error'); }
  };

  const removerCategoria = async (id) => {
    try {
      await api.delete(`/configuracoes/categorias/${id}`);
      toast('Categoria removida', 'success');
      carregarCategorias();
    } catch { toast('Erro ao remover', 'error'); }
  };

  const criarCategoria = async () => {
    if (!catNome) return;
    try {
      await api.post('/configuracoes/categorias', { nome: catNome, icone: catIcone || 'other', tipo: catTipo });
      toast('Categoria criada!', 'success');
      setShowModalCat(false);
      setCatNome(''); setCatIcone(''); setCatTipo('valor');
      carregarCategorias();
    } catch { toast('Erro ao criar.', 'error'); }
  };

  const definirNumeroInicial = async () => {
    if (!orcNumeroInicial) return;
    setSalvandoNumeroInicial(true);
    try {
      await api.post('/orcamentos/util/numero-inicial', { numero: orcNumeroInicial });
      toast('Número inicial definido!', 'success');
      setOrcNumeroInicial('');
      carregarOrcamentos();
    } catch { toast('Erro ao definir número.', 'error'); }
    setSalvandoNumeroInicial(false);
  };

  const salvarNumeroOrc = async (id) => {
    try {
      await api.patch(`/orcamentos/${id}`, { numero: novoNumeroOrc });
      toast('Número atualizado!', 'success');
      setEditandoOrc(null);
      carregarOrcamentos();
    } catch { toast('Erro ao atualizar número.', 'error'); }
  };

  const adicionarSocio = async () => {
    if (!novoSocio.nome || !novoSocio.percentual) { toast('Preencha nome e percentual', 'error'); return; }
    if (totalPercentual + Number(novoSocio.percentual) > 100) { toast('Total de percentuais não pode ultrapassar 100%', 'error'); return; }
    try {
      await api.post('/fechamento/socios', novoSocio);
      toast('Sócio adicionado!', 'success');
      setNovoSocio({ nome: '', percentual: '' });
      carregarSocios();
    } catch { toast('Erro ao adicionar sócio.', 'error'); }
  };

  const salvarEdicaoSocio = async (id) => {
    try {
      await api.patch(`/fechamento/socios/${id}`, { nome: editandoSocio.nome, percentual: editandoSocio.percentual });
      toast('Sócio atualizado!', 'success');
      setEditandoSocio(null);
      carregarSocios();
    } catch { toast('Erro ao atualizar sócio.', 'error'); }
  };

  const removerSocio = async (id) => {
    try {
      await api.patch(`/fechamento/socios/${id}`, { ativo: false });
      toast('Sócio removido!', 'success');
      carregarSocios();
    } catch { toast('Erro ao remover sócio.', 'error'); }
  };

  const adicionarBanco = async () => {
    if (!novoBanco.trim()) return;
    try {
      await api.post('/fechamento/bancos', { nome: novoBanco, data_inicio: novoBancoInicio || null });
      toast('Banco adicionado!', 'success');
      setNovoBanco('');
      setNovoBancoInicio('');
      carregarBancos();
    } catch { toast('Erro ao adicionar banco.', 'error'); }
  };

  const salvarEdicaoBanco = async (id) => {
    try {
      await api.patch(`/fechamento/bancos/${id}`, { nome: editandoBanco.nome });
      toast('Banco atualizado!', 'success');
      setEditandoBanco(null);
      carregarBancos();
    } catch { toast('Erro ao atualizar banco.', 'error'); }
  };

  // Encerra a partir de hoje em vez de desativar — mantém a conta no histórico
  // dos meses já fechados (Fechamento), só some dos meses futuros.
  const encerrarBanco = async (id) => {
    try {
      const hoje = new Date().toISOString().substring(0, 10);
      await api.patch(`/fechamento/bancos/${id}`, { data_fim: hoje });
      toast('Conta encerrada a partir de hoje.', 'success');
      carregarBancos();
    } catch { toast('Erro ao encerrar conta.', 'error'); }
  };

  const salvarAsaas = async () => {
    setAsaasSalvando(true);
    try {
      await api.patch('/configuracoes/asaas_api_key', { valor: asaasKey });
      await api.patch('/configuracoes/asaas_ambiente', { valor: asaasAmbiente });
      toast('Configurações ASAAS salvas!', 'success');
      // Testa automaticamente após salvar
      api.post('/cobranca/testar-conexao')
        .then(() => setAsaasTeste('ok'))
        .catch(() => setAsaasTeste('erro'));
    } catch { toast('Erro ao salvar.', 'error'); }
    setAsaasSalvando(false);
  };

  const testarAsaas = async () => {
    setAsaasTestando(true);
    setAsaasTeste(null);
    try {
      await api.post('/cobranca/testar-conexao');
      setAsaasTeste('ok');
    } catch {
      setAsaasTeste('erro');
    }
    setAsaasTestando(false);
  };

  // Componente de paginação inline
  const Paginator = ({ pagina, setPagina, total, porPagina }) => {
    const totalPags = Math.ceil(total / porPagina);
    if (totalPags <= 1) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${cores.border}` }}>
        <div style={{ fontSize: 12, color: cores.muted }}>
          Página {pagina} de {totalPags} · {total} itens
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setPagina(1)} disabled={pagina === 1}
            style={{ padding: '5px 10px', border: `1px solid ${cores.border}`, borderRadius: 6, background: 'white', cursor: pagina === 1 ? 'default' : 'pointer', color: pagina === 1 ? cores.muted : '#1a2233', fontSize: 12, fontWeight: 600 }}>«</button>
          <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
            style={{ padding: '5px 10px', border: `1px solid ${cores.border}`, borderRadius: 6, background: 'white', cursor: pagina === 1 ? 'default' : 'pointer', color: pagina === 1 ? cores.muted : '#1a2233', fontSize: 12, fontWeight: 600 }}>‹</button>
          {Array.from({ length: totalPags }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPags || Math.abs(p - pagina) <= 1)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) => p === '...' ? (
              <span key={`e${i}`} style={{ padding: '5px 8px', fontSize: 12, color: cores.muted }}>…</span>
            ) : (
              <button key={p} onClick={() => setPagina(p)}
                style={{ padding: '5px 10px', border: `1px solid ${p === pagina ? cores.orange : cores.border}`, borderRadius: 6, background: p === pagina ? cores.orange : 'white', color: p === pagina ? 'white' : '#1a2233', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>{p}</button>
            ))}
          <button onClick={() => setPagina(p => Math.min(totalPags, p + 1))} disabled={pagina === totalPags}
            style={{ padding: '5px 10px', border: `1px solid ${cores.border}`, borderRadius: 6, background: 'white', cursor: pagina === totalPags ? 'default' : 'pointer', color: pagina === totalPags ? cores.muted : '#1a2233', fontSize: 12, fontWeight: 600 }}>›</button>
          <button onClick={() => setPagina(totalPags)} disabled={pagina === totalPags}
            style={{ padding: '5px 10px', border: `1px solid ${cores.border}`, borderRadius: 6, background: 'white', cursor: pagina === totalPags ? 'default' : 'pointer', color: pagina === totalPags ? cores.muted : '#1a2233', fontSize: 12, fontWeight: 600 }}>»</button>
        </div>
      </div>
    );
  };

  // ── Funções Sobraa Mail ─────────────────────────────────────────────────────
  const adicionarDominio = async () => {
    if (!sobraaDominio.trim()) return;
    setSobraaAdicionando(true);
    try {
      const r = await api.post('/email-dominios/adicionar', { dominio: sobraaDominio.trim() });
      setSobraaStatus(r.data);
      toast('Domínio adicionado! Adicione os registros DNS abaixo.', 'success');
    } catch (e) {
      toast(e.response?.data?.erro || 'Erro ao adicionar domínio.', 'error');
    }
    setSobraaAdicionando(false);
  };

  const verificarDominio = async () => {
    setSobraaVerificando(true);
    try {
      const r = await api.post('/email-dominios/verificar');
      setSobraaStatus(r.data);
      if (r.data.status === 'ativo') {
        toast('✅ Domínio verificado! Sobraa Mail ativo.', 'success');
      } else {
        toast('DNS ainda não propagado. Aguarde alguns minutos e tente novamente.', 'error');
      }
    } catch (e) {
      toast(e.response?.data?.erro || 'Erro ao verificar domínio.', 'error');
    }
    setSobraaVerificando(false);
  };

  const removerDominio = async () => {
    if (!window.confirm('Remover domínio do Sobraa Mail? O envio voltará a usar SMTP próprio.')) return;
    try {
      await api.delete('/email-dominios');
      setSobraaStatus(null);
      setSobraaDominio('');
      toast('Domínio removido.', 'success');
    } catch {
      toast('Erro ao remover domínio.', 'error');
    }
  };

  const salvarRemetentes = async () => {
    setSobraaSalvandoRem(true);
    try {
      const tipos = ['orcamento','nota','ticket'];
      for (const tipo of tipos) {
        const r = sobraaRemetentes[tipo];
        if (r.email && r.nome) {
          await api.post('/email-remetentes', { tipo, nome: r.nome, email: r.email });
        }
      }
      toast('Remetentes salvos!', 'success');
    } catch {
      toast('Erro ao salvar remetentes.', 'error');
    }
    setSobraaSalvandoRem(false);
  };

  const salvarSmtp = async () => {
    setSmtpSalvando(true);
    try {
      await api.patch('/configuracoes/smtp_host',           { valor: smtpHost });
      await api.patch('/configuracoes/smtp_porta',          { valor: smtpPorta });
      await api.patch('/configuracoes/smtp_usuario',        { valor: smtpUsuario });
      await api.patch('/configuracoes/smtp_senha',          { valor: smtpSenha });
      await api.patch('/configuracoes/smtp_seguranca',      { valor: smtpSeguranca });
      await api.patch('/configuracoes/smtp_nome_remetente', { valor: smtpNomeRemetente });
      await api.patch('/configuracoes/smtp_from',           { valor: smtpFrom });
      await api.patch('/configuracoes/smtp_from_senha',     { valor: smtpFromSenha });
      await api.patch('/configuracoes/smtp_from_nome',      { valor: smtpFromNomeRemetente });
      await api.patch('/configuracoes/smtp_nota_email',     { valor: smtpNota });
      await api.patch('/configuracoes/smtp_nota_senha',     { valor: smtpNotaSenha });
      await api.patch('/configuracoes/smtp_nota_nome',      { valor: smtpNotaNome });
      toast('Configurações SMTP salvas!', 'success');
      api.post('/email-orcamento/testar')
        .then(() => setSmtpTeste('ok'))
        .catch(() => setSmtpTeste('erro'));
      if (smtpFrom) {
        api.post('/email-orcamento/testar-tickets')
          .then(() => setSmtpFromTeste('ok'))
          .catch(() => setSmtpFromTeste('erro'));
      }
      if (smtpNota) {
        api.post('/email-orcamento/testar-notas')
          .then(() => setSmtpNotaTeste('ok'))
          .catch(() => setSmtpNotaTeste('erro'));
      }
    } catch { toast('Erro ao salvar SMTP.', 'error'); }
    setSmtpSalvando(false);
  };

  const testarSmtp = async () => {
    setSmtpTestando(true);
    setSmtpTeste(null);
    try {
      await api.post('/email-orcamento/testar');
      setSmtpTeste('ok');
    } catch { setSmtpTeste('erro'); }
    setSmtpTestando(false);
  };

  const testarSmtpTickets = async () => {
    setSmtpFromTestando(true);
    setSmtpFromTeste(null);
    try {
      await api.post('/email-orcamento/testar-tickets');
      setSmtpFromTeste('ok');
    } catch { setSmtpFromTeste('erro'); }
    setSmtpFromTestando(false);
  };

  const testarSmtpNotas = async () => {
    setSmtpNotaTestando(true);
    setSmtpNotaTeste(null);
    try {
      await api.post('/email-orcamento/testar-notas');
      setSmtpNotaTeste('ok');
    } catch { setSmtpNotaTeste('erro'); }
    setSmtpNotaTestando(false);
  };

  const salvarEmailOrc = async () => {
    setEmailOrcSalvando(true);
    try {
      await api.patch('/configuracoes/email_orc_assunto',       { valor: emailOrcAssunto });
      await api.patch('/configuracoes/email_orc_corpo',          { valor: emailOrcCorpo });
      await api.patch('/configuracoes/email_orc_assinatura_img', { valor: emailOrcAssinaturaImg });
      toast('Template de email salvo!', 'success');
    } catch { toast('Erro ao salvar template.', 'error'); }
    setEmailOrcSalvando(false);
  };

  const cardStyle = { background: 'white', borderRadius: 16, border: `1px solid ${cores.border}`, padding: '20px 16px', marginBottom: 16 };

  // ── Funções WhatsApp ──────────────────────────────────────────
  const carregarWpp = useCallback(async () => {
    try {
      const [instR, filasR] = await Promise.all([
        api.get('/whatsapp/instancias').catch(() => ({ data: [] })),
        api.get('/whatsapp/filas').catch(() => ({ data: [] })),
      ]);
      setWppInstancias(instR.data);
      setWppFilas(filasR.data);
    } catch {}
    carregarWppConfig();
    // Contagem de mesas de trabalho — só usada para o aviso do toggle "Abrir chamado pelo bot"
    if (modulosAtivos.includes('tkt')) {
      try { const m = await api.get('/tickets/mesas'); setWppMesasCount((m.data || []).length); }
      catch { setWppMesasCount(0); }
    }
  }, [modulosAtivos]);

  const carregarFocus = async () => {
  try {
    const r = await api.get('/notas/fiscal');
    const d = r.data;
    setFocusAmbiente(d.focus_ambiente || 'homologacao');
    setFocusCnpjCadastrado(!!d.focus_cnpj_cadastrado);
    setFocusInscricaoEstadual(d.inscricao_estadual || '');
    setFocusInscricaoMunicipal(d.inscricao_municipal || '');
    setFocusRegimeTributario(String(d.regime_tributario || '1'));
    setFocusCnae(d.cnae || '');
    setFocusLogradouro(d.logradouro || '');
    setFocusNumeroEndereco(d.numero_endereco || '');
    setFocusBairro(d.bairro || '');
    setFocusMunicipio(d.municipio || '');
    setFocusUF(d.uf || '');
    setFocusCodigoMunicipio(d.codigo_municipio || '');
    setFocusAliquotaSimples(d.aliquota_simples_nacional != null ? String(d.aliquota_simples_nacional) : '');
    setFocusNfseNacional(d.nfse_nacional === true || d.nfse_nacional === 't');
    setFocusCep(d.cep || '');
    setFocusRntrc(d.rntrc || '');
  } catch {}
  };

  const carregarInfoCertificado = async () => {
    setFocusCertCarregando(true);
    try {
      const r = await api.get('/notas/fiscal/certificado-info');
      setFocusCertValidoAte(r.data.certificado_valido_ate || null);
      setFocusCertValidoDe(r.data.certificado_valido_de || null);
      setFocusCertCnpj(r.data.certificado_cnpj || null);
    } catch {}
    setFocusCertCarregando(false);
  };

  const abrirQRWpp = async (inst) => {
    // Limpa qualquer polling anterior antes de abrir novo modal
    if (wppQRIntervalRef.current) {
      clearInterval(wppQRIntervalRef.current);
      wppQRIntervalRef.current = null;
    }
    setWppQRModal(inst);
    setWppQRBase64('');

    const buscarQR = async () => {
      try {
        const r = await api.get(`/whatsapp/instancias/${inst.id}/qrcode`);
        // Já conectado — fecha o modal automaticamente
        if (r.data.conectado) {
          fecharQRWpp();
          toast('WhatsApp conectado com sucesso! ✅', 'success');
          carregarWpp();
          return;
        }
        if (r.data.base64) setWppQRBase64(r.data.base64);
      } catch {}
    };

    // Busca o QR imediatamente
    buscarQR();

    // Polling de STATUS a cada 4s para detectar quando o usuário escanear
    wppQRIntervalRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/whatsapp/instancias/${inst.id}/status`);
        if (r.data.status === 'conectado') {
          fecharQRWpp();
          toast('WhatsApp conectado com sucesso! ✅', 'success');
          carregarWpp();
          return;
        }
      } catch {}
    }, 4000);

    // Renova o QR Code a cada 55s (ele expira em ~60s na Evolution API)
    setTimeout(async () => {
      if (wppQRIntervalRef.current) {
        buscarQR();
      }
    }, 55000);
  };

  const fecharQRWpp = () => {
    setWppQRModal(null);
    setWppQRBase64('');
    if (wppQRIntervalRef.current) { clearInterval(wppQRIntervalRef.current); wppQRIntervalRef.current = null; }
  };

  const criarInstanciaWpp = async () => {
    if (!wppNovaInstancia.nome || !wppNovaInstancia.zapi_instance_id || !wppNovaInstancia.zapi_token || !wppNovaInstancia.zapi_client_token) {
      toast('Todos os campos são obrigatórios.', 'error'); return;
    }
    setWppLoading(true);
    try {
      await api.post('/whatsapp/instancias', {
        nome: wppNovaInstancia.nome,
        zapi_instance_id: wppNovaInstancia.zapi_instance_id,
        zapi_token: wppNovaInstancia.zapi_token,
        zapi_client_token: wppNovaInstancia.zapi_client_token,
        fila: wppNovaInstancia.fila || (wppFilas[0]?.chave) || 'geral',
      });
      toast('Instância salva! Clique em Conectar QR para escanear.', 'success');
      setWppNovaInstanciaModal(false);
      setWppNovaInstancia({ nome: '', zapi_instance_id: '', zapi_token: '', zapi_client_token: '', fila: '' });
      carregarWpp();
    } catch (e) { toast(e.response?.data?.erro || 'Erro ao salvar instância', 'error'); }
    setWppLoading(false);
  };

  const removerInstanciaWpp = async (inst) => {
    if (!window.confirm(`Remover "${inst.nome}"?\n\nIsso vai desconectar o WhatsApp e remover permanentemente.`)) return;
    try {
      await api.delete(`/whatsapp/instancias/${inst.id}`);
      toast('Instância removida e WhatsApp desconectado.', 'success');
      carregarWpp();
    } catch { toast('Erro ao remover', 'error'); }
  };

  const desconectarInstanciaWpp = async (inst) => {
    if (!window.confirm(`Desconectar "${inst.nome}" do WhatsApp?\n\nA instância ficará cadastrada para você reconectar com outro número.`)) return;
    try {
      await api.post(`/whatsapp/instancias/${inst.id}/desconectar`);
      toast('WhatsApp desconectado! Clique em "Conectar" para escanear o QR com o novo número.', 'success');
      carregarWpp();
    } catch { toast('Erro ao desconectar', 'error'); }
  };

  const criarFilaWpp = async () => {
    if (!wppNovaFila.nome) { toast('Informe um nome para a fila', 'error'); return; }
    const chave = wppNovaFila.chave || wppNovaFila.nome.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setWppLoading(true);
    try {
      await api.post('/whatsapp/filas', { ...wppNovaFila, chave });
      toast('Fila criada!', 'success');
      setWppNovaFilaModal(false);
      setWppNovaFila({ nome: '', chave: '', cor: '#2563eb', tecnicos: [] });
      carregarWpp();
    } catch (e) { toast(e.response?.data?.erro || 'Erro ao criar fila', 'error'); }
    setWppLoading(false);
  };

  const removerFilaWpp = async (id) => {
    if (!window.confirm('Remover esta fila?')) return;
    try {
      await api.delete(`/whatsapp/filas/${id}`);
      toast('Fila removida', 'success');
      carregarWpp();
    } catch { toast('Erro ao remover fila', 'error'); }
  };

  const moverFilaWpp = async (idx, direcao) => {
    // direcao: -1 = subir, +1 = descer
    const novaLista = [...wppFilas];
    const novoIdx   = idx + direcao;
    if (novoIdx < 0 || novoIdx >= novaLista.length) return;
    [novaLista[idx], novaLista[novoIdx]] = [novaLista[novoIdx], novaLista[idx]];
    setWppFilas(novaLista); // atualiza UI imediatamente (otimista)
    try {
      await api.patch('/whatsapp/filas/reordenar', { ids: novaLista.map(f => f.id) });
    } catch { toast('Erro ao reordenar filas', 'error'); carregarWpp(); }
  };

  const abrirEditarFila = (fila) => {
    setWppEditFila({
      id:       fila.id,
      nome:     fila.nome,
      chave:    fila.chave,
      cor:      fila.cor || '#2563eb',
      tecnicos: fila.tecnicos_ids || [], // banco usa tecnicos_ids
    });
    setWppEditFilaModal(true);
  };

  const salvarEditarFila = async () => {
    if (!wppEditFila?.nome) { toast('Informe um nome para a fila', 'error'); return; }
    try {
      await api.patch(`/whatsapp/filas/${wppEditFila.id}`, {
        nome:     wppEditFila.nome,
        cor:      wppEditFila.cor,
        tecnicos: wppEditFila.tecnicos,
      });
      toast('Fila atualizada!', 'success');
      setWppEditFilaModal(false);
      setWppEditFila(null);
      carregarWpp();
    } catch (e) { toast(e.response?.data?.erro || 'Erro ao atualizar fila', 'error'); }
  };

  const toggleTecnicoEditFila = (userId) => {
    setWppEditFila(prev => ({
      ...prev,
      tecnicos: prev.tecnicos.includes(userId)
        ? prev.tecnicos.filter(id => id !== userId)
        : [...prev.tecnicos, userId],
    }));
  };

  const toggleTecnicoFila = (userId) => {
    setWppNovaFila(prev => ({
      ...prev,
      tecnicos: prev.tecnicos.includes(userId)
        ? prev.tecnicos.filter(id => id !== userId)
        : [...prev.tecnicos, userId],
    }));
  };
  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${cores.border}`, fontSize: 14, boxSizing: 'border-box', outline: 'none' };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: cores.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase' };
  const btnPrimary = { padding: '10px 20px', background: cores.orange, color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' };
  const btnGhost = { padding: '10px 20px', background: 'white', color: cores.muted, border: `1.5px solid ${cores.border}`, borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' };

 const subAbas = [
    { key: 'geral', label: 'Geral' },
    { key: 'reembolso', label: 'Reembolso' },
    { key: 'financeiro', label: 'Financeiro' },
    { key: 'orcamentos', label: 'Orçamentos' },
    { key: 'clientes', label: 'Clientes' },
    { key: 'fechamento', label: 'Fechamento' },
    ...(modulosAtivos.includes('tkt') ? [{ key: 'tickets', label: 'Tickets' }] : []),
    ...(modulosAtivos.includes('tkt') ? [{ key: 'relatorio', label: 'Relatório' }] : []),
    { key: 'cofre', label: 'Cofre de Senhas' },
    { key: 'integracoes', label: 'Integrações' },
    { key: 'backup', label: 'Backup' },
  ];

  if (aba !== 'config') return null;

  const subAbaIcons = {
    geral:       '👥',
    reembolso:   '🚗',
    financeiro:  '💰',
    orcamentos:  '📋',
    clientes:    '🏢',
    fechamento:  '📊',
    tickets:     '🎫',
    relatorio:   '📬',
    cofre:       '🔐',
    integracoes: '🔌',
    backup:      '💾',
  };

  // Hook de mobile — detecta tela menor que 700px
  const isMobileGestor = typeof window !== 'undefined' && window.innerWidth < 700;

  return (
    <div style={{ padding: isMobileGestor ? '12px 8px' : '20px 16px', maxWidth: 980, margin: '0 auto' }}>

      {/* ── MOBILE: Select dropdown de seção ── */}
      {isMobileGestor && (
        <div style={{ marginBottom: 12 }}>
          <select
            value={subAbaConfig}
            onChange={e => setSubAbaConfig(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 10,
              border: `1px solid ${cores.border}`,
              fontSize: 15,
              fontWeight: 600,
              color: cores.navy,
              background: 'white',
              appearance: 'auto',
            }}
          >
            {subAbas.map(a => (
              <option key={a.key} value={a.key}>
                {subAbaIcons[a.key]} {a.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* Sidebar vertical — só no desktop */}
        {!isMobileGestor && (
          <div style={{ width: 176, flexShrink: 0, background: 'white', borderRadius: 16, border: `1px solid ${cores.border}`, padding: '8px 0', position: 'sticky', top: 16 }}>
            {subAbas.map(a => (
              <button key={a.key} onClick={() => setSubAbaConfig(a.key)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
                background: subAbaConfig === a.key ? '#fff3eb' : 'transparent',
                borderLeft: `3px solid ${subAbaConfig === a.key ? cores.orange : 'transparent'}`,
                fontWeight: subAbaConfig === a.key ? 700 : 500,
                fontSize: 13,
                color: subAbaConfig === a.key ? cores.orange : cores.muted,
                transition: 'all 0.15s',
                boxSizing: 'border-box',
              }}>
                <span style={{ fontSize: 15 }}>{subAbaIcons[a.key]}</span>
                {a.label}
              </button>
            ))}
          </div>
        )}

        {/* Conteúdo — ocupa 100% no mobile */}
        <div style={{ flex: 1, minWidth: 0, width: isMobileGestor ? '100%' : undefined }}>

      {/* ── Sub-aba Geral ── */}
      {subAbaConfig === 'geral' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Usuarios</div>
              {(() => {
                try {
                  const u = JSON.parse(localStorage.getItem('usuario') || '{}');
                  const limiteBase = u.limite_usuarios;
                  const extras = u.extra_usuarios || 0;
                  if (limiteBase === null || limiteBase === undefined) return null;
                  const limiteTotal = limiteBase + extras;
                  const usados = usuarios.length;
                  const restam = limiteTotal - usados;
                  const cor = restam <= 1 ? '#e74c3c' : restam <= 2 ? '#f59e0b' : cores.muted;
                  return (
                    <div style={{ fontSize: 12, color: cor, marginTop: 2, fontWeight: 600 }}>
                      {usados} de {limiteTotal} usuário(s) utilizados
                      {restam <= 1 && ' — limite atingido'}
                    </div>
                  );
                } catch { return null; }
              })()}
            </div>
            <button onClick={() => setShowModalUser(true)} style={btnPrimary}>+ Novo</button>
          </div>
          {usuarios.map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${cores.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: cores.navyL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: cores.navy }}>
                  {u.iniciais}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{u.nome}</div>
                  <div style={{ fontSize: 12, color: cores.muted }}>
                    {(u.perfis || [u.perfil]).map(p => todosPerfis.find(x => x.key === p)?.label || p).join(', ')} — {u.departamento}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => abrirEdicao(u)} style={{ padding: '5px 12px', background: '#eef2ff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#2563eb', fontWeight: 600 }}>Editar</button>
                <button onClick={() => removerUsuario(u.id)} style={{ padding: '5px 12px', background: '#fde8e8', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: cores.red, fontWeight: 600 }}>Remover</button>
              </div>
            </div>
          ))}

          <Modal show={showModalUser} onClose={() => setShowModalUser(false)} title="Novo Usuario">
            {[['Nome completo', nuNome, setNuNome, 'text', 'Ex: Joao Silva'], ['Email', nuEmail, setNuEmail, 'email', 'email@empresa.com']].map(([label, val, set, type, ph]) => (
              <div key={label} style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{label}</label>
                <input type={type} value={val} onChange={e => set(e.target.value)} placeholder={ph} style={inputStyle} />
              </div>
            ))}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Senha</label>
              <input type="password" value={nuSenha} onChange={e => setNuSenha(e.target.value)} placeholder="••••••••" style={inputStyle} />
              <PasswordStrength senha={nuSenha} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Departamento</label>
              <input type="text" value={nuDept} onChange={e => setNuDept(e.target.value)} placeholder="Ex: Comercial" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Perfis</label>
              <SeletorPerfis perfis={nuPerfis} onChange={setNuPerfis} modulosAtivos={modulosAtivos} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Cor na Agenda</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {['#2563eb','#27ae60','#7c3aed','#e74c3c','#f59e0b','#0891b2','#D85A30','#059669','#dc2626','#1d4ed8','#15803d','#be185d'].map(c => (
                    <button key={c} type="button" onClick={() => setNuCor(c)}
                      style={{ width:24, height:24, borderRadius:'50%', background:c, border:`3px solid ${nuCor===c ? '#1a2233' : 'transparent'}`, cursor:'pointer', transition:'border .15s', padding:0 }} />
                  ))}
                </div>
                <input type="color" value={nuCor} onChange={e => setNuCor(e.target.value)}
                  title="Cor personalizada"
                  style={{ width:32, height:32, border:'none', borderRadius:6, cursor:'pointer', padding:0, background:'none' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowModalUser(false)} style={{ ...btnGhost, flex: 1 }}>Cancelar</button>
              <button onClick={criarUsuario} style={{ ...btnPrimary, flex: 1 }}>Criar</button>
            </div>
          </Modal>

          <Modal show={showModalEditUser} onClose={() => setShowModalEditUser(false)} title="Editar Usuario">
            {[['Nome completo', euNome, setEuNome, 'text', 'Ex: Joao Silva'], ['Email', euEmail, setEuEmail, 'email', 'email@empresa.com'], ['Nova Senha (deixe em branco para nao alterar)', euSenha, setEuSenha, 'password', '••••••••'], ['Departamento', euDept, setEuDept, 'text', 'Ex: Comercial']].map(([label, val, set, type, ph]) => (
              <div key={label} style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{label}</label>
                <input type={type} value={val} onChange={e => set(e.target.value)} placeholder={ph} style={inputStyle} />
              </div>
            ))}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Perfis</label>
              <SeletorPerfis perfis={euPerfis} onChange={setEuPerfis} modulosAtivos={modulosAtivos} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Cor na Agenda</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {['#2563eb','#27ae60','#7c3aed','#e74c3c','#f59e0b','#0891b2','#D85A30','#059669','#dc2626','#1d4ed8','#15803d','#be185d'].map(c => (
                    <button key={c} type="button" onClick={() => setEuCor(c)}
                      style={{ width:24, height:24, borderRadius:'50%', background:c, border:`3px solid ${euCor===c ? '#1a2233' : 'transparent'}`, cursor:'pointer', transition:'border .15s', padding:0 }} />
                  ))}
                </div>
                <input type="color" value={euCor} onChange={e => setEuCor(e.target.value)}
                  title="Cor personalizada"
                  style={{ width:32, height:32, border:'none', borderRadius:6, cursor:'pointer', padding:0, background:'none' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowModalEditUser(false)} style={{ ...btnGhost, flex: 1 }}>Cancelar</button>
              <button onClick={salvarEdicao} style={{ ...btnPrimary, flex: 1 }}>Salvar</button>
            </div>
          </Modal>
        </div>
      )}

      {/* ── Card MFA (sub-aba Geral) ── */}
      {subAbaConfig === 'geral' && mfaCarregado && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🔐 Autenticação em Dois Fatores (MFA)</div>
              <div style={{ fontSize: 13, color: cores.muted, lineHeight: 1.6 }}>
                Quando ativado, todos os usuários recebem um código por e-mail ao fazer login.
              </div>
            </div>
            {/* Toggle switch */}
            <button
              onClick={toggleMFA}
              disabled={mfaSalvando}
              title={mfaAtivo ? 'Desativar MFA' : 'Ativar MFA'}
              style={{
                width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
                position: 'relative', flexShrink: 0, padding: 0,
                background: mfaAtivo ? cores.orange : cores.border,
                transition: 'background 0.3s',
                opacity: mfaSalvando ? 0.6 : 1,
              }}
            >
              <div style={{
                position: 'absolute', top: 4, left: mfaAtivo ? 26 : 4,
                width: 20, height: 20, borderRadius: '50%', background: 'white',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                transition: 'left 0.3s',
              }} />
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: mfaAtivo ? cores.green : cores.muted }} />
            <span style={{ fontSize: 13, color: mfaAtivo ? cores.green : cores.muted }}>
              {mfaAtivo ? 'Ativo — login exige verificação por e-mail' : 'Inativo — login sem verificação extra'}
            </span>
          </div>
        </div>
      )}

      {/* ── Sub-aba Reembolso ── */}
      {subAbaConfig === 'reembolso' && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Configuracoes do Sistema</div>

          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: cores.muted, paddingBottom: 8, borderBottom: `1px solid ${cores.border}`, marginBottom: 12 }}>Quilometragem</div>
          {[['carro', 'Carro', 'km_carro'], ['moto', 'Moto', 'km_moto']].map(([tipo, label, chave]) => (
            <div key={tipo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${cores.border}` }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{iconeEmoji(tipo)} {label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: cores.orange, background: '#fff3eb', padding: '4px 10px', borderRadius: 6 }}>
                  R$ {(configs[chave] || 0).toFixed(2).replace('.', ',')}
                </span>
                <button onClick={() => editarKm(tipo)} style={{ padding: '5px 12px', background: '#f0f3f7', border: `1px solid ${cores.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Editar</button>
              </div>
            </div>
          ))}

          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: cores.muted, paddingBottom: 8, borderBottom: `1px solid ${cores.border}`, margin: '20px 0 12px' }}>Categorias</div>
          {categorias.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${cores.border}` }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{iconeEmoji(c.icone)} {c.nome}</div>
                <div style={{ fontSize: 12, color: cores.muted }}>{c.tipo === 'km' ? 'Por quilometro' : 'Por valor (R$)'}</div>
              </div>
              <button onClick={() => removerCategoria(c.id)} style={{ padding: '5px 12px', background: '#fde8e8', border: '1px solid #f5b7b1', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#c0392b', fontWeight: 600 }}>Remover</button>
            </div>
          ))}
          <button onClick={() => setShowModalCat(true)} style={{ ...btnGhost, marginTop: 16, width: '100%', fontSize: 13 }}>+ Adicionar Categoria</button>

          <Modal show={showModalKm} onClose={() => setShowModalKm(false)} title={`Editar KM — ${kmTipo === 'carro' ? 'Carro' : 'Moto'}`}>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Novo valor (R$ por KM)</label>
              <input type="number" step="0.01" value={kmValor} onChange={e => setKmValor(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowModalKm(false)} style={{ ...btnGhost, flex: 1 }}>Cancelar</button>
              <button onClick={salvarKm} style={{ ...btnPrimary, flex: 1 }}>Salvar</button>
            </div>
          </Modal>

          <Modal show={showModalCat} onClose={() => setShowModalCat(false)} title="Nova Categoria">
  <div style={{ marginBottom: 14 }}>
    <label style={labelStyle}>Nome</label>
    <input value={catNome} onChange={e => setCatNome(e.target.value)} placeholder="Ex: Estacionamento" style={inputStyle} />
  </div>
  <div style={{ marginBottom: 14 }}>
    <label style={labelStyle}>Ícone</label>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
      {[
        { key: 'bus',   emoji: '🚌', label: 'Ônibus'     },
        { key: 'car',   emoji: '🚗', label: 'Carro'      },
        { key: 'moto',  emoji: '🏍️', label: 'Moto'       },
        { key: 'food',  emoji: '🍽️', label: 'Alimentação'},
        { key: 'box',   emoji: '📦', label: 'Material'   },
        { key: 'other', emoji: '📎', label: 'Outros'     },
        { key: 'park',  emoji: '🅿️', label: 'Estacionar' },
        { key: 'plane', emoji: '✈️', label: 'Viagem'     },
        { key: 'hotel', emoji: '🏨', label: 'Hotel'      },
        { key: 'fuel',  emoji: '⛽', label: 'Combustível'},
      ].map(ic => (
        <button key={ic.key} type="button" onClick={() => setCatIcone(ic.key)} style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '10px 4px', borderRadius: 10, cursor: 'pointer',
          border: `2px solid ${catIcone === ic.key ? cores.orange : cores.border}`,
          background: catIcone === ic.key ? '#fff3eb' : 'white',
          gap: 4,
        }}>
          <span style={{ fontSize: 22 }}>{ic.emoji}</span>
          <span style={{ fontSize: 9, fontWeight: 600, color: catIcone === ic.key ? cores.orange : cores.muted, textAlign: 'center', lineHeight: 1.2 }}>{ic.label}</span>
        </button>
      ))}
    </div>
  </div>
  <div style={{ marginBottom: 20 }}>
    <label style={labelStyle}>Tipo</label>
    <select value={catTipo} onChange={e => setCatTipo(e.target.value)} style={inputStyle}>
      <option value="valor">Por valor (R$)</option>
      <option value="km">Por quilometro (KM)</option>
    </select>
  </div>
  <div style={{ display: 'flex', gap: 10 }}>
    <button onClick={() => setShowModalCat(false)} style={{ ...btnGhost, flex: 1 }}>Cancelar</button>
    <button onClick={criarCategoria} style={{ ...btnPrimary, flex: 1 }}>Criar</button>
  </div>
</Modal>
        </div>
      )}

      {/* ── Sub-aba Orçamentos ── */}
      {subAbaConfig === 'orcamentos' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── Identidade Visual ── */}
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🏢 Identidade Visual da Empresa</div>
          <div style={{ fontSize: 13, color: cores.muted, marginBottom: 20 }}>
            Essas informações aparecem no cabeçalho do PDF de orçamento.
          </div>

          {/* Logo upload */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Logo da Empresa</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {empresaLogoPreview ? (
                <img src={empresaLogoPreview} alt="Logo" style={{ height: 64, maxWidth: 160, objectFit: 'contain', borderRadius: 8, border: `1px solid ${cores.border}`, padding: 6, background: '#fff' }} />
              ) : (
                <div style={{ width: 100, height: 64, borderRadius: 8, border: `2px dashed ${cores.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🖼️</div>
              )}
              <div>
                <input type="file" accept="image/*" onChange={handleLogoUpload} id="logo-upload" style={{ display: 'none' }} />
                <label htmlFor="logo-upload" style={{ padding: '8px 16px', background: '#f0f3f7', border: `1px solid ${cores.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-block' }}>
                  {empresaLogoPreview ? '🔄 Trocar logo' : '📁 Enviar logo'}
                </label>
                <div style={{ fontSize: 11, color: cores.muted, marginTop: 4 }}>PNG ou JPG, até 500KB</div>
              </div>
            </div>
          </div>

          {/* Tamanho do logo no PDF */}
          {empresaLogoPreview && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Tamanho do Logo no PDF</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="range" min={40} max={200} step={5}
                  value={empresaLogoAltura}
                  onChange={e => setEmpresaLogoAltura(Number(e.target.value))}
                  style={{ flex: 1, accentColor: '#D85A30' }}
                />
                <span style={{ fontSize: 13, color: '#1A2233', minWidth: 50 }}>{empresaLogoAltura}px</span>
              </div>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: cores.muted }}>Prévia:</span>
                <img src={empresaLogoPreview} alt="Logo"
                  style={{ height: empresaLogoAltura, maxWidth: 200, objectFit: 'contain',
                    border: `1px solid ${cores.border}`, borderRadius: 4, padding: 4, background: '#fff' }} />
              </div>
            </div>
          )}

          {/* Cor primária */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Cor Principal do PDF</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="color" value={empresaCor} onChange={e => setEmpresaCor(e.target.value)}
                style={{ width: 48, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
              <span style={{ fontSize: 13, color: cores.muted }}>Usada em títulos, tabelas e destaques do PDF</span>
              <div style={{ marginLeft: 'auto', padding: '4px 14px', borderRadius: 20, background: empresaCor, color: '#fff', fontSize: 12, fontWeight: 700 }}>Preview</div>
            </div>
          </div>

          <button onClick={salvarEmpresa} disabled={salvandoEmpresa} style={{ ...btnPrimary, opacity: salvandoEmpresa ? 0.7 : 1 }}>
            {salvandoEmpresa ? 'Salvando...' : '💾 Salvar Identidade Visual'}
          </button>
        </div>

        {/* ── Configurações de Numeração ── */}
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Configurações de Orçamentos</div>
          <div style={{ fontSize: 13, color: cores.muted, marginBottom: 20 }}>
            Próximo número automático: <strong style={{ color: cores.orange }}>{orcProximoNumero}</strong>
          </div>
          <div style={{ background: '#fff8f0', borderRadius: 10, padding: 16, marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: cores.orange, textTransform: 'uppercase', marginBottom: 8 }}>Definir Número Inicial</div>
            <div style={{ fontSize: 13, color: cores.muted, marginBottom: 12 }}>
              Define a partir de qual número os próximos orçamentos serão gerados.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <input type="number" value={orcNumeroInicial} onChange={e => setOrcNumeroInicial(e.target.value)} placeholder="Ex: 150" style={{ ...inputStyle, flex: 1 }} />
              <button onClick={definirNumeroInicial} disabled={salvandoNumeroInicial || !orcNumeroInicial} style={{ ...btnPrimary, whiteSpace: 'nowrap', opacity: !orcNumeroInicial ? 0.6 : 1 }}>
                {salvandoNumeroInicial ? 'Salvando...' : 'Definir'}
              </button>
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: cores.muted, paddingBottom: 8, borderBottom: `1px solid ${cores.border}`, marginBottom: 12 }}>
            Editar Número de Orçamento Existente
          </div>
          {orcamentos.length === 0 && (
            <div style={{ textAlign: 'center', color: cores.muted, padding: 24, fontSize: 13 }}>Nenhum orçamento cadastrado.</div>
          )}
          {orcamentos.slice((paginaOrc - 1) * POR_PAGINA, paginaOrc * POR_PAGINA).map(orc => (
            <div key={orc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${cores.border}`, gap: 10 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{orc.cliente_nome}</div>
                <div style={{ fontSize: 12, color: cores.muted }}>Nº {orc.numero}</div>
              </div>
              {editandoOrc === orc.id ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input value={novoNumeroOrc} onChange={e => setNovoNumeroOrc(e.target.value)} placeholder={orc.numero} style={{ ...inputStyle, width: 140, padding: '6px 10px' }} />
                  <button onClick={() => salvarNumeroOrc(orc.id)} style={{ padding: '6px 14px', background: cores.green, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>✓</button>
                  <button onClick={() => setEditandoOrc(null)} style={{ padding: '6px 12px', background: '#f0f3f7', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✕</button>
                </div>
              ) : (
                <button onClick={() => { setEditandoOrc(orc.id); setNovoNumeroOrc(orc.numero); }} style={{ padding: '5px 12px', background: '#eef2ff', color: '#2563eb', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>
                  ✏️ Editar Nº
                </button>
              )}
            </div>
          ))}
          <Paginator pagina={paginaOrc} setPagina={setPaginaOrc} total={orcamentos.length} porPagina={POR_PAGINA} />
        </div>

        {/* ── Template de Email ── */}
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>📧 Template de Email</div>
          <div style={{ fontSize: 13, color: cores.muted, marginBottom: 16 }}>
            Personalize o email enviado junto com o PDF do orçamento. Use as variáveis abaixo.
          </div>

          {/* Variáveis disponíveis */}
          <div style={{ background: '#f7f8fa', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: cores.muted, lineHeight: 2 }}>
            <strong style={{ color: '#1a2233', display: 'block', marginBottom: 4 }}>Variáveis disponíveis:</strong>
            {[
              ['{{numero}}', 'Número do orçamento'],
              ['{{cliente}}', 'Nome do cliente'],
              ['{{valor}}', 'Valor total'],
              ['{{vencimento}}', 'Data de vencimento do boleto'],
              ['{{link_boleto}}', 'Link do boleto gerado'],
              ['{{descricao_cobranca}}', 'Descrição da cobrança'],
              ['{{assinatura}}', 'Imagem da assinatura'],
            ].map(([v, d]) => (
              <span key={v} style={{ marginRight: 16 }}>
                <code style={{ background: '#e8f0fe', color: '#1a56db', padding: '1px 6px', borderRadius: 4 }}>{v}</code>
                {' '}{d}
              </span>
            ))}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Assunto</label>
            <input value={emailOrcAssunto} onChange={e => setEmailOrcAssunto(e.target.value)}
              placeholder="Orçamento Nº {{numero}} — {{cliente}}"
              style={{ ...inputStyle, width: '100%' }} />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Corpo do Email</label>
            <textarea value={emailOrcCorpo} onChange={e => setEmailOrcCorpo(e.target.value)}
              rows={8} placeholder="Digite o corpo do email..."
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} />
          </div>

          {/* Assinatura de email */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Imagem da Assinatura (opcional)</label>
            <div style={{ fontSize: 12, color: cores.muted, marginBottom: 8 }}>
              Use <code style={{ background: '#e8f0fe', color: '#1a56db', padding: '1px 5px', borderRadius: 3 }}>{'{{assinatura}}'}</code> no corpo do email para inserir a imagem abaixo.
            </div>
            {emailOrcAssinaturaImg ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <img src={emailOrcAssinaturaImg} alt="Assinatura"
                  style={{ maxHeight: 80, maxWidth: 300, border: `1px solid ${cores.border}`,
                    borderRadius: 6, padding: 4, background: '#fff' }} />
                <button onClick={() => setEmailOrcAssinaturaImg('')}
                  style={{ background: 'none', border: `1px solid #e74c3c`, color: '#e74c3c',
                    borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13 }}>
                  Remover
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: cores.muted, marginBottom: 8 }}>Nenhuma imagem de assinatura</div>
            )}
            <label style={{ display: 'inline-block', background: '#f5f7fa', border: `1px solid ${cores.border}`,
              borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontSize: 13 }}>
              📎 Escolher imagem
              <input type="file" accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (file.size > 500 * 1024) { toast('Imagem muito grande. Máximo 500KB.', 'error'); return; }
                  const reader = new FileReader();
                  reader.onload = ev => setEmailOrcAssinaturaImg(ev.target.result);
                  reader.readAsDataURL(file);
                }} />
            </label>
          </div>

          <button onClick={salvarEmailOrc} disabled={emailOrcSalvando} style={{ ...btnPrimary, opacity: emailOrcSalvando ? 0.7 : 1 }}>
            {emailOrcSalvando ? 'Salvando...' : '💾 Salvar Template'}
          </button>
        </div>
        </div>
      )}

      {/* ── Sub-aba Fechamento ── */}
      {subAbaConfig === 'fechamento' && (
        <div>
          {/* Sócios */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Sócios</div>
            <div style={{ fontSize: 13, color: cores.muted, marginBottom: 16 }}>
              Define como o saldo D.L. é dividido entre os sócios.
            </div>

            {/* Indicador total */}
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 16,
              background: totalPercentual === 100 ? '#f0fdf4' : totalPercentual > 100 ? '#fef2f2' : '#fff8f0',
              border: `1px solid ${totalPercentual === 100 ? '#86efac' : totalPercentual > 100 ? '#fca5a5' : '#fed7aa'}`
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: totalPercentual === 100 ? cores.green : totalPercentual > 100 ? cores.red : cores.orange }}>
                Total: {totalPercentual.toFixed(1)}% {totalPercentual === 100 ? '✓ Correto' : totalPercentual > 100 ? '— Ultrapassou 100%!' : `— Faltam ${(100 - totalPercentual).toFixed(1)}%`}
              </span>
            </div>

            {/* Lista de sócios */}
            {socios.length === 0 && (
              <p style={{ color: cores.muted, fontSize: 13 }}>Nenhum sócio cadastrado.</p>
            )}
            {socios.map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: `1px solid ${cores.border}` }}>
                {editandoSocio?.id === s.id ? (
                  <>
                    <input
                      value={editandoSocio.nome}
                      onChange={e => setEditandoSocio(p => ({ ...p, nome: e.target.value }))}
                      style={{ ...inputStyle, flex: 2, padding: '6px 10px' }}
                      placeholder="Nome"
                    />
                    <input
                      type="number" step="0.01"
                      value={editandoSocio.percentual}
                      onChange={e => setEditandoSocio(p => ({ ...p, percentual: e.target.value }))}
                      style={{ ...inputStyle, flex: 1, padding: '6px 10px' }}
                      placeholder="%"
                    />
                    <button onClick={() => salvarEdicaoSocio(s.id)} style={{ padding: '6px 12px', background: cores.green, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>✓</button>
                    <button onClick={() => setEditandoSocio(null)} style={{ padding: '6px 10px', background: '#f0f3f7', border: 'none', borderRadius: 6, cursor: 'pointer' }}>✕</button>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 2, fontWeight: 600, fontSize: 14 }}>{s.nome}</div>
                    <div style={{ flex: 1 }}>
                      <span style={{ background: '#fff3eb', color: cores.orange, fontWeight: 700, fontSize: 13, padding: '3px 10px', borderRadius: 6 }}>
                        {s.percentual}%
                      </span>
                    </div>
                    <button onClick={() => setEditandoSocio({ id: s.id, nome: s.nome, percentual: s.percentual })}
                      style={{ padding: '5px 10px', background: '#eef2ff', color: '#2563eb', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✏️</button>
                    <button onClick={() => removerSocio(s.id)}
                      style={{ padding: '5px 10px', background: '#fde8e8', color: cores.red, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                  </>
                )}
              </div>
            ))}

            {/* Adicionar sócio */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <input
                value={novoSocio.nome}
                onChange={e => setNovoSocio(p => ({ ...p, nome: e.target.value }))}
                placeholder="Nome do sócio"
                style={{ ...inputStyle, flex: 2 }}
              />
              <input
                type="number" step="0.01"
                value={novoSocio.percentual}
                onChange={e => setNovoSocio(p => ({ ...p, percentual: e.target.value }))}
                placeholder="%"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button onClick={adicionarSocio} style={{ ...btnPrimary, whiteSpace: 'nowrap' }}>+ Adicionar</button>
            </div>
          </div>

          {/* Bancos */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Contas / Bancos</div>
            <div style={{ fontSize: 13, color: cores.muted, marginBottom: 16 }}>
              Contas que aparecem na aba de entradas do fechamento mensal.
            </div>

            {bancos.length === 0 && (
              <p style={{ color: cores.muted, fontSize: 13 }}>Nenhuma conta cadastrada.</p>
            )}
            {bancos.map(b => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: `1px solid ${cores.border}` }}>
                {editandoBanco?.id === b.id ? (
                  <>
                    <input
                      value={editandoBanco.nome}
                      onChange={e => setEditandoBanco(p => ({ ...p, nome: e.target.value }))}
                      style={{ ...inputStyle, flex: 1, padding: '6px 10px' }}
                    />
                    <button onClick={() => salvarEdicaoBanco(b.id)} style={{ padding: '6px 12px', background: cores.green, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>✓</button>
                    <button onClick={() => setEditandoBanco(null)} style={{ padding: '6px 10px', background: '#f0f3f7', border: 'none', borderRadius: 6, cursor: 'pointer' }}>✕</button>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>🏦 {b.nome}</div>
                      {(b.data_inicio || b.data_fim) && (
                        <div style={{ fontSize: 11, color: cores.muted, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          {b.data_inicio && <span>Início: {new Date(b.data_inicio).toLocaleDateString('pt-BR')}</span>}
                          {b.data_fim && <span style={{ color: cores.red, fontWeight: 600 }}>Encerrado: {new Date(b.data_fim).toLocaleDateString('pt-BR')}</span>}
                        </div>
                      )}
                    </div>
                    <button onClick={() => setEditandoBanco({ id: b.id, nome: b.nome })}
                      style={{ padding: '5px 10px', background: '#eef2ff', color: '#2563eb', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✏️</button>
                    <button
                      title="Encerra a partir de hoje, mantém no histórico dos meses anteriores"
                      onClick={() => encerrarBanco(b.id)}
                      style={{ padding: '5px 10px', background: '#fde8e8', color: cores.red, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>⏹️</button>
                  </>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <input
                value={novoBanco}
                onChange={e => setNovoBanco(e.target.value)}
                placeholder="Ex: Nubank, Inter, Bradesco..."
                style={{ ...inputStyle, flex: 1, minWidth: 180 }}
              />
              <input
                type="date"
                value={novoBancoInicio}
                onChange={e => setNovoBancoInicio(e.target.value)}
                title="Início (opcional) — deixe em branco para valer em todos os meses"
                style={{ ...inputStyle, width: 160 }}
              />
              <button onClick={adicionarBanco} style={{ ...btnPrimary, whiteSpace: 'nowrap' }}>+ Adicionar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sub-aba Clientes ── */}
      {subAbaConfig === 'clientes' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#1a2233' }}>👥 Clientes</div>
              <div style={{ fontSize: 13, color: cores.muted, marginTop: 2 }}>{clientes.length} cliente(s) cadastrado(s)</div>
            </div>
            <button onClick={() => { setClienteForm({ emails_adicionais: '[]' }); setClienteModal('novo'); }} style={{ ...btnPrimary }}>+ Novo Cliente</button>
          </div>

          {/* Busca */}
          <input
            placeholder="Buscar por nome, email ou cidade..."
            value={clientesBusca}
            onChange={e => { setClientesBusca(e.target.value); setPaginaCli(1); }}
            style={{ ...inputStyle, marginBottom: 16, width: '100%', boxSizing: 'border-box' }}
          />

          {/* Lista */}
          {(() => {
            const filtrados = clientes.filter(c => {
              const q = clientesBusca.toLowerCase();
              return !q || (c.nome||'').toLowerCase().includes(q) || (c.email||'').toLowerCase().includes(q) || (c.cidade||'').toLowerCase().includes(q);
            });
            const paginados = filtrados.slice((paginaCli - 1) * POR_PAGINA, paginaCli * POR_PAGINA);
            return (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {paginados.map(c => (
                    <div key={c.id} style={{ background: 'white', border: `1px solid ${cores.border}`, borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                      {/* Avatar */}
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: cores.orange, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                        {(c.nome||'?').slice(0,2).toUpperCase()}
                      </div>
                      {/* Dados */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1a2233', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.nome}</div>
                        <div style={{ fontSize: 12, color: cores.muted, marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          {(() => {
                            // Mostra email principal da lista ou campo email legado
                            try {
                              const raw = JSON.parse(c.emails_adicionais || '[]').filter(Boolean);
                              const principal = raw.find(e => (typeof e === 'object') && e.principal && e.email);
                              const emailExibir = principal?.email || c.email;
                              const extras = raw.map(e => typeof e === 'string' ? e : e.email).filter(Boolean);
                              return <>
                                {emailExibir && <span>✉️ {emailExibir}</span>}
                                {extras.length > 1 && <span style={{ fontSize: 11, color: cores.muted, background: '#f0f3f7', borderRadius: 4, padding: '1px 6px' }}>+{extras.length - 1} email(s)</span>}
                              </>;
                            } catch {
                              return c.email ? <span>✉️ {c.email}</span> : null;
                            }
                          })()}
                          {c.telefone && <span>📞 {c.telefone}</span>}
                          {c.cidade   && <span>📍 {c.cidade}{c.bairro ? ` · ${c.bairro}` : ''}</span>}
                        </div>
                      </div>
                      {/* Ações */}
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button onClick={() => {
                          // Migra o email legado (coluna `email`) para emails_adicionais
                          // UMA ÚNICA VEZ, ao abrir o modal — evita reinjeção a cada render.
                          let listaEmails = [];
                          try { listaEmails = JSON.parse(c.emails_adicionais || '[]'); } catch {}
                          listaEmails = listaEmails.map((e, i) => typeof e === 'string'
                            ? { email: e, orcamentos: true, notas: false, tickets: false, principal: i === 0 }
                            : e
                          );
                          if (c.email && !listaEmails.some(e => e.email === c.email)) {
                            listaEmails = [{ email: c.email, orcamentos: true, notas: false, tickets: false, principal: listaEmails.length === 0 }, ...listaEmails];
                          }
                          if (listaEmails.length > 0 && !listaEmails.some(e => e.principal)) {
                            listaEmails[0] = { ...listaEmails[0], principal: true };
                          }
                          setClienteForm({ ...c, emails_adicionais: JSON.stringify(listaEmails) });
                          setClienteModal(c);
                        }}
                          style={{ background: '#f0f3f7', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: '#1a2233', fontWeight: 600 }}>
                          ✏️ Editar
                        </button>
                        <button onClick={() => excluirCliente(c.id)}
                          style={{ background: '#fde8e8', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                  {filtrados.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 40, color: cores.muted, fontSize: 14 }}>Nenhum cliente encontrado.</div>
                  )}
                </div>
                <Paginator pagina={paginaCli} setPagina={setPaginaCli} total={filtrados.length} porPagina={POR_PAGINA} />
              </div>
            );
          })()}

          {/* Modal Novo/Editar Cliente */}
          {clienteModal !== null && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
              <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
                <div style={{ background: '#1a2233', borderRadius: '16px 16px 0 0', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: 'white', fontWeight: 700, fontSize: 16 }}>{clienteModal === 'novo' ? '+ Novo Cliente' : '✏️ Editar Cliente'}</span>
                  <button onClick={() => { setClienteModal(null); setClienteForm({}); }} style={{ background: 'none', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}>×</button>
                </div>
                <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Nome */}
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Nome *</label>
                    <input value={clienteForm.nome || ''} onChange={e => setClienteForm(p => ({ ...p, nome: e.target.value }))} placeholder="Nome completo ou razão social" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                  </div>

                  {/* Apelido / Nome Fantasia */}
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Apelido / Nome Fantasia</label>
                    <input value={clienteForm.cliente_apelido || ''} onChange={e => setClienteForm(p => ({ ...p, cliente_apelido: e.target.value }))}
                      placeholder="Como é conhecido (aparece nos orçamentos e tickets)" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                  </div>

                  {/* Emails de contato — lista unificada com flags */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5 }}>Emails de contato</label>
                      <button type="button"
                        onClick={() => {
                          const lista = (() => { try { return JSON.parse(clienteForm.emails_adicionais || '[]'); } catch { return []; } })();
                          const novo = { email: '', orcamentos: false, notas: false, tickets: false, principal: lista.length === 0 };
                          setClienteForm(p => ({ ...p, emails_adicionais: JSON.stringify([...lista, novo]) }));
                        }}
                        style={{ fontSize: 12, color: cores.orange, background: '#fff3eb', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}>
                        + Adicionar
                      </button>
                    </div>
                    {(() => {
                      let lista = [];
                      try {
                        const raw = JSON.parse(clienteForm.emails_adicionais || '[]');
                        // Compatibilidade: migra strings antigas para objetos
                        lista = raw.map((e, i) => typeof e === 'string'
                          ? { email: e, orcamentos: true, notas: false, tickets: false, principal: i === 0 }
                          : e
                        );
                        // Se nenhum é principal, marca o primeiro
                        if (lista.length > 0 && !lista.some(e => e.principal)) {
                          lista[0] = { ...lista[0], principal: true };
                        }
                        // NOTA: a migração do email legado (clienteForm.email) para esta lista
                        // acontece apenas UMA VEZ, no momento de abrir o modal (botão Editar/+Novo Cliente).
                        // Não fazer essa migração aqui dentro do render: como o onChange do email só
                        // atualiza `emails_adicionais` e nunca `clienteForm.email`, reinjetar aqui a
                        // cada render recriava um item novo a cada edição e impedia apagar o 1º email.
                      } catch {}
                      const funcoesDisponiveis = [
                        { key: 'orcamentos', label: 'Orçamentos' },
                        ...(modulosAtivos.includes('tkt') ? [{ key: 'tickets', label: 'Tickets' }] : []),
                        { key: 'notas', label: 'Notas fiscais' },
                        { key: 'principal', label: 'Principal' },
                      ];
                      if (lista.length === 0) return (
                        <div style={{ fontSize: 12, color: cores.muted, padding: '4px 0' }}>
                          Nenhum email cadastrado. Clique em + Adicionar.
                        </div>
                      );
                      return lista.map((item, idx) => {
                        const isPrincipal = !!item.principal;
                        return (
                          <div key={idx} style={{ border: `1px solid ${isPrincipal ? cores.orange : cores.borda}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: isPrincipal ? '#fff8f5' : 'transparent' }}>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
                              <input type="email" value={item.email || ''}
                                onChange={e => {
                                  const nova = [...lista]; nova[idx] = { ...nova[idx], email: e.target.value };
                                  setClienteForm(p => ({ ...p, emails_adicionais: JSON.stringify(nova) }));
                                }}
                                placeholder="email@exemplo.com"
                                style={{ ...inputStyle, flex: 1, boxSizing: 'border-box' }} />
                              <button type="button"
                                onClick={() => {
                                  const nova = lista.filter((_, i) => i !== idx);
                                  // Se removeu o principal, marca o primeiro como principal
                                  if (isPrincipal && nova.length > 0) nova[0] = { ...nova[0], principal: true };
                                  setClienteForm(p => ({ ...p, emails_adicionais: JSON.stringify(nova) }));
                                }}
                                style={{ padding: '6px 10px', background: '#fde8e8', color: '#991b1b', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>✕</button>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {funcoesDisponiveis.map(fn => {
                                const ativo = !!item[fn.key];
                                return (
                                  <label key={fn.key} style={{
                                    display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer',
                                    background: ativo ? '#fff3eb' : '#f7f8fa',
                                    border: `1px solid ${ativo ? cores.orange : cores.borda}`,
                                    borderRadius: 6, padding: '3px 10px',
                                    color: ativo ? cores.orange : cores.muted, fontWeight: ativo ? 600 : 400,
                                  }}>
                                    <input type="checkbox" checked={ativo}
                                      onChange={() => {
                                        const nova = [...lista];
                                        if (fn.key === 'principal') {
                                          // Principal é exclusivo — desmarca todos os outros
                                          nova.forEach((e, i) => { nova[i] = { ...nova[i], principal: i === idx ? !ativo : false }; });
                                          // Garante que sempre tem um principal
                                          if (!nova.some(e => e.principal)) nova[0] = { ...nova[0], principal: true };
                                        } else {
                                          nova[idx] = { ...nova[idx], [fn.key]: !ativo };
                                        }
                                        setClienteForm(p => ({ ...p, emails_adicionais: JSON.stringify(nova) }));
                                      }}
                                      style={{ accentColor: cores.orange, width: 13, height: 13 }} />
                                    {fn.label}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  {/* Toggle: notificar ao finalizar ticket — só aparece com módulo tkt */}
                  {modulosAtivos.includes('tkt') && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', borderRadius: 10, padding: '12px 14px', border: '1px solid #e8ecf0' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1A2233' }}>📧 Notificar ao finalizar ticket</div>
                      <div style={{ fontSize: 12, color: cores.muted, marginTop: 2 }}>
                        Envia email automático ao cliente quando um ticket for finalizado
                      </div>
                    </div>
                    <div
                      onClick={() => setClienteForm(p => ({ ...p, notificar_ticket_finalizado: !p.notificar_ticket_finalizado }))}
                      style={{ width: 44, height: 24, borderRadius: 12, cursor: 'pointer', flexShrink: 0, background: clienteForm.notificar_ticket_finalizado ? '#D85A30' : '#d4dde8', position: 'relative', transition: 'background .2s' }}>
                      <div style={{ position: 'absolute', top: 3, left: clienteForm.notificar_ticket_finalizado ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                    </div>
                  </div>
                  )}

                  {/* Telefones */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Telefone</label>
                      <input value={clienteForm.telefone || ''} onChange={e => {
                        let v = e.target.value.replace(/\D/g,'').slice(0,11);
                        if (v.length > 10) v = v.replace(/^(\d{2})(\d{5})(\d{4})$/,'($1) $2-$3');
                        else if (v.length > 6) v = v.replace(/^(\d{2})(\d{4})(\d*)$/,'($1) $2-$3');
                        else if (v.length > 2) v = v.replace(/^(\d{2})(\d*)$/,'($1) $2');
                        setClienteForm(p => ({ ...p, telefone: v }));
                      }} placeholder="(00) 00000-0000" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Telefone 2</label>
                      <input value={clienteForm.telefone2 || ''} onChange={e => {
                        let v = e.target.value.replace(/\D/g,'').slice(0,11);
                        if (v.length > 10) v = v.replace(/^(\d{2})(\d{5})(\d{4})$/,'($1) $2-$3');
                        else if (v.length > 6) v = v.replace(/^(\d{2})(\d{4})(\d*)$/,'($1) $2-$3');
                        else if (v.length > 2) v = v.replace(/^(\d{2})(\d*)$/,'($1) $2');
                        setClienteForm(p => ({ ...p, telefone2: v }));
                      }} placeholder="(00) 00000-0000" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                    </div>
                  </div>

                  {/* CPF */}
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>CPF</label>
                    <input value={clienteForm.cpf || ''} onChange={e => {
                      let v = e.target.value.replace(/\D/g,'').slice(0,11);
                      v = v.replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2})$/,(_, a,b,c,d) => d ? `${a}.${b}.${c}-${d}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a);
                      setClienteForm(p => ({ ...p, cpf: v }));
                    }} placeholder="000.000.000-00" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                  </div>

                  {/* CNPJ com busca BrasilAPI */}
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>CNPJ</label>
                    <input value={clienteForm.cnpj || ''} onChange={async e => {
                      let v = e.target.value.replace(/\D/g,'').slice(0,14);
                      const fmt = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})$/,(_, a,b,c,d,ee) => ee ? `${a}.${b}.${c}/${d}-${ee}` : d ? `${a}.${b}.${c}/${d}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a);
                      setClienteForm(p => ({ ...p, cnpj: fmt, _cnpjStatus: v.length === 14 ? 'buscando' : '' }));
                      if (v.length === 14) {
                        try {
                          const r = await api.get(`/utils/cnpj/${v}`);
                          const d = r.data;
                          if (d.razao_social) {
                            // Usa função com snapshot do state atual para evitar stale closure
                            // Só preenche campos vazios — nunca sobrescreve o que o usuário já digitou
                            setClienteForm(p => ({
                              ...p,
                              cnpj:     fmt,
                              nome:     p.nome?.trim()     ? p.nome     : (d.razao_social || ''),
                              endereco: p.endereco?.trim() ? p.endereco : (d.logradouro ? `${d.logradouro}, ${d.numero||''}`.trim() : ''),
                              bairro:   p.bairro?.trim()   ? p.bairro   : (d.bairro || ''),
                              cidade:   p.cidade?.trim()   ? p.cidade   : (d.municipio ? `${d.municipio}-${d.uf}` : ''),
                              cep:      p.cep?.trim()      ? p.cep      : (d.cep?.replace(/\D/g,'').replace(/^(\d{5})(\d{3})$/,'$1-$2') || ''),
                              email:    p.email?.trim()    ? p.email    : (d.email || ''),
                              telefone: p.telefone?.trim() ? p.telefone : (d.ddd_telefone_1 ? d.ddd_telefone_1.replace(/\D/g,'').replace(/^(\d{2})(\d{4,5})(\d{4})$/,'($1) $2-$3') : ''),
                              _cnpjStatus: 'ok',
                            }));
                          } else { setClienteForm(p => ({ ...p, _cnpjStatus: 'nao_encontrado' })); }
                        } catch { setClienteForm(p => ({ ...p, _cnpjStatus: 'nao_encontrado' })); }
                      }
                    }} placeholder="00.000.000/0000-00" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                    {clienteForm._cnpjStatus === 'buscando'       && <div style={{ fontSize: 11, color: cores.muted,    marginTop: 4 }}>🔍 Buscando CNPJ...</div>}
                    {clienteForm._cnpjStatus === 'ok'             && <div style={{ fontSize: 11, color: '#27ae60',      marginTop: 4 }}>✓ Dados preenchidos automaticamente</div>}
                    {clienteForm._cnpjStatus === 'nao_encontrado' && <div style={{ fontSize: 11, color: '#e74c3c',      marginTop: 4 }}>CNPJ não encontrado</div>}
                  </div>

                  {/* Inscrição Estadual */}
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Inscrição Estadual</label>
                    <input value={clienteForm.inscricao_estadual || ''} onChange={e => setClienteForm(p => ({ ...p, inscricao_estadual: e.target.value }))}
                      placeholder="Preenchida automaticamente" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                  </div>

                  {/* CEP com busca ViaCEP */}
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>CEP</label>
                    <input value={clienteForm.cep || ''} onChange={async e => {
                      let v = e.target.value.replace(/\D/g,'').slice(0,8);
                      const fmt = v.length > 5 ? v.replace(/^(\d{5})(\d{0,3})$/,'$1-$2') : v;
                      setClienteForm(p => ({ ...p, cep: fmt, _cepStatus: v.length === 8 ? 'buscando' : '' }));
                      if (v.length === 8) {
                        try {
                          const r = await api.get(`/utils/cep/${v}`);
                          const d = r.data;
                          if (!d.erro) {
                            setClienteForm(p => ({
                              ...p, cep: fmt,
                              endereco: d.logradouro || p.endereco,
                              bairro:   d.bairro     || p.bairro,
                              cidade:   d.localidade ? `${d.localidade}-${d.uf}` : p.cidade,
                              // Código IBGE do município — necessário para NFS-e (campo
                              // codigo_municipio_tomador no Ambiente Nacional da Focus NFe)
                              codigo_municipio: d.ibge || p.codigo_municipio,
                              _cepStatus: 'ok',
                            }));
                          } else { setClienteForm(p => ({ ...p, _cepStatus: 'nao_encontrado' })); }
                        } catch { setClienteForm(p => ({ ...p, _cepStatus: 'nao_encontrado' })); }
                      }
                    }} placeholder="00000-000" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                    {clienteForm._cepStatus === 'buscando'       && <div style={{ fontSize: 11, color: cores.muted,    marginTop: 4 }}>🔍 Buscando CEP...</div>}
                    {clienteForm._cepStatus === 'ok'             && <div style={{ fontSize: 11, color: '#27ae60',      marginTop: 4 }}>✓ Endereço preenchido automaticamente</div>}
                    {clienteForm._cepStatus === 'nao_encontrado' && <div style={{ fontSize: 11, color: '#e74c3c',      marginTop: 4 }}>CEP não encontrado</div>}
                  </div>

                  {/* Endereço, Bairro, Cidade */}
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Endereço</label>
                    <input value={clienteForm.endereco || ''} onChange={e => setClienteForm(p => ({ ...p, endereco: e.target.value }))} placeholder="Rua, número" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Bairro</label>
                      <input value={clienteForm.bairro || ''} onChange={e => setClienteForm(p => ({ ...p, bairro: e.target.value }))} placeholder="Bairro" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', letterSpacing: .5, display: 'block', marginBottom: 4 }}>Cidade</label>
                      <input value={clienteForm.cidade || ''} onChange={e => setClienteForm(p => ({ ...p, cidade: e.target.value }))} placeholder="Cidade-UF" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => { setClienteModal(null); setClienteForm({}); }}
                      style={{ background: 'white', border: `1px solid ${cores.border}`, borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14 }}>
                      Cancelar
                    </button>
                    <button onClick={salvarCliente} disabled={salvandoCliente}
                      style={{ ...btnPrimary, opacity: salvandoCliente ? .7 : 1 }}>
                      {salvandoCliente ? 'Salvando...' : clienteModal === 'novo' ? '+ Adicionar' : '✓ Salvar'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Sub-aba Integrações ── */}
      {subAbaConfig === 'integracoes' && (
        <div style={{ maxWidth: 660 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a2233', marginBottom: 4, marginTop: 0 }}>🔌 Integrações</h3>
          <p style={{ fontSize: 13, color: cores.muted, marginBottom: 16, marginTop: 4 }}>
            Configure as integrações externas da sua empresa.
          </p>

          {/* Sub-abas internas de integrações */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f0f3f7', padding: 4, borderRadius: 10, width: 'fit-content' }}>
            {[
              { key: 'asaas',    label: '🏦 ASAAS' },
              { key: 'smtp',     label: '📧 Email SMTP' },
              { key: 'whatsapp', label: '📱 WhatsApp' },
              { key: 'focusnfe', label: '🧾 NFe' },
            ].map(a => (
              <button key={a.key} onClick={() => setSubAbaInteg(a.key)}
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: subAbaInteg === a.key ? 'white' : 'transparent',
                  color: subAbaInteg === a.key ? cores.orange : cores.muted,
                  boxShadow: subAbaInteg === a.key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
                {a.label}
              </button>
            ))}
          </div>

          {/* ── ASAAS ── */}
          {subAbaInteg === 'asaas' && <div>

          {/* ASAAS */}
          <div style={{ background: 'white', border: `1px solid ${cores.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, background: '#e8f4ff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🏦</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1a2233' }}>ASAAS</div>
                <div style={{ fontSize: 12, color: cores.muted }}>Geração de boletos e cobranças PIX</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                  background: asaasTeste === 'ok' ? '#22c55e' : asaasTeste === 'erro' ? '#ef4444' : asaasKey ? '#f59e0b' : '#d1d5db',
                  boxShadow: asaasTeste === 'ok' ? '0 0 0 3px rgba(34,197,94,0.2)' : asaasTeste === 'erro' ? '0 0 0 3px rgba(239,68,68,0.2)' : 'none',
                }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: asaasTeste === 'ok' ? '#15803d' : asaasTeste === 'erro' ? '#991b1b' : cores.muted }}>
                  {asaasTeste === 'ok' ? 'Conectado' : asaasTeste === 'erro' ? 'Falhou' : asaasKey ? 'Não testado' : 'Não configurado'}
                </span>
              </div>
            </div>

            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#0369a1' }}>
              ℹ️ O ambiente de cobrança (Sandbox/Produção) é definido pelo administrador no painel admin.
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>API Key</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type={asaasKeyVisivel ? 'text' : 'password'}
                  value={asaasKey}
                  onChange={e => { setAsaasKey(e.target.value); setAsaasTeste(null); }}
                  placeholder="$aact_xxxxxxxxxxxxxxxxxxxx"
                  style={{ ...inputStyle, flex: 1, fontFamily: asaasKeyVisivel ? 'inherit' : 'monospace' }}
                />
                <button onClick={() => setAsaasKeyVisivel(v => !v)}
                  style={{ padding: '8px 12px', background: '#f0f3f7', border: `1px solid ${cores.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
                  {asaasKeyVisivel ? '🙈' : '👁️'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: cores.muted, marginTop: 4 }}>
                Acesse app.asaas.com → Minha Conta → Integrações → Gerar Token
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={testarAsaas} disabled={!asaasKey || asaasTestando}
                style={{ padding: '10px 16px', background: '#f0f3f7', color: '#1a2233', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: !asaasKey || asaasTestando ? 'not-allowed' : 'pointer', opacity: !asaasKey ? 0.5 : 1 }}>
                {asaasTestando ? '⏳ Testando...' : '🔗 Testar Conexão'}
              </button>
              <button onClick={salvarAsaas} disabled={!asaasKey || asaasSalvando}
                style={{ flex: 1, padding: '10px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: !asaasKey || asaasSalvando ? 'not-allowed' : 'pointer', opacity: !asaasKey ? 0.5 : 1 }}>
                {asaasSalvando ? 'Salvando...' : '💾 Salvar'}
              </button>
            </div>
          </div>

          </div>}

          {/* ── SMTP / Sobraa Mail ── */}
          {subAbaInteg === 'smtp' && <div>

          {/* ── Sobraa Mail ─────────────────────────────────────────────────── */}
          <div style={{ background: 'white', border: `1px solid ${cores.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, background: '#f0fdf4', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🚀</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1a2233' }}>Sobraa Mail</div>
                <div style={{ fontSize: 12, color: cores.muted }}>Envie emails com seu domínio pelos servidores do Sobraa</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                  background: sobraaStatus?.status === 'ativo' ? '#22c55e' : sobraaStatus ? '#f59e0b' : '#d1d5db',
                  boxShadow: sobraaStatus?.status === 'ativo' ? '0 0 0 3px rgba(34,197,94,0.2)' : 'none',
                }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: sobraaStatus?.status === 'ativo' ? '#15803d' : sobraaStatus ? '#92400e' : cores.muted }}>
                  {sobraaStatus?.status === 'ativo' ? '✅ Ativo' : sobraaStatus ? '⏳ Pendente' : 'Não configurado'}
                </span>
              </div>
            </div>

            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#0369a1', lineHeight: 1.6 }}>
              ℹ️ Com o <strong>Sobraa Mail</strong>, seus emails são enviados pelos servidores do Sobraa com seu domínio verificado.
              Não é necessário configurar SMTP, senhas ou servidor. Funciona com Gmail, Outlook, Hotmail e qualquer provedor.
            </div>

            {/* Domínio */}
            {!sobraaStatus ? (
              <div>
                <label style={labelStyle}>Seu domínio de email</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={sobraaDominio}
                    onChange={e => setSobraaDominio(e.target.value.toLowerCase())}
                    placeholder="seudominio.com.br"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button onClick={adicionarDominio} disabled={!sobraaDominio.trim() || sobraaAdicionando}
                    style={{ padding: '8px 16px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {sobraaAdicionando ? '⏳...' : '➕ Adicionar'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: cores.muted, marginTop: 4 }}>
                  Ex: seudominio.com.br — apenas o domínio, sem www ou @
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ flex: 1, background: '#f8fafc', border: `1px solid ${cores.border}`, borderRadius: 8, padding: '8px 12px', fontWeight: 600, fontSize: 14, color: '#1a2233' }}>
                    🌐 {sobraaStatus.dominio}
                  </div>
                  <button onClick={removerDominio}
                    style={{ padding: '8px 12px', background: '#fff', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    🗑️ Remover
                  </button>
                </div>

                {/* Registros DNS */}
                {sobraaStatus.status !== 'ativo' && sobraaStatus.registros_dns && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2233', marginBottom: 8 }}>
                      📋 Adicione estes registros no DNS do seu domínio:
                    </div>
                    <div style={{ background: '#1e293b', borderRadius: 8, padding: 12, fontSize: 11, fontFamily: 'monospace', color: '#e2e8f0', overflowX: 'auto' }}>
                      {(Array.isArray(sobraaStatus.registros_dns) ? sobraaStatus.registros_dns : []).map((r, i) => (
                        <div key={i} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: i < sobraaStatus.registros_dns.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                          <span style={{ color: '#94a3b8' }}>Tipo: </span><span style={{ color: '#7dd3fc' }}>{r.type || r.record_type}</span>
                          {'  '}
                          <span style={{ color: '#94a3b8' }}>Nome: </span><span style={{ color: '#86efac' }}>{r.name}</span>
                          <br/>
                          <span style={{ color: '#94a3b8' }}>Valor: </span><span style={{ color: '#fde68a', wordBreak: 'break-all' }}>{r.value}</span>
                          {r.status && <span style={{ marginLeft: 8, color: r.status === 'verified' ? '#4ade80' : '#fb923c' }}>({r.status === 'verified' ? '✓ verificado' : '⏳ pendente'})</span>}
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: cores.muted, marginTop: 8 }}>
                      Após adicionar os registros, a propagação pode levar de 5 minutos a 48 horas.
                    </div>
                  </div>
                )}

                {sobraaStatus.status === 'ativo' && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#15803d' }}>
                    ✅ Domínio verificado e ativo! Emails enviados com seu domínio pelos servidores do Sobraa.
                  </div>
                )}

                <button onClick={verificarDominio} disabled={sobraaVerificando || sobraaStatus?.status === 'ativo'}
                  style={{ padding: '8px 16px', background: '#f0f3f7', color: '#1a2233', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: sobraaStatus?.status === 'ativo' ? 0.5 : 1 }}>
                  {sobraaVerificando ? '⏳ Verificando...' : '🔍 Verificar DNS'}
                </button>
              </div>
            )}

            {/* Remetentes — só exibe quando domínio ativo */}
            {sobraaStatus?.status === 'ativo' && (
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${cores.border}` }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2233', marginBottom: 12 }}>📨 Remetentes por tipo de email</div>

                {[
                  { tipo: 'orcamento', label: '📄 Orçamentos', placeholder: 'mail@mail.com' },
                  { tipo: 'nota',      label: '🧾 Notas e Boletos', placeholder: 'mail@mail.com' },
                  ...(modulosAtivos.includes('tkt') ? [{ tipo: 'ticket', label: '🎫 Tickets', placeholder: 'mail@mail.com' }] : []),
                ].map(({ tipo, label, placeholder }) => (
                  <div key={tipo} style={{ background: '#f8fafc', border: `1px solid ${cores.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: cores.muted, marginBottom: 8 }}>{label}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ ...labelStyle, fontSize: 11 }}>Nome exibido</label>
                        <input
                          value={sobraaRemetentes[tipo]?.nome || ''}
                          onChange={e => setSobraaRemetentes(prev => ({ ...prev, [tipo]: { ...prev[tipo], nome: e.target.value } }))}
                          placeholder={tipo === 'orcamento' ? 'Assunto' : tipo === 'nota' ? 'Assunto' : 'Assunto'}
                          style={{ ...inputStyle, fontSize: 12 }}
                        />
                      </div>
                      <div>
                        <label style={{ ...labelStyle, fontSize: 11 }}>Email remetente</label>
                        <input
                          type="email"
                          value={sobraaRemetentes[tipo]?.email || ''}
                          onChange={e => setSobraaRemetentes(prev => ({ ...prev, [tipo]: { ...prev[tipo], email: e.target.value } }))}
                          placeholder={placeholder}
                          style={{ ...inputStyle, fontSize: 12 }}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button onClick={salvarRemetentes} disabled={sobraaSalvandoRem}
                  style={{ width: '100%', padding: '10px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', marginTop: 4 }}>
                  {sobraaSalvandoRem ? 'Salvando...' : '💾 Salvar remetentes'}
                </button>
              </div>
            )}
          </div>

          </div>}

{/* ── Focus NFe ── */}
{subAbaInteg === 'focusnfe' && (
  <div>
    <div style={{ background: 'white', border: `1px solid ${cores.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, background: '#fff3eb', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🧾</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a2233' }}>NFe — Emissão Fiscal</div>
          <div style={{ fontSize: 12, color: cores.muted }}>Configure os dados para emissão de NF-e e NFS-e</div>
        </div>
        {focusCnpjCadastrado && (
          <span style={{ fontSize: 12, fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '4px 10px', borderRadius: 20 }}>✅ Dados Cadastrados</span>
        )}
      </div>

      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#0369a1' }}>
        ℹ️ O ambiente de emissão (Homologação/Produção) é definido pelo administrador no painel admin.
      </div>

      {/* Dados da empresa */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Nome da Empresa</label>
          <input value={empresaNome} onChange={e => setEmpresaNome(e.target.value)}
            placeholder="Ex: Acme Ltda" style={{ ...inputStyle }} />
        </div>
        <div>
          <label style={labelStyle}>CNPJ</label>
          <input value={empresaCNPJ} onChange={e => setEmpresaCNPJ(e.target.value)}
            placeholder="00.000.000/0001-00" style={{ ...inputStyle }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Telefone / WhatsApp</label>
          <input value={empresaTelefone} onChange={e => setEmpresaTelefone(e.target.value)}
            placeholder="+55 (11) 99999-9999" style={{ ...inputStyle }} />
        </div>
        <div>
          <label style={labelStyle}>CEP</label>
          <div style={{ position: 'relative' }}>
            <input
              value={focusCep}
              onChange={e => buscarCepFocus(e.target.value)}
              placeholder="00000-000"
              maxLength={9}
              style={{ ...inputStyle, paddingRight: focusBuscandoCep ? 32 : undefined }}
            />
            {focusBuscandoCep && (
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14 }}>⏳</span>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Logradouro</label>
        <input value={focusLogradouro} onChange={e => setFocusLogradouro(e.target.value)} placeholder="Rua, Av., etc." style={{ ...inputStyle }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Bairro</label>
          <input value={focusBairro} onChange={e => setFocusBairro(e.target.value)} style={{ ...inputStyle }} />
        </div>
        <div>
          <label style={labelStyle}>Número</label>
          <input value={focusNumeroEndereco} onChange={e => setFocusNumeroEndereco(e.target.value)} placeholder="S/N" style={{ ...inputStyle }} />
        </div>
        <div>
          <label style={labelStyle}>UF</label>
          <input value={focusUF} onChange={e => setFocusUF(e.target.value.toUpperCase())} maxLength={2} style={{ ...inputStyle, textTransform: 'uppercase' }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Município</label>
          <input value={focusMunicipio} onChange={e => setFocusMunicipio(e.target.value)} placeholder="Nome da cidade" style={{ ...inputStyle }} />
        </div>
        <div>
          <label style={labelStyle}>Código IBGE <span style={{ color: '#D85A30', fontSize: 10 }}>NFS-e</span></label>
          <input
            value={focusCodigoMunicipio}
            onChange={e => setFocusCodigoMunicipio(e.target.value.replace(/\D/g, ''))}
            placeholder="Ex: 3550308"
            maxLength={7}
            style={{ ...inputStyle }}
          />
          {focusMunicipio.toLowerCase().includes('paulo') && !focusCodigoMunicipio && (
            <div
              onClick={() => setFocusCodigoMunicipio('3550308')}
              style={{ fontSize: 11, color: '#0369a1', cursor: 'pointer', marginTop: 3 }}
            >
              💡 São Paulo = 3550308
            </div>
          )}
        </div>
      </div>

      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14,
        padding: 12, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8,
      }}>
        <input
          type="checkbox"
          id="focusNfseNacional"
          checked={focusNfseNacional}
          onChange={e => setFocusNfseNacional(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <label htmlFor="focusNfseNacional" style={{ cursor: 'pointer' }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>
            Município usa o Ambiente Nacional da NFS-e
          </div>
          <div style={{ fontSize: 11, color: cores.muted, marginTop: 2 }}>
            Ative apenas se o município da empresa já aderiu ao Emissor Nacional (verifique tentando emitir — a Focus avisa quando esse é o caso). Isso muda o formato de emissão da NFS-e.
          </div>
        </label>
      </div>

      {focusRegimeTributario === '1' && (
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>
            Alíquota do Simples Nacional (%) <span style={{ color: '#D85A30', fontSize: 10 }}>NFS-e Nacional</span>
          </label>
          <input
            value={focusAliquotaSimples}
            onChange={e => setFocusAliquotaSimples(e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.'))}
            placeholder="Ex: 6.00"
            style={{ ...inputStyle, maxWidth: 200 }}
          />
          <div style={{ fontSize: 11, color: cores.muted, marginTop: 3 }}>
            Obrigatório só para empresas em município no Ambiente Nacional da NFS-e. Consulte a alíquota efetiva no seu extrato do Simples Nacional (PGDAS-D) ou com seu contador.
          </div>
        </div>
      )}

      <hr style={{ border: 'none', borderTop: `1px solid ${cores.border}`, margin: '4px 0 16px' }} />

      {/* Regime tributário */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Regime Tributário</label>
        <select value={focusRegimeTributario} onChange={e => setFocusRegimeTributario(e.target.value)} style={{ ...inputStyle }}>
          <option value="1">1 — Simples Nacional</option>
          <option value="2">2 — Lucro Presumido</option>
          <option value="3">3 — Lucro Real</option>
          <option value="4">4 — MEI</option>
        </select>
      </div>

      {/* Inscrições */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Inscrição Estadual</label>
          <input value={focusInscricaoEstadual} onChange={e => setFocusInscricaoEstadual(e.target.value)} placeholder="Deixe vazio se isento" style={{ ...inputStyle }} />
        </div>
        <div>
          <label style={labelStyle}>Inscrição Municipal</label>
          <input value={focusInscricaoMunicipal} onChange={e => setFocusInscricaoMunicipal(e.target.value)} placeholder="Obrigatório para NFS-e" style={{ ...inputStyle }} />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>CNAE Principal</label>
        <input value={focusCnae} onChange={e => setFocusCnae(e.target.value)} placeholder="Ex: 6201500" style={{ ...inputStyle, maxWidth: 200 }} />
      </div>

      {/* Código de serviço padrão — NFS-e */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>
          Código de Serviço Padrão (LC 116) <span style={{ color: '#D85A30', fontSize: 10 }}>NFS-e</span>
        </label>
        <input
          value={focusCodigoServicoPadrao ?? ''}
          onChange={e => setFocusCodigoServicoPadrao(e.target.value.replace(/[^0-9.]/g, '').slice(0, 20))}
          placeholder="Ex: 02.19"
          style={{ ...inputStyle, maxWidth: 200 }}
        />
        <div style={{ fontSize: 11, color: cores.muted, marginTop: 3 }}>
          Preenchido automaticamente nos serviços ao emitir NFS-e pelo Orçamento (pode ser alterado na hora da emissão). Confirme o código com seu contador ou com a prefeitura.
        </div>
      </div>

      {/* Transporte — CT-e */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>
          RNTRC <span style={{ color: '#D85A30', fontSize: 10 }}>CT-e</span>
        </label>
        <input
          value={focusRntrc}
          onChange={e => setFocusRntrc(e.target.value.replace(/\D/g, '').slice(0, 8))}
          placeholder="Ex: 12345678"
          style={{ ...inputStyle, maxWidth: 200 }}
        />
        <div style={{ fontSize: 11, color: cores.muted, marginTop: 3 }}>
          Registro Nacional de Transportadores Rodoviários de Carga. Obrigatório apenas para emitir CT-e (Financeiro → Notas Fiscais → Conhecimento de Transporte).
        </div>
      </div>

      {/* Certificado */}
      <div style={{ borderTop: `1px solid ${cores.border}`, paddingTop: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>🔐 Certificado Digital A1</div>

        {/* Painel de status do certificado atual */}
        {focusCertCarregando ? (
          <div style={{ fontSize: 12, color: cores.muted, marginBottom: 12 }}>⏳ Verificando certificado...</div>
        ) : focusCertValidoAte ? (() => {
          const ate = new Date(focusCertValidoAte);
          const hoje = new Date();
          const diasRestantes = Math.ceil((ate - hoje) / (1000 * 60 * 60 * 24));
          const vencido = diasRestantes <= 0;
          const alertaProximo = diasRestantes > 0 && diasRestantes <= 30;
          const corStatus = vencido ? '#dc2626' : alertaProximo ? '#d97706' : '#15803d';
          const bgStatus  = vencido ? '#fef2f2' : alertaProximo ? '#fffbeb' : '#f0fdf4';
          const bdStatus  = vencido ? '#fca5a5' : alertaProximo ? '#fcd34d' : '#86efac';
          return (
            <div style={{ background: bgStatus, border: `1px solid ${bdStatus}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: corStatus, marginBottom: 4 }}>
                {vencido ? '❌ Certificado vencido' : alertaProximo ? `⚠️ Vence em ${diasRestantes} dia${diasRestantes === 1 ? '' : 's'}` : `✅ Certificado válido — ${diasRestantes} dias restantes`}
              </div>
              <div style={{ color: '#374151' }}>
                Válido de <strong>{new Date(focusCertValidoDe).toLocaleDateString('pt-BR')}</strong> até <strong>{ate.toLocaleDateString('pt-BR')}</strong>
                {focusCertCnpj && <span style={{ marginLeft: 8, color: cores.muted }}>· CNPJ: {focusCertCnpj}</span>}
              </div>
              {(vencido || alertaProximo) && (
                <div style={{ marginTop: 6, color: corStatus, fontWeight: 600 }}>
                  {vencido ? 'Renove o certificado e envie um novo arquivo .pfx abaixo.' : 'Considere renovar o certificado em breve.'}
                </div>
              )}
            </div>
          );
        })() : (
          <div style={{ fontSize: 12, color: cores.muted, marginBottom: 14 }}>
            Nenhum certificado detectado. Envie o arquivo .pfx abaixo.
          </div>
        )}

        <div style={{ fontSize: 12, color: cores.muted, marginBottom: 14 }}>
          {focusCertValidoAte ? 'Para trocar o certificado, selecione o novo arquivo .pfx abaixo. O anterior será substituído.' : 'Selecione o arquivo .pfx e informe a senha para enviar o certificado.'}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Arquivo .pfx / .p12 {focusCertValidoAte ? '(novo certificado)' : ''}</label>
          <input type="file" accept=".pfx,.p12" onChange={e => setFocusCertArquivo(e.target.files[0])}
            style={{ fontSize: 13, display: 'block', marginTop: 4 }} />
          {focusCertArquivo && (
            <div style={{ fontSize: 12, color: '#15803d', marginTop: 6, fontWeight: 600 }}>
              📎 {focusCertArquivo.name} — será enviado ao salvar
            </div>
          )}
        </div>

        <div style={{ marginBottom: 4 }}>
          <label style={labelStyle}>Senha do Certificado</label>
          <input type="password" value={focusCertSenha} onChange={e => setFocusCertSenha(e.target.value)}
            placeholder="Senha do arquivo .pfx" style={{ ...inputStyle }} />
        </div>
      </div>

      <button onClick={salvarFocus} disabled={focusSalvando} style={{
        width: '100%', padding: '12px', background: cores.orange, color: 'white',
        border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 8
      }}>
        {focusSalvando ? '⏳ Salvando e sincronizando...' : '💾 Salvar e Sincronizar com NFe'}
      </button>
      <div style={{ fontSize: 11, color: cores.muted, textAlign: 'center', marginBottom: 8 }}>
        💡 Salva os dados fiscais e sincroniza com a NFe{focusCertArquivo ? ', incluindo o novo certificado' : ''}.
      </div>
    </div>
  </div>
)}

          {/* ── WhatsApp ── */}
          {subAbaInteg === 'whatsapp' && <div>

          {/* Números conectados */}
          <div style={{ background: 'white', border: `1px solid ${cores.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1a2233' }}>📱 Números conectados</div>
              <button onClick={() => setWppNovaInstanciaModal(true)}
                style={{ padding: '7px 14px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                + Novo número
              </button>
            </div>

            {/* Instâncias */}
            <div style={{ marginBottom: 8 }}>
              {wppInstancias.length === 0 ? (
                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: 8, textAlign: 'center', color: cores.muted, fontSize: 13 }}>
                  Nenhum número conectado ainda
                </div>
              ) : wppInstancias.map(inst => {
                // status vem normalizado do backend: 'conectado' | 'conectando' | 'desconectado'
                const conectado = inst.status === 'conectado';
                const conectando = inst.status === 'conectando';
                const corBolinha = conectado ? '#22c55e' : conectando ? '#f59e0b' : '#ef4444';
                const brilhoBolinha = conectado ? '0 0 0 3px rgba(34,197,94,0.2)' : conectando ? '0 0 0 3px rgba(245,158,11,0.2)' : 'none';
                return (
                  <div key={inst.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: `1px solid ${cores.border}`, borderRadius: 8, marginBottom: 8, background: '#fafbfc' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: corBolinha, boxShadow: brilhoBolinha, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1a2233' }}>{inst.nome}</div>
                      <div style={{ fontSize: 12, color: cores.muted }}>
                        {conectado ? '✅ Conectado' : conectando ? '🔄 Conectando...' : '❌ Desconectado'} · Fila: {inst.fila}
                      </div>
                    </div>
                    {!conectado && (
                      <button onClick={() => abrirQRWpp(inst)}
                        style={{ padding: '6px 12px', background: '#25D366', color: 'white', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        📱 Conectar
                      </button>
                    )}
                    {conectado && (
                      <button onClick={() => desconectarInstanciaWpp(inst)}
                        title="Desconectar para trocar de número"
                        style={{ padding: '6px 12px', background: '#fff8e1', color: '#92400e', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        🔌 Trocar número
                      </button>
                    )}
                    <button onClick={() => removerInstanciaWpp(inst)}
                      title="Remover instância permanentemente"
                      style={{ padding: '6px 10px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
                      🗑️
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Filas */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#1a2233' }}>Filas de atendimento</div>
                <button onClick={() => setWppNovaFilaModal(true)}
                  style={{ padding: '6px 14px', background: '#f0f3f7', color: '#1a2233', border: `1px solid ${cores.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  + Nova fila
                </button>
              </div>
              {wppFilas.length === 0 ? (
                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: 8, textAlign: 'center', color: cores.muted, fontSize: 13 }}>
                  Nenhuma fila criada. Crie filas para organizar o atendimento.
                </div>
              ) : wppFilas.map((fila, idx) => (
                <div key={fila.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: `1px solid ${cores.border}`, borderRadius: 8, marginBottom: 8, background: '#fafbfc' }}>
                  {/* Botões de ordem */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                    <button
                      onClick={() => moverFilaWpp(idx, -1)}
                      disabled={idx === 0}
                      title="Subir"
                      style={{ background: 'none', border: '1px solid #d4dde8', borderRadius: 4, width: 22, height: 22, cursor: idx === 0 ? 'default' : 'pointer', fontSize: 10, color: idx === 0 ? '#ccc' : '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                      ▲
                    </button>
                    <button
                      onClick={() => moverFilaWpp(idx, 1)}
                      disabled={idx === wppFilas.length - 1}
                      title="Descer"
                      style={{ background: 'none', border: '1px solid #d4dde8', borderRadius: 4, width: 22, height: 22, cursor: idx === wppFilas.length - 1 ? 'default' : 'pointer', fontSize: 10, color: idx === wppFilas.length - 1 ? '#ccc' : '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                      ▼
                    </button>
                  </div>
                  {/* Número da posição */}
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#f0f3f7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#6b7a8d', flexShrink: 0 }}>
                    {idx + 1}
                  </div>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: fila.cor || '#2563eb', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1a2233' }}>{fila.nome}</div>
                    <div style={{ fontSize: 12, color: cores.muted }}>
                      {fila.tecnicos_nomes?.length > 0 ? fila.tecnicos_nomes.join(', ') : 'Sem técnicos atribuídos'}
                    </div>
                  </div>
                  <button onClick={() => abrirEditarFila(fila)}
                    style={{ padding: '6px 10px', background: '#f0f3f7', color: '#1a2233', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                    ✏️
                  </button>
                  <button onClick={() => removerFilaWpp(fila.id)}
                    style={{ padding: '6px 10px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Bot de triagem + Mensagens + Expediente */}
          <div style={{ background: 'white', border: `1px solid ${cores.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a2233', marginBottom: 4 }}>🤖 Bot de triagem</div>
            <div style={{ fontSize: 13, color: cores.muted, marginBottom: 16 }}>
              Quando um cliente envia mensagem, o bot responde automaticamente com as filas disponíveis.
            </div>

            {/* Preview do bot */}
            <div style={{ background: '#f8fafc', border: `1px solid ${cores.border}`, borderRadius: 8, padding: 14, marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: '#1a2233', fontWeight: 600, marginBottom: 6 }}>Prévia da mensagem enviada pelo bot:</div>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151', lineHeight: 1.8 }}>
                {wppConfig.wpp_boas_vindas || 'Olá! 👋 Como posso ajudar?'}<br/>
                {wppFilas.map((f, i) => <span key={f.id}>{i + 1} - {f.nome}<br/></span>)}
                {wppFilas.length === 0 && <span style={{ color: cores.muted }}>Crie filas acima para o bot exibi-las aqui</span>}
              </div>
            </div>

            {/* Mensagem de boas-vindas */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Mensagem de boas-vindas (início do atendimento)</label>
              <textarea value={wppConfig.wpp_boas_vindas}
                onChange={e => setWppConfig(p => ({ ...p, wpp_boas_vindas: e.target.value }))}
                rows={3} placeholder="Ex: Olá! 👋 Seja bem-vindo. Como posso ajudar?"
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
              <div style={{ fontSize: 11, color: cores.muted, marginTop: 4 }}>O menu de filas é adicionado automaticamente logo abaixo desta mensagem.</div>
            </div>

            {/* Mensagem após escolha da fila */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Mensagem após o cliente escolher a fila</label>
              <textarea value={wppConfig.wpp_msg_fila_selecionada}
                onChange={e => setWppConfig(p => ({ ...p, wpp_msg_fila_selecionada: e.target.value }))}
                rows={2} placeholder="✅ Você foi direcionado para a fila *{fila}*. Em breve um atendente irá te ajudar!"
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
              <div style={{ fontSize: 11, color: cores.muted, marginTop: 4 }}>
                Use <code style={{ background: '#f0f0f0', borderRadius: 3, padding: '1px 4px' }}>{'{fila}'}</code> para o nome da fila e <code style={{ background: '#f0f0f0', borderRadius: 3, padding: '1px 4px' }}>{'{nome}'}</code> para o nome do cliente.
              </div>
            </div>

            {/* Mensagem de encerramento */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Mensagem de encerramento do chat</label>
              <textarea value={wppConfig.wpp_msg_encerramento}
                onChange={e => setWppConfig(p => ({ ...p, wpp_msg_encerramento: e.target.value }))}
                rows={2} placeholder="Ex: Obrigado pelo contato! Qualquer dúvida, pode nos chamar. 😊"
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
              <div style={{ fontSize: 11, color: cores.muted, marginTop: 4 }}>Enviada ao cliente quando o atendente encerra o chat. Deixe em branco para não enviar.</div>
            </div>

            {/* Abrir chamado direto pelo bot (só aparece com o módulo Tickets ativo) */}
            {modulosAtivos.includes('tkt') && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1a2233' }}>📋 Abrir chamado pelo bot</div>
                    <div style={{ fontSize: 12, color: cores.muted, marginTop: 2 }}>
                      Adiciona uma opção no menu para o cliente abrir um chamado direto pelo WhatsApp, sem precisar de atendente.
                    </div>
                  </div>
                  <button
                    onClick={() => setWppConfig(p => ({ ...p, wpp_abrir_chamado_ativo: p.wpp_abrir_chamado_ativo === 'true' ? 'false' : 'true' }))}
                    style={{
                      width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                      position: 'relative', padding: 0, flexShrink: 0,
                      background: wppConfig.wpp_abrir_chamado_ativo === 'true' ? cores.orange : cores.border,
                      transition: 'background 0.2s',
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 3, left: wppConfig.wpp_abrir_chamado_ativo === 'true' ? 24 : 3,
                      width: 20, height: 20, borderRadius: '50%', background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                    }} />
                  </button>
                </div>
                {wppConfig.wpp_abrir_chamado_ativo === 'true' && wppMesasCount === 0 && (
                  <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#9a3412' }}>
                    ⚠️ Nenhuma mesa de trabalho cadastrada ainda. Cadastre ao menos uma em Configurações → Tickets → Mesas de Trabalho para essa opção aparecer no menu do bot.
                  </div>
                )}
                {wppConfig.wpp_abrir_chamado_ativo === 'true' && wppMesasCount > 0 && (
                  <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#0369a1' }}>
                    O cliente vai escolher entre {wppMesasCount} mesa{wppMesasCount > 1 ? 's' : ''} de trabalho cadastrada{wppMesasCount > 1 ? 's' : ''} para direcionar o chamado.
                  </div>
                )}
              </div>
            )}

            {/* Separador */}
            <div style={{ borderTop: `1px solid ${cores.border}`, margin: '20px 0' }} />

            {/* Horário de expediente */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1a2233' }}>🕐 Horário de Expediente</div>
                <div style={{ fontSize: 12, color: cores.muted, marginTop: 2 }}>Mensagens fora do horário recebem resposta automática.</div>
              </div>
              <button
                onClick={() => setWppConfig(p => ({ ...p, wpp_expediente_ativo: p.wpp_expediente_ativo === 'true' ? 'false' : 'true' }))}
                style={{
                  width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                  position: 'relative', padding: 0, flexShrink: 0,
                  background: wppConfig.wpp_expediente_ativo === 'true' ? cores.orange : cores.border,
                  transition: 'background 0.2s',
                }}
              >
                <div style={{
                  position: 'absolute', top: 3, left: wppConfig.wpp_expediente_ativo === 'true' ? 24 : 3,
                  width: 20, height: 20, borderRadius: '50%', background: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                }} />
              </button>
            </div>

            {wppConfig.wpp_expediente_ativo === 'true' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Horários */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Início</label>
                    <input type="time" value={wppConfig.wpp_expediente_inicio}
                      onChange={e => setWppConfig(p => ({ ...p, wpp_expediente_inicio: e.target.value }))}
                      style={{ ...inputStyle }} />
                  </div>
                  <div>
                    <label style={labelStyle}>Fim</label>
                    <input type="time" value={wppConfig.wpp_expediente_fim}
                      onChange={e => setWppConfig(p => ({ ...p, wpp_expediente_fim: e.target.value }))}
                      style={{ ...inputStyle }} />
                  </div>
                </div>

                {/* Dias da semana */}
                <div>
                  <label style={labelStyle}>Dias de funcionamento</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {WPP_CONFIG_DIAS_SEMANA.map(d => (
                      <button key={d.v} onClick={() => wppToggleDia(d.v)} type="button"
                        style={{
                          padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                          background: wppDiasAtivos.includes(d.v) ? cores.orange : '#f0f3f7',
                          color: wppDiasAtivos.includes(d.v) ? 'white' : cores.muted,
                        }}>
                        {d.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mensagem fora do expediente */}
                <div>
                  <label style={labelStyle}>Mensagem fora do expediente</label>
                  <textarea value={wppConfig.wpp_msg_fora_expediente}
                    onChange={e => setWppConfig(p => ({ ...p, wpp_msg_fora_expediente: e.target.value }))}
                    rows={3}
                    placeholder={`Ex: ⏰ Nosso atendimento é de seg a sex das ${wppConfig.wpp_expediente_inicio} às ${wppConfig.wpp_expediente_fim}. Retornaremos em breve!`}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
                  <div style={{ fontSize: 11, color: cores.muted, marginTop: 4 }}>Se vazia, uma mensagem padrão com o horário configurado é enviada.</div>
                </div>

                {/* Preview horário */}
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#0369a1' }}>
                  📋 {WPP_CONFIG_DIAS_SEMANA.filter(d => wppDiasAtivos.includes(d.v)).map(d => d.l).join(', ')} · {wppConfig.wpp_expediente_inicio} às {wppConfig.wpp_expediente_fim}
                </div>
              </div>
            )}

            <div style={{ marginTop: 20 }}>
              <button onClick={salvarWppConfig} disabled={wppConfigSalvando}
                style={{ ...btnPrimary, opacity: wppConfigSalvando ? 0.7 : 1 }}>
                {wppConfigSalvando ? 'Salvando...' : '💾 Salvar configurações do bot'}
              </button>
            </div>
          </div>

          </div>}
        </div>
      )}

      {/* Modal QR Code WhatsApp */}
      {wppQRModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, textAlign: 'center', maxWidth: 380, width: '90vw' }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4, color: '#1a2233' }}>Conectar WhatsApp</div>
            <div style={{ fontSize: 13, color: cores.muted, marginBottom: 20 }}>{wppQRModal.nome}</div>
            {wppQRBase64 ? (
              <>
                <img src={wppQRBase64} alt="QR Code" style={{ width: 260, height: 260, borderRadius: 8, border: `2px solid ${cores.border}` }} />
                <div style={{ marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#15803d', textAlign: 'left' }}>
                  <strong>Como escanear:</strong><br />
                  1. Abra o WhatsApp no celular<br />
                  2. Toque em Menu (⋮) → Aparelhos conectados<br />
                  3. Toque em "Conectar um aparelho"<br />
                  4. Aponte a câmera para o QR Code acima
                </div>
                <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 8 }}>⏱ O QR Code expira em ~60s e é renovado automaticamente</div>
              </>
            ) : (
              <div style={{ width: 260, height: 260, background: '#f8fafc', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', margin: '0 auto', color: cores.muted, fontSize: 13, gap: 10 }}>
                <div style={{ fontSize: 28 }}>⏳</div>
                Gerando QR Code...
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'center' }}>
              <button onClick={() => abrirQRWpp(wppQRModal)}
                style={{ padding: '8px 16px', border: `1px solid ${cores.border}`, borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}>🔄 Novo QR</button>
              <button onClick={fecharQRWpp}
                style={{ padding: '8px 16px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nova Instância */}
      {wppNovaInstanciaModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: 400, maxWidth: '90vw' }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 20, color: '#1a2233' }}>Novo número WhatsApp</div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Nome do número</label>
              <input value={wppNovaInstancia.nome} onChange={e => setWppNovaInstancia(p => ({ ...p, nome: e.target.value }))}
                placeholder="Ex: Atendimento Principal" style={{ ...inputStyle }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Instance ID (Z-API)</label>
              <input value={wppNovaInstancia.zapi_instance_id} onChange={e => setWppNovaInstancia(p => ({ ...p, zapi_instance_id: e.target.value }))}
                placeholder="" style={{ ...inputStyle }} />
              <div style={{ fontSize: 11, color: cores.muted, marginTop: 3 }}>Encontre em z-api.io → sua instância → "ID da instância"</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Token (Z-API)</label>
              <input value={wppNovaInstancia.zapi_token} onChange={e => setWppNovaInstancia(p => ({ ...p, zapi_token: e.target.value }))}
                placeholder="" style={{ ...inputStyle }} />
              <div style={{ fontSize: 11, color: cores.muted, marginTop: 3 }}>Encontre em z-api.io → sua instância → "Token da instância"</div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Security Token (Z-API)</label>
              <input value={wppNovaInstancia.zapi_client_token} onChange={e => setWppNovaInstancia(p => ({ ...p, zapi_client_token: e.target.value }))}
                placeholder="" style={{ ...inputStyle }} />
              <div style={{ fontSize: 11, color: cores.muted, marginTop: 3 }}>Encontre em z-api.io → seu perfil (canto superior direito) → "Security Token"</div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setWppNovaInstanciaModal(false)}
                style={{ padding: '8px 18px', border: `1px solid ${cores.border}`, borderRadius: 8, background: 'white', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={criarInstanciaWpp} disabled={wppLoading}
                style={{ padding: '8px 18px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: wppLoading ? 0.7 : 1 }}>
                {wppLoading ? 'Criando...' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nova Fila */}
      {wppNovaFilaModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: 460, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 20, color: '#1a2233' }}>Nova fila de atendimento</div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Nome da fila</label>
              <input value={wppNovaFila.nome} onChange={e => setWppNovaFila(p => ({ ...p, nome: e.target.value }))}
                placeholder="Ex: Suporte Técnico" style={{ ...inputStyle }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Cor da fila</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['#2563eb','#16a34a','#d97706','#dc2626','#7c3aed','#0891b2','#db2777'].map(cor => (
                  <div key={cor} onClick={() => setWppNovaFila(p => ({ ...p, cor }))}
                    style={{ width: 28, height: 28, borderRadius: '50%', background: cor, cursor: 'pointer', border: wppNovaFila.cor === cor ? '3px solid #1a2233' : '3px solid transparent' }} />
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Técnicos desta fila</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto', padding: 4 }}>
                {usuarios.filter(u => u.ativo !== false).map(u => (
                  <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 10px', borderRadius: 8, border: `1px solid ${wppNovaFila.tecnicos.includes(u.id) ? cores.orange : cores.border}`, background: wppNovaFila.tecnicos.includes(u.id) ? '#fff8f0' : 'white' }}>
                    <input type="checkbox" checked={wppNovaFila.tecnicos.includes(u.id)} onChange={() => toggleTecnicoFila(u.id)} style={{ display: 'none' }} />
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: u.cor || cores.orange, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{u.iniciais || u.nome?.slice(0,2).toUpperCase()}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1a2233' }}>{u.nome}</div>
                      <div style={{ fontSize: 12, color: cores.muted }}>{u.email}</div>
                    </div>
                    {wppNovaFila.tecnicos.includes(u.id) && <span style={{ marginLeft: 'auto', color: cores.orange, fontSize: 16 }}>✓</span>}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setWppNovaFilaModal(false); setWppNovaFila({ nome: '', chave: '', cor: '#2563eb', tecnicos: [] }); }}
                style={{ padding: '8px 18px', border: `1px solid ${cores.border}`, borderRadius: 8, background: 'white', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={criarFilaWpp} disabled={wppLoading}
                style={{ padding: '8px 18px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: wppLoading ? 0.7 : 1 }}>
                {wppLoading ? 'Criando...' : 'Criar Fila'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Fila */}
      {wppEditFilaModal && wppEditFila && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: 460, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 20, color: '#1a2233' }}>✏️ Editar fila</div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Nome da fila</label>
              <input value={wppEditFila.nome}
                onChange={e => setWppEditFila(p => ({ ...p, nome: e.target.value }))}
                placeholder="Ex: Suporte Técnico" style={{ ...inputStyle }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Cor da fila</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['#2563eb','#16a34a','#d97706','#dc2626','#7c3aed','#0891b2','#db2777'].map(cor => (
                  <div key={cor} onClick={() => setWppEditFila(p => ({ ...p, cor }))}
                    style={{ width: 28, height: 28, borderRadius: '50%', background: cor, cursor: 'pointer',
                      border: wppEditFila.cor === cor ? '3px solid #1a2233' : '3px solid transparent' }} />
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Técnicos desta fila</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto', padding: 4 }}>
                {usuarios.filter(u => u.ativo !== false).map(u => (
                  <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 10px', borderRadius: 8,
                    border: `1px solid ${wppEditFila.tecnicos.includes(u.id) ? cores.orange : cores.border}`,
                    background: wppEditFila.tecnicos.includes(u.id) ? '#fff8f0' : 'white' }}>
                    <input type="checkbox" checked={wppEditFila.tecnicos.includes(u.id)}
                      onChange={() => toggleTecnicoEditFila(u.id)} style={{ display: 'none' }} />
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: u.cor || cores.orange, color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                      {u.iniciais || u.nome?.slice(0,2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1a2233' }}>{u.nome}</div>
                      <div style={{ fontSize: 12, color: cores.muted }}>{u.email}</div>
                    </div>
                    {wppEditFila.tecnicos.includes(u.id) && <span style={{ marginLeft: 'auto', color: cores.orange, fontSize: 16 }}>✓</span>}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setWppEditFilaModal(false); setWppEditFila(null); }}
                style={{ padding: '8px 18px', border: `1px solid ${cores.border}`, borderRadius: 8, background: 'white', cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={salvarEditarFila} disabled={wppLoading}
                style={{ padding: '8px 18px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: wppLoading ? 0.7 : 1 }}>
                {wppLoading ? 'Salvando...' : '💾 Salvar alterações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sub-aba Tickets ── */}
      {subAbaConfig === 'tickets' && (
        <AbaTicketConfig toast={toast} usuarios={usuarios} />
      )}

      {/* ── Sub-aba Relatório ── */}
      {subAbaConfig === 'relatorio' && (
        <AbaRelatorioConfig toast={toast} />
      )}

      {/* ── Sub-aba Cofre de Senhas ── */}
      {subAbaConfig === 'cofre' && (
        <ConfiguracaoCofre toast={toast} />
      )}


      {/* ── Sub-aba Financeiro ── */}
      {subAbaConfig === 'financeiro' && (
        <AbaFinanceiroConfig toast={toast} />
      )}

      {/* ── Sub-aba Backup ── */}
      {subAbaConfig === 'backup' && (
        <AbaBackup toast={toast} />
      )}

        </div>{/* fim conteúdo */}
      </div>{/* fim flex */}
    </div>
  );
}